import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Box, Typography, Paper, Button, Stack, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Menu as MuiMenu, MenuItem as MuiMenuItem, Alert, Backdrop, CircularProgress, Popover, Fade, TextField, FormControlLabel, Checkbox, RadioGroup, Radio, ListItemIcon, ListItemText, Divider } from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CloseIcon from '@mui/icons-material/Close'
import { useDispatch } from 'react-redux'
import { showResultDialog, showSnackbar } from '../store/slices/uiSlice'
import GoldenArrow from '../components/common/GoldenArrow'
import { lineWithProjectDefaults, linesWithHeaderCostCenter, linesWithHeaderProject, withItemAuxiliary } from '../utils/prProjectMapping'
import { draftAutosavePayload, newPrDefaultsAllowed, prHeaderKey, waitingAutosaveAction, WaitingAutosave } from '../utils/prReopen'
import { prLineValues } from '../utils/prLineValues'
import { fixTwoDigitYear, isDocumentDate } from '../utils/documentDate'
import PRGlobalPicker from '../components/common/PRGlobalPicker'
import CopyPRDialog from '../components/common/CopyPRDialog'
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import apiService from '../services/api.service';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import TableChartIcon from '@mui/icons-material/TableChart';
import { useLocation, useNavigate } from 'react-router-dom';
import { transformPRForCopy } from '../utils/prCopyTransformer';
import {
  ImportedLine, displayDate, fieldForHeader, hasAmbiguousDates, inferDateOrder, isHeaderRow, localeDateOrder, parseDate,
} from '../utils/prLineImport';
import PRLineImportDialog, { ImportMode } from '../components/pr/PRLineImportDialog';
import { allowedCodes } from '../utils/userAccess';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  EDITOR_COLORS,
  EditorField,
  EditorLayout,
  EditorSection,
  SideCard,
  SideFact,
  StatusPill,
  SubmitChecklist,
  addButtonSx,
  chipSx,
  editorControlStyle,
  secondaryButtonSx,
} from '../components/documentEditor/DocumentEditor';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);
 
 
type PRCompanyRules = { requireLineProject: boolean; serviceUnitPriceMustBeOne: boolean }
const NO_COMPANY_RULES: PRCompanyRules = { requireLineProject: false, serviceUnitPriceMustBeOne: false }
// Mirrors backend serviceUnitPriceRule.service: a line is Service by the PR type, its own item type,
// or an item code starting with 62 (service items).
const isServiceLine = (requisitionType: string, line: { itemtype?: string; itemcode?: string }) =>
  requisitionType === 'Service' || line.itemtype === 'Service' || String(line.itemcode || '').trim().startsWith('62')
const isFilledLine = (line: { itemcode?: string; itemdescription?: string }) => !!line.itemcode || !!line.itemdescription
// The requisition types as cards; the values are the ones the PR stores.
const PR_TYPE_CHOICES: Array<{ value: 'Inventory' | 'Service' | 'FixedAsset'; label: string; hint: string }> = [
  { value: 'Inventory', label: 'Material', hint: 'Items received into a warehouse' },
  { value: 'Service', label: 'Service', hint: 'Work or services, nothing goes into stock' },
  { value: 'FixedAsset', label: 'Asset', hint: 'Equipment you capitalise as a fixed asset' },
]
// A stage of the approval route preview (raw ApprovalWorkflows row from GET /pr/approval-route).
const stageTitle = (stage: any, index: number): string =>
  String(stage?.stagename || stage?.StageName || stage?.approverrole || stage?.ApproverRole || `Stage ${index + 1}`)
const stageWho = (stage: any): string => {
  const fixed = String(stage?.fixedapprovername || stage?.FixedApproverName || '').trim()
  if (fixed) return fixed
  // Role codes read as words: ProjectManager -> Project Manager.
  const role = String(stage?.approverrole || stage?.ApproverRole || '').trim().replace(/([a-z])([A-Z])/g, '$1 $2')
  const name = String(stage?.stagename || stage?.StageName || '').trim()
  return role && role.toLowerCase() !== name.toLowerCase() ? role : ''
}
type Line ={ itemtype: 'Inventory' | 'Service' | 'FixedAsset'; itemcode?: string; itemdescription?: string; u_item_details?: string; quantity: number; uomcode?: string; unitprice: number; accountcode: string; vendorcode?: string; warehouse?: string; projectcode?: string; dim1?: string; dim2?: string; dim3?: string; dim4?: string; dim5?: string; deliverydate?: string; lineremarks?: string; u_ser_qty?: number | null; u_ser_unit_price?: number | null }

const toDDMMYYYY = (v: any): string => {
  const s = String(v || '').trim();
  if (!s) return '';
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[1]}-${slash[2]}-${slash[3]}`;
  const compact = s.match(/^(\d{8})$/);
  if (compact) return `${s.slice(6,8)}-${s.slice(4,6)}-${s.slice(0,4)}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
};

const CreatePR: React.FC = () => {
  const { user } = useSelector((s: RootState) => s.auth)
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  
  
  
  const [createdPrId, setCreatedPrId] = useState<number | null>(null)
  const [prNumber, setPrNumber] = useState<string>('')
  const [prDate, setPrDate] = useState<string>('')
  const [attachments, setAttachments] = useState<any[]>([])
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [rowProgress, setRowProgress] = useState<number[]>([])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('pr_grid_view')
      return saved ? JSON.parse(saved) : {
        itemcode: true,
        itemdescription: true,
        u_item_details: true,
        quantity: true,
        uomcode: true,
        unitprice: true,
        linetotal: true,
        vendorcode: true,
        warehouse: true,
        deliverydate: true,
        projectcode: true,
        dim1: true,
        dim2: true,
        dim3: true,
        dim4: true,
        dim5: true,
      }
    } catch {
      return {
        itemcode: true,
        itemdescription: true,
        u_item_details: true,
        quantity: true,
        uomcode: true,
        unitprice: true,
        linetotal: true,
        vendorcode: true,
        warehouse: true,
        deliverydate: true,
        projectcode: true,
        dim1: true,
        dim2: true,
        dim3: true,
        dim4: true,
        dim5: true,
      }
    }
  })
  const [itemSearchOpen, setItemSearchOpen] = useState(false)
  const [itemSearchQuery, setItemSearchQuery] = useState('')
  const [itemSearchResults, setItemSearchResults] = useState<any[]>([])
  const [itemSearchSelectedIndex, setItemSearchSelectedIndex] = useState<number | null>(null)
  const [itemSearchLoading, setItemSearchLoading] = useState(false)
  const [applyToRowId, setApplyToRowId] = useState<number | null>(null)
  const [inlinePickerOpen, setInlinePickerOpen] = useState(false)
  const [inlinePickerType, setInlinePickerType] = useState<'item'|'warehouse'|'vendor'|'project'|'dim1'|'dim2'|'dim3'|'dim4'|'dim5'>('warehouse')
  
  const [inlineQuery, setInlineQuery] = useState('')
  const [inlineRows, setInlineRows] = useState<any[]>([])
  const [inlineSelection, setInlineSelection] = useState<any[]>([])
  const [inlineLoading, setInlineLoading] = useState(false)
  const inlineDebounceRef = useRef<any>(null)
  const inlineRowsSigRef = useRef<string>('')
  const inlineSelectionSigRef = useRef<string>('')
  const inlineOpenedOnceRef = useRef<boolean>(false)
  const isAdminUser = String(user?.role || '').toLowerCase() === 'admin'
  const inlineColumns = useMemo(() => {
    if (inlinePickerType === 'warehouse') return [
      { field: 'WarehouseCode', headerName: 'Code', width: 160 },
      { field: 'WarehouseName', headerName: 'Name', width: 320 },
    ]
    if (inlinePickerType === 'item') return [
      { field: 'ItemCode', headerName: 'Item Code', width: 180 },
      { field: 'ItemName', headerName: 'Item Name', width: 340 },
      { field: 'PurchaseUnit', headerName: 'UoM', width: 120 },
    ]
    if (inlinePickerType === 'vendor') return [
      { field: 'CardCode', headerName: 'Vendor Code', width: 180 },
      { field: 'CardName', headerName: 'Vendor Name', width: 340 },
    ]
    if (inlinePickerType === 'project') return [
      { field: 'Code', headerName: 'Project Code', width: 200 },
      { field: 'Name', headerName: 'Project Name', width: 340 },
    ]
    if (inlinePickerType === 'dim1') return [
      { field: 'FactorCode', headerName: 'Division Code', width: 200 },
      { field: 'FactorDescription', headerName: 'Division Name', width: 340 },
    ]
    if (inlinePickerType === 'dim2') return [
      { field: 'FactorCode', headerName: 'Department Code', width: 200 },
      { field: 'FactorDescription', headerName: 'Department Name', width: 340 },
    ]
    if (inlinePickerType === 'dim3') return [
      { field: 'FactorCode', headerName: 'Cost Code (WBS)', width: 220 },
      { field: 'FactorDescription', headerName: 'WBS Name', width: 340 },
    ]
    if (inlinePickerType === 'dim4') return [
      { field: 'FactorCode', headerName: 'Auxiliary Code', width: 200 },
      { field: 'FactorDescription', headerName: 'Auxiliary Name', width: 340 },
    ]
    return [
      { field: 'FactorCode', headerName: 'LOC Code', width: 200 },
      { field: 'FactorDescription', headerName: 'LOC Name', width: 340 },
    ]
  }, [inlinePickerType])
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; rowId: number | null } | null>(null)
  const [clipboardRow, setClipboardRow] = useState<Line | null>(null)
  const [clipboardMode, setClipboardMode] = useState<'copy' | 'cut' | null>(null)
  const requesterName = user?.fullname || ''
  const [tab, setTab] = useState<'contents'|'attachments'>('contents')
  const [requisitionType, setRequisitionType] = useState<'Inventory'|'Service'|'FixedAsset'>('Inventory')
  const [allocationType, setAllocationType] = useState<'Project'|'CostCenter'>('Project')
  const [allocationCode, setAllocationCode] = useState<string>('')
  // The header project's Division / Department / Auxiliary / Location / Warehouse, filled into
  // every row: the rows there when the project was chosen and every row added afterwards.
  const [headerLineDefaults, setHeaderLineDefaults] = useState<Partial<Line>>({})
  const newLineAllocation = useMemo((): Partial<Line> => (
    allocationType === 'Project' && allocationCode
      ? { projectcode: allocationCode, ...headerLineDefaults }
      : { dim2: allocationType === 'CostCenter' && allocationCode ? allocationCode : undefined }
  ), [allocationType, allocationCode, headerLineDefaults])
  const [facilityType, setFacilityType] = useState<string>('NA')
  const [cycle, setCycle] = useState<string>('Project')
  const [requiredDate, setRequiredDate] = useState<string>('')
  const [remarks, setRemarks] = useState<string>('')
  const [lines, setLines] = useState<Line[]>([])
  // The lines as last rendered, for callbacks that need a line's item outside a state update.
  const linesRef = useRef<Line[]>(lines)
  linesRef.current = lines
  // Each item's own Auxiliary (SAP item field U_AUX_Code, Dimension 5), asked of the server once per item
  // and company; the server reads SAP when its synced item lacks the field. A line takes it when its item
  // is chosen, and it beats a project's auxiliary (withItemAuxiliary).
  const itemAuxCache = useRef(new Map<string, string | null>())
  const itemAuxPending = useRef(new Map<string, Promise<void>>())
  const loadItemAuxiliaries = React.useCallback(async (codes: string[]): Promise<Map<string, string | null>> => {
    const company = localStorage.getItem('selectedCompanyId') || ''
    const key = (code: string) => `${company}|${code}`
    const wanted = Array.from(new Set(codes.map((c) => String(c || '').trim()).filter(Boolean)))
    const missing = wanted.filter((c) => !itemAuxCache.current.has(key(c)) && !itemAuxPending.current.has(key(c)))
    for (let i = 0; i < missing.length; i += 200) {
      const group = missing.slice(i, i + 200)
      const request = apiService.getItemAuxiliaries(group, Number(company) || undefined)
        .then((found) => { for (const c of group) if (c in found) itemAuxCache.current.set(key(c), found[c] ?? null) })
        .catch((error) => console.error('Could not read the items\' auxiliaries', error))
        .finally(() => { for (const c of group) itemAuxPending.current.delete(key(c)) })
      for (const c of group) itemAuxPending.current.set(key(c), request)
    }
    await Promise.all(wanted.map((c) => itemAuxPending.current.get(key(c))).filter(Boolean))
    return new Map(wanted.map((c) => [c, itemAuxCache.current.get(key(c)) ?? null]))
  }, [])
  // The Auxiliary of an item already asked about; null = none, or not known yet.
  const knownItemAuxiliary = React.useCallback((code: string): string | null => (
    itemAuxCache.current.get(`${localStorage.getItem('selectedCompanyId') || ''}|${String(code || '').trim()}`) ?? null
  ), [])
  // Chosen items' Auxiliaries onto their lines, unless a line holds another item by then.
  const fillItemAuxiliaries = React.useCallback(async (rows: Array<{ rowIdx: number; itemCode: string }>) => {
    const wanted = rows.map((r) => ({ rowIdx: r.rowIdx, code: String(r.itemCode || '').trim() })).filter((r) => r.code)
    if (!wanted.length) return
    const found = await loadItemAuxiliaries(wanted.map((r) => r.code))
    const byRow = new Map(wanted.filter((r) => found.get(r.code)).map((r) => [r.rowIdx, r.code]))
    if (!byRow.size) return
    setLines((ls) => ls.map((l, i) => {
      const code = byRow.get(i)
      return code && String(l.itemcode || '').trim() === code ? { ...l, dim4: found.get(code) as string } : l
    }))
  }, [loadItemAuxiliaries])
  // After a project replaced the lines' auxiliaries: each line whose item has its own takes it again.
  const refillItemAuxiliaries = React.useCallback(async () => {
    const codes = linesRef.current.map((l) => String(l.itemcode || '').trim()).filter(Boolean)
    if (!codes.length) return
    const found = await loadItemAuxiliaries(codes)
    setLines((ls) => ls.map((l) => withItemAuxiliary(l, (c) => found.get(c))))
  }, [loadItemAuxiliaries])
  const [importOpen, setImportOpen] = useState(false)
  const [prStatus, setPrStatus] = useState<string>('Draft')
  const [matchedApprovalFlow, setMatchedApprovalFlow] = useState<string>('')
  // The server's reason when no single approval flow matches (no access, no flow, several flows...).
  const [approvalFlowError, setApprovalFlowError] = useState<string>('')
  // The stages of the matched flow, for the "Who approves" card (display only).
  const [approvalStages, setApprovalStages] = useState<any[]>([])
  // When this draft was last saved (Save as Draft or autosave), for the header (display only).
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  // A saved PR opened here (loadPRById) stays as it was saved: the new-PR defaults, the approval flow
  // re-check and the header project's line values react to the user's changes, not to the load, and
  // autosave sends only what changes after it (utils/prReopen).
  const prLoadRequest = useRef(0)
  const prLoading = useRef<Promise<void> | null>(null)
  // The header the shown saved PR was opened with (prHeaderKey); null = a new PR.
  const loadedHeader = useRef<string | null>(null)
  // The allocation of the PR just opened, until the header project effect has seen it.
  const loadedAllocation = useRef<string | null>(null)
  // The last body the server has for this draft, as far as autosave knows.
  const autosaveBaseline = useRef<{ prId: number; body: string } | null>(null)
  // Autosave runs only while the draft is editable ('create'): not read-only, not the Find filters.
  const viewModeRef = useRef<'create' | 'read' | 'find'>('create')
  // A draft change still waiting for its autosave (sent 1.5 s after the last edit). When the page leaves the
  // draft before then (another PR, Find, Close, a menu link) it is sent at once (waitingAutosaveAction).
  const pendingAutosave = useRef<WaitingAutosave | null>(null)
  const createdPrIdRef = useRef<number | null>(null)
  createdPrIdRef.current = createdPrId
  // Sends one draft autosave; the "last saved" time and the baseline follow only the PR still shown.
  const sendAutosave = React.useCallback(async (pending: WaitingAutosave) => {
    try {
      await apiService.updatePRDraft(pending.prId, pending.payload)
      if (createdPrIdRef.current === pending.prId) {
        autosaveBaseline.current = { prId: pending.prId, body: pending.body }
        setLastSavedAt(new Date())
      }
    } catch {}
  }, [])
  // Leaving the page (Close, a menu link) before a change's autosave: it is sent now.
  useEffect(() => () => {
    const waiting = pendingAutosave.current
    pendingAutosave.current = null
    if (waiting) void apiService.updatePRDraft(waiting.prId, waiting.payload).catch(() => {})
  }, [])
  const approvalFlowRequest = useRef(0)
  // Company PR rules (Company Management); the server enforces them again on Submit.
  const [companyRules, setCompanyRules] = useState<PRCompanyRules>(NO_COMPANY_RULES)
  const companyRulesRequest = useRef(0)
  const [availableFlowOptions, setAvailableFlowOptions] = useState<{
    requisitionType: string[];
    allocationType: string[];
    facilityType: string[];
    cycle: string[];
    allocationCode: string[];
  } | null>(null)
  const [allAvailableFlows, setAllAvailableFlows] = useState<any[]>([])
  const [autoFillApplied, setAutoFillApplied] = useState(false)
  const [selectedFlowId, setSelectedFlowId] = useState<string>('')
  const userAssignedRequisitionTypes = useMemo(() => {
    const values = allAvailableFlows
      .map((flow) => String(flow.requisitionType || '').trim())
      .filter(Boolean)
    return Array.from(new Set(values))
  }, [allAvailableFlows])
  const canShowRequisitionType = React.useCallback((type: 'Inventory' | 'Service' | 'FixedAsset') => {
    if (userAssignedRequisitionTypes.length > 0) {
      return userAssignedRequisitionTypes.includes(type)
    }
    const initialTypes = availableFlowOptions?.requisitionType || []
    return initialTypes.length === 0 || initialTypes.includes(type)
  }, [availableFlowOptions?.requisitionType, userAssignedRequisitionTypes])
  const normalizeFlowValue = React.useCallback((value: any) => String(value || '').trim().toUpperCase(), [])
  const allocationCodeMatchesFlow = React.useCallback((flowCode: any, selectedCode: string) => {
    const selectedCodes = String(selectedCode || '')
      .split(',')
      .map((code) => normalizeFlowValue(code))
      .filter(Boolean)
    if (!selectedCodes.length) return true
    const flowCodes = String(flowCode || '')
      .split(',')
      .map((code) => normalizeFlowValue(code))
      .filter(Boolean)
    return flowCodes.length === 0 || selectedCodes.some((code) => flowCodes.includes(code))
  }, [normalizeFlowValue])
  const sortApprovalFlows = React.useCallback((flows: any[]) => {
    const reqOrder: Record<string, number> = { INVENTORY: 1, SERVICE: 2, FIXEDASSET: 3 }
    return [...flows].sort((a, b) => {
      const reqA = reqOrder[normalizeFlowValue(a.requisitionType)] ?? 99
      const reqB = reqOrder[normalizeFlowValue(b.requisitionType)] ?? 99
      if (reqA !== reqB) return reqA - reqB
      const labelA = [
        a.flowName,
        a.requisitionType,
        a.facilityType,
        a.cycle,
        a.allocationCode
      ].map((v) => String(v || '')).join('|')
      const labelB = [
        b.flowName,
        b.requisitionType,
        b.facilityType,
        b.cycle,
        b.allocationCode
      ].map((v) => String(v || '')).join('|')
      return labelA.localeCompare(labelB)
    })
  }, [normalizeFlowValue])
  const buildFlowOptions = React.useCallback((flows: any[]) => ({
    requisitionType: Array.from(new Set(flows.map(f => f.requisitionType).filter(Boolean))),
    allocationType: Array.from(new Set(flows.map(f => f.allocationType).filter(Boolean))),
    facilityType: Array.from(new Set(flows.map(f => f.facilityType).filter((v) => v !== null && v !== undefined))),
    cycle: Array.from(new Set(flows.map(f => f.cycle).filter((v) => v !== null && v !== undefined))),
    allocationCode: Array.from(new Set(flows.map(f => f.allocationCode).filter((v) => v !== null && v !== undefined)))
  }), [])
  const flowMatchesCurrentHeader = React.useCallback((flow: any) => {
    if (requisitionType && flow.requisitionType && normalizeFlowValue(flow.requisitionType) !== normalizeFlowValue(requisitionType)) return false
    if (allocationType && flow.allocationType && normalizeFlowValue(flow.allocationType) !== normalizeFlowValue(allocationType)) return false
    if (facilityType && flow.facilityType && normalizeFlowValue(flow.facilityType) !== normalizeFlowValue(facilityType)) return false
    if (cycle && flow.cycle && normalizeFlowValue(flow.cycle) !== normalizeFlowValue(cycle)) return false
    if (allocationCode && !allocationCodeMatchesFlow(flow.allocationCode, allocationCode)) return false
    return true
  }, [allocationCode, allocationCodeMatchesFlow, allocationType, cycle, facilityType, normalizeFlowValue, requisitionType])
  const applyApprovalFlowToHeader = React.useCallback((flow: any) => {
    if (!flow) return
    if (flow.requisitionType) setRequisitionType(flow.requisitionType)
    if (flow.allocationType) setAllocationType(flow.allocationType)
    if (flow.facilityType) setFacilityType(flow.facilityType)
    if (flow.cycle) setCycle(flow.cycle)
    if (flow.allocationCode) setAllocationCode(String(flow.allocationCode))
    const idx = allAvailableFlows.indexOf(flow)
    setSelectedFlowId(idx >= 0 ? `${idx}` : '')
  }, [allAvailableFlows])
  const [gridMenuAnchor, setGridMenuAnchor] = useState<null | HTMLElement>(null)
  const gridMenuOpen = Boolean(gridMenuAnchor)
  const [columnsOpen, setColumnsOpen] = useState<boolean>(false)
  const [densityOpen, setDensityOpen] = useState<boolean>(false)
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false)
  const [quickFilterText, setQuickFilterText] = useState<string>('')
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const defaults = { itemcode: 100, itemdescription: 200, u_item_details: 240, quantity: 80, uomcode: 60, unitprice: 90, vendorcode: 110, warehouse: 90, deliverydate: 105, projectcode: 100, dim1: 85, dim2: 90, dim3: 90, dim4: 80, dim5: 75 }
    try {
      const rawV2 = localStorage.getItem('pr_grid_widths_v2')
      if (rawV2) {
        const parsed = JSON.parse(rawV2)
        if (parsed && typeof parsed === 'object' && typeof parsed.version === 'number' && parsed.widths) {
          return parsed.widths as Record<string, number>
        }
      }
    } catch {}
    try {
      const savedLegacy = localStorage.getItem('pr_grid_widths')
      return savedLegacy ? JSON.parse(savedLegacy) : defaults
    } catch { return defaults }
  })
  const [headerWidths, setHeaderWidths] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem('pr_header_widths')
      return saved ? JSON.parse(saved) : { requester: 130, type: 100, status: 100, requireddate: 160, allocationtype: 130, allocationcode: 150 }
    } catch {
      return { requester: 130, type: 100, status: 100, requireddate: 160, allocationtype: 130, allocationcode: 150 }
    }
  })
  const [autoResizeColumns] = useState<boolean>(() => {
    try { return localStorage.getItem('pr_grid_auto_resize') === 'true' } catch { return false }
  })
  const [gridDensity, setGridDensity] = useState<'compact'|'standard'|'comfortable'>(() => {
    try { return (localStorage.getItem('pr_grid_density') as any) || 'compact' } catch { return 'compact' }
  })
  const WIDTHS_STORAGE_KEY = 'pr_grid_widths_v2'
  const WIDTHS_STORAGE_VERSION = 2
  const RESIZE_MIN = 30
  const RESIZE_MAX = 500
  const [submitError, setSubmitError] = useState<string>('')
  const [centerPopup, setCenterPopup] = useState<{
    open: boolean
    title: string
    message: string
    severity: 'success' | 'error'
    details: string[]
    nextPath?: string
  }>({ open: false, title: '', message: '', severity: 'success', details: [], nextPath: '' })
  const [resultInfo, setResultInfo] = useState<string>('')
  const [warnInfo, setWarnInfo] = useState<string>('')
  const gridRef = useRef<any>(null) // For AG Grid
  const lastClickedCellRef = useRef<number | null>(null) // Store last clicked cell position
  const uniqueIdCounter = useRef<number>(Date.now()) // For generating unique row IDs
  const [focusedCellPosition, setFocusedCellPosition] = React.useState<{ rowIndex: number; colId?: string } | null>(null)

  const [prList, setPrList] = useState<any[]>([])
  const [filteredPrList, setFilteredPrList] = useState<any[]>([])
  const [currentIndex, setCurrentIndex] = useState<number | null>(null)
  const [viewMode, setViewMode] = useState<'create'|'read'|'find'>('create')
  viewModeRef.current = viewMode
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const [previewType, setPreviewType] = useState<'image'|'pdf'|'other'>('other')
  const [previewAttachmentId, setPreviewAttachmentId] = useState<number | null>(null)
  const [previewAttachmentName, setPreviewAttachmentName] = useState<string>('')
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string>('')
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const contentsRef = useRef<HTMLDivElement | null>(null)
  const attachmentsRef = useRef<HTMLDivElement | null>(null)
  const [headerReady, setHeaderReady] = useState(false)
  useEffect(() => { setHeaderReady(true) }, [])
  const [globalPickerOpen, setGlobalPickerOpen] = useState(false)
  const [triggerFlowEval, setTriggerFlowEval] = useState(0)
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)

  const openCenterPopup = (
    title: string,
    message: string,
    severity: 'success' | 'error',
    details: string[] = [],
    nextPath: string = ''
  ) => {
    setCenterPopup({ open: true, title, message, severity, details, nextPath })
  }


  const openPreview = async (attachmentId: number, name: string) => {
    try {
      const { blob, contentType } = await apiService.fetchAttachmentBlob(attachmentId)
      const objUrl = URL.createObjectURL(blob)
      const ext = String(name || '').toLowerCase()
      const isImage = contentType.startsWith('image/') || ext.endsWith('.png') || ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.gif') || ext.endsWith('.webp')
      const isPdf = contentType === 'application/pdf' || ext.endsWith('.pdf')
      setPreviewType(isImage ? 'image' : isPdf ? 'pdf' : 'other')
      setPreviewUrl(objUrl)
      setPreviewObjectUrl(objUrl)
      setPreviewAttachmentId(attachmentId)
      setPreviewAttachmentName(name)
      const idx = attachments.findIndex((a: any) => Number(a.attachmentid ?? a.AttachmentID) === Number(attachmentId))
      setPreviewIndex(idx >= 0 ? idx : null)
      setPreviewOpen(true)
    } catch {}
  }

  const closePreview = () => {
    setPreviewOpen(false)
    if (previewObjectUrl) {
      try { URL.revokeObjectURL(previewObjectUrl) } catch {}
    }
    setPreviewObjectUrl('')
    setPreviewUrl('')
    setPreviewAttachmentId(null)
    setPreviewAttachmentName('')
    setPreviewIndex(null)
  }

  const goPrevAttachment = async () => {
    if (previewIndex === null) return
    const newIdx = Math.max(0, previewIndex - 1)
    const a = attachments[newIdx]
    if (!a) return
    await openPreview(Number(a.attachmentid ?? a.AttachmentID), String((a.filename ?? a.FileName) || ''))
  }

  const [detailOpen, setDetailOpen] = useState(false)
  const [detail] = useState<any | null>(null)

  const goNextAttachment = async () => {
    if (previewIndex === null) return
    const newIdx = Math.min(attachments.length - 1, previewIndex + 1)
    const a = attachments[newIdx]
    if (!a) return
    await openPreview(Number(a.attachmentid ?? a.AttachmentID), String((a.filename ?? a.FileName) || ''))
  }

  const downloadAttachment = async (attachmentId: number, name: string) => {
    try {
      const { blob } = await apiService.fetchAttachmentBlob(attachmentId)
      const dlUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = dlUrl
      a.download = name || 'attachment'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(dlUrl)
    } catch {}
  }

  const handleCopyPR = async (prId: number) => {
    try {
      const response = await apiService.getPR(prId)
      const prData = (response as any)?.data || response
      const copiedData = transformPRForCopy(prData)

      // A copy is a new PR: the new-PR defaults and the approval flow re-check apply to it as before.
      loadedHeader.current = null
      loadedAllocation.current = null
      setRequisitionType(copiedData.requisitionType)
      setAllocationType(copiedData.allocationType)
      setAllocationCode(copiedData.allocationCode)
      setFacilityType(copiedData.facilityType)
      setCycle(copiedData.cycle)
      setRequiredDate(copiedData.requiredDate)
      setRemarks(copiedData.remarks)
      setLines(copiedData.lines)
      setViewMode('create')
      setPrStatus('Draft')
      setCreatedPrId(null)
      setPrNumber('')
      setPrDate('')
      setCopyDialogOpen(false)

      // Trigger approval flow evaluation after state updates complete
      setTimeout(() => {
        setTriggerFlowEval(prev => prev + 1)
      }, 100)

      dispatch(showResultDialog({ title: 'Success', message: `PR data copied successfully from ${prData.prnumber || 'PR'}`, severity: 'success' }))
    } catch (error: any) {
      console.error('Failed to copy PR:', error)
      dispatch(showResultDialog({ title: 'Error', message: 'Failed to copy PR data. Please try again.', severity: 'error' }))
    }
  }

  useEffect(() => {
    const state = (location as any)?.state
    if (state?.copiedData) {
      // A copy is a new PR (see handleCopyPR).
      loadedHeader.current = null
      loadedAllocation.current = null
      setRequisitionType(state.copiedData.requisitionType)
      setAllocationType(state.copiedData.allocationType)
      setAllocationCode(state.copiedData.allocationCode)
      setFacilityType(state.copiedData.facilityType)
      setCycle(state.copiedData.cycle)
      setRequiredDate(state.copiedData.requiredDate)
      setRemarks(state.copiedData.remarks)
      setLines(state.copiedData.lines)
      setViewMode('create')
      setPrStatus('Draft')
      setCreatedPrId(null)
      setPrNumber('')
      setPrDate('')

      navigate(location.pathname, { replace: true, state: {} })

      // Trigger approval flow evaluation after state updates complete
      setTimeout(() => {
        setTriggerFlowEval(prev => prev + 1)
      }, 100)

      if (state.sourcePRId) {
        dispatch(showResultDialog({ title: 'Success', message: `PR data copied successfully from PR`, severity: 'success' }))
      }
    }
  }, [location, navigate, dispatch])

  useEffect(() => {
    setLines((ls) => ls.length ? ls : Array.from({ length: 20 }, () => ({
      ...newLineAllocation,
      itemtype: requisitionType,
      quantity: 1,
      unitprice: 1,
      accountcode: '',
      vendorcode: '',
    })))
  }, [requisitionType, allocationType, allocationCode, newLineAllocation])

  // Auto-fill project/cost center in ALL rows when allocation code changes. A saved PR just opened
  // keeps its lines' own values and only fills empty ones (loadedAllocation, set by loadPRById).
  useEffect(() => {
    const keepSaved = loadedAllocation.current === `${allocationType}|${allocationCode}`
    loadedAllocation.current = null
    setHeaderLineDefaults({})
    if (allocationType && allocationCode) {
      // If Project allocation, fetch project master data and auto-fill all related fields
      if (allocationType === 'Project') {
        void (async () => {
          try {
            const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined
            console.log('Fetching project master data for header allocation:', allocationCode, 'companyId:', selectedCompanyId)
            const res = await apiService.get<any>('/master-data', {
              companyid: selectedCompanyId,
              endpointkey: 'sap-projects',
              keycode: allocationCode
            })
            console.log('Project master data response:', res)
            const projectData = ((res.data || [])[0] || {}).jsondata || {}
            console.log('Parsed project data:', projectData)

            // Extract default values from project data
            const warehouse = String(projectData.U_WH ?? '').trim()
            const loc = String(projectData.U_LOC ?? '').trim()
            const scope = String(projectData.U_SCOPE ?? '').trim()
            const division = String(projectData.U_Division ?? '').trim()
            const department = String(projectData.U_Department ?? '').trim()

            // Fetch auxiliary (scope) from distribution rules based on project if not in project data
            let auxiliary = scope
            if (!auxiliary) {
              try {
                console.log('Fetching auxiliary filtered by project:', allocationCode, 'companyId:', selectedCompanyId)
                const auxRes = await apiService.get<any>('/master-data/auxiliary-by-project', {
                  projectcode: allocationCode,
                  companyid: selectedCompanyId
                })
                const auxList = (auxRes as any)?.data || auxRes || []
                console.log('Auxiliary distribution rules response (filtered by project):', auxList.length, 'items', auxList)
                // If there's only one option, auto-select it
                if (auxList.length === 1) {
                  const item = auxList[0]
                  // Access data field if it exists (structure: {data: {FactorCode: ...}})
                  const factorData = item.data || item
                  auxiliary = String(factorData.FactorCode || factorData.Code || factorData.DistributionRule || '').trim()
                  console.log('Auto-filled auxiliary from distribution rules:', auxiliary)
                } else if (auxList.length > 1) {
                  console.log('Multiple auxiliary options found (filtered by project), not auto-filling')
                } else {
                  console.log('No auxiliary distribution rules found for this project')
                }
              } catch (error) {
                console.error('Error fetching auxiliary distribution rules:', error)
              }
            }

            console.log('Auto-filling ALL rows from project - Warehouse:', warehouse, 'LOC:', loc, 'Scope:', auxiliary, 'Division:', division, 'Department:', department)

            // Rows added later (Add Line, pickers, paste, import) get the same defaults
            const defaults: Partial<Line> = {}
            if (division) defaults.dim1 = division
            if (department) defaults.dim2 = department
            if (auxiliary) defaults.dim4 = auxiliary
            if (loc) defaults.dim5 = loc
            if (warehouse) defaults.warehouse = warehouse
            setHeaderLineDefaults(defaults)

            // Update ALL rows with project code and project defaults. A line's item keeps its own
            // Auxiliary; a saved PR being opened keeps what it was saved with.
            setLines((ls) => {
              const mapped = linesWithHeaderProject(ls, allocationCode, defaults, keepSaved)
              return keepSaved ? mapped : mapped.map((l) => withItemAuxiliary(l, knownItemAuxiliary))
            })
            if (!keepSaved) void refillItemAuxiliaries()
          } catch (error) {
            console.error('Error fetching/applying project master data:', error)
            // Fallback: just set project code for all rows
            setLines((ls) => keepSaved
              ? linesWithHeaderProject(ls, allocationCode, {}, true)
              : ls.map((l) => ({ ...l, projectcode: allocationCode, dim1: undefined })))
          }
        })()
      } else if (allocationType === 'CostCenter') {
        // Cost center is the header route; each line may still use its own project.
        setLines((ls) => linesWithHeaderCostCenter(ls, allocationCode, keepSaved))
      }
    }
  }, [allocationType, allocationCode, knownItemAuxiliary, refillItemAuxiliaries])

  // Auto-fill delivery date from required date when required date changes: rows with no usable
  // date, and rows still on the previous required date (shown in italics), follow the new one.
  // A 2-digit year (0026-09-28 from a date box) is read as 20yy: SAP cannot make a PO from year 26.
  const lastRequiredDate = useRef('')
  useEffect(() => {
    if (!isDocumentDate(requiredDate)) return
    const previous = lastRequiredDate.current
    lastRequiredDate.current = requiredDate
    setLines((ls) => ls.map((l) => {
      const date = fixTwoDigitYear(l.deliverydate)
      if (!isDocumentDate(date) || date === previous) return { ...l, deliverydate: requiredDate }
      return date === l.deliverydate ? l : { ...l, deliverydate: date }
    }))
  }, [requiredDate])

  useEffect(() => {
    const loadMy = async () => {
      try {
        const res = await apiService.getMyPRs()
        const data = (res as any)?.data || res || []
        setPrList(data)
      } catch {}
    }
    loadMy()
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const key = String(e.key || '').toLowerCase()
      if (e.ctrlKey && key === 'f') {
        e.preventDefault()
        setViewMode('find')
        setFilteredPrList([])
        setCurrentIndex(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const activeList = (viewMode === 'find' ? filteredPrList : prList)
  const canNavigate = activeList.length > 0 && currentIndex !== null

  const loadPRById = React.useCallback(async (prId: number) => {
    // Only the newest load fills the page (Next clicked twice); the new-PR defaults wait for it.
    const request = ++prLoadRequest.current
    let finished: () => void = () => {}
    const loading = new Promise<void>((resolve) => { finished = () => resolve() })
    prLoading.current = loading
    const prevScroll = contentsRef.current ? contentsRef.current.scrollTop : 0
    try {
      // A change of the draft shown, still waiting for its autosave, is sent first: not lost, nor reloaded away.
      const waiting = pendingAutosave.current
      pendingAutosave.current = null
      if (waiting) await sendAutosave(waiting)
      const r = await apiService.getPR(Number(prId))
      if (request !== prLoadRequest.current) return
      const d = (r as any)?.data || r || {}
      const header = {
        requisitionType: String(d.requisitiontype || 'Inventory'),
        allocationType: String(d.allocationtype || 'Project'),
        allocationCode: String(d.allocationcode || ''),
        facilityType: String(d.facilitytype || 'NA'),
        cycle: String(d.cycle || 'Project'),
        requiredDate: String(d.requireddate || ''),
        remarks: String(d.remarks || ''),
      }
      setLastSavedAt(null)
      setCreatedPrId(Number(d.prid) || null)
      setPrNumber(String(d.prnumber || ''))
      setPrDate(String(d.prdate || ''))
      setRequisitionType(header.requisitionType as any)
      setAllocationType(header.allocationType as any)
      setAllocationCode(header.allocationCode)
      setFacilityType(header.facilityType)
      setCycle(header.cycle)
      setRequiredDate(header.requiredDate)
      setRemarks(header.remarks)
      setPrStatus(String(d.status || 'Draft'))
      const linesData = Array.isArray(d.lines) ? d.lines : []
      const loadedLines: Line[] = linesData.map((ln: any) => ({
        itemtype: String(d.requisitiontype || 'Inventory') as any,
        itemcode: ln.itemcode || '',
        itemdescription: ln.itemdescription || '',
        u_item_details: ln.u_item_details || ln.lineremarks || '',
        quantity: Number(ln.quantity || 0),
        uomcode: ln.uomcode || '',
        unitprice: Number(ln.unitprice || 0),
        accountcode: ln.accountcode || '',
        vendorcode: ln.vendorcode || '',
        warehouse: ln.warehouse || '',
        deliverydate: ln.deliverydate || '',
        projectcode: (ln.projectcode || (d.allocationtype === 'Project' ? String(d.allocationcode || '') : '')),
        dim1: ln.dim1 || '',
        dim2: ln.dim2 || (d.allocationtype === 'CostCenter' ? String(d.allocationcode || '') : ''),
        dim3: ln.dim3 || '',
        dim4: ln.dim4 || '',
        dim5: ln.dim5 || '',
        u_ser_qty: ln.u_ser_qty ?? null,
        u_ser_unit_price: ln.u_ser_unit_price ?? null
      }))
      setLines(loadedLines)
      // The PR as saved: the header, flow and project effects keep it, the delivery dates stay where
      // they are, and autosave sends only what changes from here.
      loadedHeader.current = prHeaderKey(header)
      loadedAllocation.current = `${header.allocationType}|${header.allocationCode}`
      lastRequiredDate.current = header.requiredDate
      autosaveBaseline.current = { prId: Number(d.prid) || 0, body: JSON.stringify(draftAutosavePayload(header, loadedLines)) }
      const attsData = Array.isArray(d.attachments) ? d.attachments : []
      setAttachments(attsData)
      setTab('contents')
      const initiatorId = Number((d as any).requestedbyuserid ?? (d as any).RequestedByUserID ?? 0)
      const currentUserId = Number((user as any)?.userid || 0)
      const isDraft = /draft/i.test(String(d.status || ''))
      const canEditDraft = isDraft && !!currentUserId && currentUserId === initiatorId
      setViewMode(canEditDraft ? 'create' : 'read')
    } catch {} finally {
      if (prLoading.current === loading) prLoading.current = null
      finished()
    }
    setTimeout(() => { if (contentsRef.current) contentsRef.current.scrollTop = prevScroll }, 0)
  }, [user, sendAutosave])

  useEffect(() => {
    const editId = Number(new URLSearchParams(location.search).get('editPR'))
    if (Number.isSafeInteger(editId) && editId > 0) void loadPRById(editId)
  }, [location.search, loadPRById])

  const evaluateApprovalFlowMatch = React.useCallback(async () => {
    // Only the latest request may set the result; an older, slower answer is dropped.
    const request = ++approvalFlowRequest.current
    try {
      if (!requisitionType || !allocationType || !allocationCode || !facilityType || !cycle) {
        setMatchedApprovalFlow('')
        setApprovalFlowError('')
        setApprovalStages([])
        return
      }
      const response = await apiService.get<any>('/pr/approval-route', {
        companyid: Number(localStorage.getItem('selectedCompanyId')),
        requisitiontype: requisitionType, allocationtype: allocationType,
        allocationcode: allocationCode, facilitytype: facilityType, cycle
      })
      if (request !== approvalFlowRequest.current) return
      setMatchedApprovalFlow(String(response?.data?.flowName || 'Unnamed approval flow'))
      setApprovalFlowError('')
      setApprovalStages(Array.isArray(response?.data?.stages) ? response.data.stages : [])
    } catch (error: any) {
      if (request !== approvalFlowRequest.current) return
      setMatchedApprovalFlow('')
      setApprovalFlowError(String(error?.response?.data?.message || error?.message || 'Unable to resolve the approval flow.'))
      setApprovalStages([])
    }
  }, [allocationCode, allocationType, cycle, facilityType, requisitionType])

  useEffect(() => {
    const loadCompanyRules = async () => {
      const request = ++companyRulesRequest.current
      const companyId = Number(localStorage.getItem('selectedCompanyId') || '')
      let rules = NO_COMPANY_RULES
      if (companyId) {
        try {
          const response = await apiService.getPRCompanyRules(companyId)
          rules = { requireLineProject: !!response?.data?.requireLineProject, serviceUnitPriceMustBeOne: !!response?.data?.serviceUnitPriceMustBeOne }
        } catch {
          // Rules unknown: Submit stays available and the server checks them.
        }
      }
      if (request === companyRulesRequest.current) setCompanyRules(rules)
    }
    void loadCompanyRules()
    window.addEventListener('company-changed', loadCompanyRules)
    return () => {
      companyRulesRequest.current += 1
      window.removeEventListener('company-changed', loadCompanyRules)
    }
  }, [])

  // With the company's "unit price 1" rule, a Service line's price is always 1:
  // fill it on rows that have none yet (new, pasted or cleared rows).
  useEffect(() => {
    if (viewMode !== 'create' || !companyRules.serviceUnitPriceMustBeOne) return
    const needsPrice = (l: Line) => isServiceLine(requisitionType, l) && isFilledLine(l) && !Number(l.unitprice)
    if (!lines.some(needsPrice)) return
    setLines(ls => ls.map(l => needsPrice(l) ? { ...l, unitprice: 1 } : l))
  }, [lines, companyRules.serviceUnitPriceMustBeOne, requisitionType, viewMode])

  const loadUserFlowOptions = React.useCallback(async () => {
    try {
      const currentUserId = Number((user as any)?.userid || 0)
      if (!currentUserId) {
        return
      }

      const response = await apiService.getUserAvailableFlowOptions(currentUserId)
      const data = response?.data

      if (!data || data.uniqueFlows === 0) {
        console.log('No approval flows found for user')
        return
      }

      console.log('User has', data.uniqueFlows, 'approval flows:', data.flows)
      console.log('Available options:', data.options)

      setAvailableFlowOptions(data.options)
      setAllAvailableFlows(data.flows)
      setAutoFillApplied(true)

      // The defaults below are for a new PR: a saved PR being opened keeps what it was saved with.
      if (!(await newPrDefaultsAllowed(() => prLoading.current, () => loadedHeader.current))) return

      // Set Required Date to today + 7 days
      const sevenDaysFromNow = new Date()
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7)
      const defaultRequiredDate = sevenDaysFromNow.toISOString().split('T')[0]
      console.log('DEBUG: Setting Required Date to:', defaultRequiredDate)
      setRequiredDate(defaultRequiredDate)

      // If only one flow, auto-fill all fields
      if (data.uniqueFlows === 1) {
        const singleFlow = data.flows[0]
        console.log('Single flow detected, auto-filling all fields:', singleFlow)
        if (singleFlow.requisitionType) setRequisitionType(singleFlow.requisitionType)
        if (singleFlow.allocationType) setAllocationType(singleFlow.allocationType)
        if (singleFlow.facilityType) setFacilityType(singleFlow.facilityType)
        if (singleFlow.cycle) setCycle(singleFlow.cycle)
        if (singleFlow.allocationCode) setAllocationCode(singleFlow.allocationCode)
      } else {
        // Multiple flows: auto-fill only fields with single unique value
        console.log('Multiple flows detected, auto-filling single-value fields')
        if (data.options.requisitionType?.length === 1) {
          console.log('Auto-filling RequisitionType:', data.options.requisitionType[0])
          setRequisitionType(data.options.requisitionType[0])
        }
        if (data.options.allocationType?.length === 1) {
          console.log('Auto-filling AllocationType:', data.options.allocationType[0])
          setAllocationType(data.options.allocationType[0])
        }
        if (data.options.facilityType?.length === 1) {
          console.log('Auto-filling FacilityType:', data.options.facilityType[0])
          setFacilityType(data.options.facilityType[0])
        }
        if (data.options.cycle?.length === 1) {
          console.log('Auto-filling Cycle:', data.options.cycle[0])
          setCycle(data.options.cycle[0])
        }
      }
    } catch (error: any) {
      console.error('Error loading user flow options:', error)
    }
  }, [user])

  const handleFieldChangeAndReEvaluate = React.useCallback((keepHeader = false) => {
    if (!allAvailableFlows || allAvailableFlows.length === 0 || !autoFillApplied) return

    console.log('Re-evaluating approval flows based on current selections:', {
      requisitionType,
      allocationType,
      facilityType,
      cycle,
      allocationCode
    })

    const allocationScopedFlows = sortApprovalFlows(allAvailableFlows.filter(flow => {
      if (allocationType && flow.allocationType && normalizeFlowValue(flow.allocationType) !== normalizeFlowValue(allocationType)) return false
      if (allocationCode && !allocationCodeMatchesFlow(flow.allocationCode, allocationCode)) return false
      return true
    }))

    if (allocationCode && allocationScopedFlows.length === 0) {
      setSelectedFlowId('')
      setWarnInfo(`No approval flow is assigned to you for ${allocationType} ${allocationCode}. Please select another allocation or contact administrator.`)
      return
    }

    const matchingFlows = allocationScopedFlows.filter(flowMatchesCurrentHeader)

    console.log(`Found ${matchingFlows.length} matching flows:`, matchingFlows)

    const optionSource = matchingFlows.length > 0 ? matchingFlows : allocationScopedFlows
    const recalculatedOptions = buildFlowOptions(optionSource)

    console.log('Recalculated options based on matching flows:', recalculatedOptions)

    const optionsChanged =
      JSON.stringify([...recalculatedOptions.requisitionType].sort()) !== JSON.stringify([...(availableFlowOptions?.requisitionType || [])].sort()) ||
      JSON.stringify([...recalculatedOptions.allocationType].sort()) !== JSON.stringify([...(availableFlowOptions?.allocationType || [])].sort()) ||
      JSON.stringify([...recalculatedOptions.facilityType].sort()) !== JSON.stringify([...(availableFlowOptions?.facilityType || [])].sort()) ||
      JSON.stringify([...recalculatedOptions.cycle].sort()) !== JSON.stringify([...(availableFlowOptions?.cycle || [])].sort()) ||
      JSON.stringify([...recalculatedOptions.allocationCode].sort()) !== JSON.stringify([...(availableFlowOptions?.allocationCode || [])].sort())

    if (optionsChanged) {
      console.log('Options changed, updating availableFlowOptions')
      setAvailableFlowOptions(recalculatedOptions)
    } else {
      console.log('Options unchanged, skipping update')
    }

    // A PR just opened keeps its saved header: show the flow it matches and apply none.
    if (keepHeader && matchingFlows.length <= 1) {
      setWarnInfo('')
      setSelectedFlowId(matchingFlows.length === 1 ? `${allAvailableFlows.indexOf(matchingFlows[0])}` : '')
      return
    }

    if (allocationScopedFlows.length === 1) {
      setWarnInfo('')
      applyApprovalFlowToHeader(allocationScopedFlows[0])
      return
    }

    if (allocationScopedFlows.length > 1 && matchingFlows.length === 0) {
      const firstFlow = allocationScopedFlows[0]
      applyApprovalFlowToHeader(firstFlow)
      setWarnInfo(`Multiple approval combinations are available for ${allocationType} ${allocationCode}. The first combination was selected automatically; change Module Type / Facility / Cycle if required.`)
      return
    }

    if (matchingFlows.length === 1) {
      setWarnInfo('')
      applyApprovalFlowToHeader(matchingFlows[0])
    } else if (matchingFlows.length > 1) {
      setWarnInfo(`Multiple approval combinations are available for ${allocationType} ${allocationCode}. Please select the exact combination from the approval flow list.`)
    } else {
      console.log('No matching flows found for current selection')
    }
  }, [allAvailableFlows, allocationCode, allocationCodeMatchesFlow, allocationType, applyApprovalFlowToHeader, autoFillApplied, availableFlowOptions, buildFlowOptions, cycle, facilityType, flowMatchesCurrentHeader, normalizeFlowValue, requisitionType, sortApprovalFlows])

  const handleFlowSelection = React.useCallback((flowId: string) => {
    if (!flowId || !allAvailableFlows.length) {
      setSelectedFlowId('')
      return
    }

    const selectedFlow = allAvailableFlows.find((_f, idx) => `${idx}` === flowId)
    if (!selectedFlow) {
      console.log('Selected flow not found')
      return
    }

    console.log('Flow selected, auto-filling all fields:', selectedFlow)
    setSelectedFlowId(flowId)

    // Auto-fill all fields from the selected flow
    if (selectedFlow.requisitionType) setRequisitionType(selectedFlow.requisitionType)
    if (selectedFlow.allocationType) setAllocationType(selectedFlow.allocationType)
    if (selectedFlow.facilityType) setFacilityType(selectedFlow.facilityType)
    if (selectedFlow.cycle) setCycle(selectedFlow.cycle)
    if (selectedFlow.allocationCode) setAllocationCode(selectedFlow.allocationCode)

    // Set Required Date to today + 7 days when the PR has none (it fills the type, facility and cycle)
    const sevenDaysFromNow = new Date()
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7)
    const defaultRequiredDate = sevenDaysFromNow.toISOString().split('T')[0]
    setRequiredDate((current) => current || defaultRequiredDate)
  }, [allAvailableFlows])

  useEffect(() => {
    if (viewMode === 'create') {
      void evaluateApprovalFlowMatch()
    }
  }, [evaluateApprovalFlowMatch, viewMode])

  useEffect(() => {
    if (viewMode === 'create' && !createdPrId) {
      void loadUserFlowOptions()
    }
  }, [viewMode, createdPrId, loadUserFlowOptions])

  useEffect(() => {
    if (triggerFlowEval > 0) {
      void evaluateApprovalFlowMatch()
    }
  }, [triggerFlowEval, evaluateApprovalFlowMatch])

  useEffect(() => {
    if (viewMode === 'create' && availableFlowOptions) {
      // While the header is the one a saved PR was opened with, the re-check applies no flow to it.
      handleFieldChangeAndReEvaluate(loadedHeader.current === prHeaderKey({ requisitionType, allocationType, allocationCode, facilityType, cycle }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, requisitionType, allocationType, facilityType, cycle, allocationCode])

  const loadPRByIndex = async (idx: number) => {
    const list = activeList
    if (!list.length) return
    const i = Math.max(0, Math.min(list.length - 1, idx))
    const pr = list[i]
    setCurrentIndex(i)
    await loadPRById(Number(pr.prid))
  }

  const goFirst = () => { if (activeList.length) void loadPRByIndex(0) }
  const goPrev = () => { if (canNavigate) void loadPRByIndex(Math.max(0, (currentIndex as number) - 1)) }
  const goNext = () => { if (canNavigate) void loadPRByIndex(Math.min(activeList.length - 1, (currentIndex as number) + 1)) }
  const goLast = () => { if (activeList.length) void loadPRByIndex(activeList.length - 1) }

  const executeFind = () => {
    const filtered = prList.filter((pr: any) => {
      // Extract date without timezone conversion - match YYYY-MM-DD format directly
      const prDateStr = String(pr.requireddate || '').trim()
      const prDate = prDateStr.match(/^(\d{4}-\d{2}-\d{2})/) ? prDateStr.slice(0, 10) : ''
      if (requisitionType && String(pr.requisitiontype || '') !== String(requisitionType)) return false
      if (allocationType && String(pr.allocationtype || '') !== String(allocationType)) return false
      if (allocationCode && !String(pr.allocationcode || '').toLowerCase().includes(String(allocationCode).toLowerCase())) return false
      if (requiredDate && prDate !== String(requiredDate)) return false
      return true
    })
    setFilteredPrList(filtered)
    if (filtered.length) { void loadPRByIndex(0) } else { setCurrentIndex(null) }
  }

  useEffect(() => {
    const onPick = (ev: any) => {
      const prId = Number(ev?.detail?.id || 0)
      if (prId) { void loadPRById(prId) }
    }
    window.addEventListener('pr-select', onPick as any)
    return () => { window.removeEventListener('pr-select', onPick as any) }
  }, [loadPRById])

  
  
  

  const loadInlineList = React.useCallback(async (query?: string) => {
    setInlineLoading(true)
    try {
      let nextRows: any[] = []
      // The signed-in user's grants in the selected company (utils/userAccess): null = every one.
      const allowedSet = allowedCodes('projects')
      const allowedDim1Set = allowedCodes('divisions')
      // The header cost center is a department (Dimension 2): PR submit checks that grant.
      const allowedDim2Set = applyToRowId === null ? allowedCodes('departments') : null
      if (inlinePickerType === 'warehouse') {
        const res = await apiService.getWarehouses()
        const data = (res as any)?.data || res || []
        const filtered = query ? data.filter((w: any) => String(w.WarehouseCode || w.WhsCode).toLowerCase().includes(query.toLowerCase()) || String(w.WarehouseName || w.WhsName).toLowerCase().includes(query.toLowerCase())) : data
        nextRows = filtered.map((w: any) => ({ id: String(w.WarehouseCode ?? w.WhsCode ?? ''), WarehouseCode: w.WarehouseCode ?? w.WhsCode, WarehouseName: w.WarehouseName ?? w.WhsName }))
      } else if (inlinePickerType === 'item') {
        const res = await apiService.listLocalItemMaster(undefined, undefined, requisitionType)
        let data = (res as any)?.data || res || []

        // Apply flexible search filter
        if (query && query.trim().length > 0) {
          const searchLower = query.toLowerCase()
          data = data.filter((it: any) => {
            const itemCode = String(it.data?.ItemCode ?? it.ItemCode ?? '').toLowerCase()
            const itemName = String(it.data?.ItemName ?? it.ItemName ?? '').toLowerCase()
            return itemCode.includes(searchLower) || itemName.includes(searchLower)
          })
        }

        nextRows = data.map((it: any) => ({
          id: String(it.data?.ItemCode ?? it.ItemCode ?? ''),
          ItemCode: it.data?.ItemCode ?? it.ItemCode,
          ItemName: it.data?.ItemName ?? it.ItemName,
          PurchaseUnit: (it.data?.PurchaseUnit ?? (it as any).PurchaseUnit ?? it.data?.InventoryUOM ?? it.InventoryUOM),
          ItemsGroupCode: it.data?.ItemsGroupCode ?? it.ItemsGroupCode
        }))
      } else if (inlinePickerType === 'vendor') {
        const res = await apiService.getVendors()
        const data = (res as any)?.data || res || []
        const filtered = query ? data.filter((v: any) => String(v.CardCode || v.cardcode).toLowerCase().includes(query.toLowerCase()) || String(v.CardName || v.cardname).toLowerCase().includes(query.toLowerCase())) : data
        nextRows = filtered.map((v: any) => ({ id: String(v.CardCode ?? v.cardcode ?? ''), CardCode: v.CardCode ?? v.cardcode, CardName: v.CardName ?? v.cardname }))
      } else if (inlinePickerType === 'project') {
        const res = await apiService.getProjects()
        let data = (res as any)?.data || res || []
        if (allowedSet && !isAdminUser) data = data.filter((p: any) => allowedSet.has(String(p.Code || p.ProjCode || p.Project || '').trim().toLowerCase()))
        const filtered = query ? data.filter((p: any) => String(p.Code || p.ProjCode || p.Project || '').toLowerCase().includes(query.toLowerCase()) || String(p.Name || p.ProjectName || '').toLowerCase().includes(query.toLowerCase())) : data
        nextRows = filtered.map((p: any) => ({ id: String(p.Code ?? p.ProjCode ?? p.Project ?? ''), Code: p.Code ?? p.ProjCode ?? p.Project ?? '', Name: p.Name ?? p.ProjectName ?? '' }))
      } else if (['dim1','dim2','dim3','dim4','dim5'].includes(inlinePickerType)) {
        // All dimensions now use masterdatalocal with InWhichDimension mapping
        // dim1 (Division) → InWhichDimension = 1
        // dim2 (Department) → InWhichDimension = 2
        // dim3 (WBS) → InWhichDimension = 3
        // dim4 (Scope/Auxiliary) → InWhichDimension = 5
        // dim5 (LOC) → InWhichDimension = 4
        const dimensionMap: Record<string, number> = {
          dim1: 1,
          dim2: 2,
          dim3: 3,
          dim4: 5,
          dim5: 4
        }
        const dimension = dimensionMap[inlinePickerType]
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined

        // For dim4 (Auxiliary), filter by project if a project is selected in the current row
        if (inlinePickerType === 'dim4' && applyToRowId !== null) {
          const currentRow = lines[applyToRowId]
          const projectCode = currentRow?.projectcode
          if (projectCode) {
            console.log('Loading auxiliary filtered by project (inline picker):', projectCode)
            const res = await apiService.get<any>('/master-data/auxiliary-by-project', {
              projectcode: projectCode,
              companyid: selectedCompanyId
            })
            console.log('Auxiliary API response (inline picker):', res)
            let data = (res as any)?.data || res || []
            console.log('Auxiliary data array (inline picker):', data.length, 'items', data)
            const filtered = query ? data.filter((d: any) => String(d.FactorCode || '').toLowerCase().includes(query.toLowerCase()) || String(d.FactorDescription || '').toLowerCase().includes(query.toLowerCase())) : data
            console.log('Filtered auxiliary (inline picker):', filtered.length, 'items')
            nextRows = filtered.map((d: any) => ({
              id: String(d.FactorCode || ''),
              FactorCode: d.FactorCode || '',
              FactorDescription: d.FactorDescription || '',
              InWhichDimension: d.InWhichDimension || dimension
            }))
            console.log('Next rows for inline picker:', nextRows.length, 'items', nextRows)
          } else {
            // No project selected, show all auxiliary
            const res = await apiService.get<any>('/master-data/distribution-rules', {
              dimension,
              companyid: selectedCompanyId
            })
            let data = (res as any)?.data || res || []
            const filtered = query ? data.filter((d: any) => String(d.FactorCode || '').toLowerCase().includes(query.toLowerCase()) || String(d.FactorDescription || '').toLowerCase().includes(query.toLowerCase())) : data
            nextRows = filtered.map((d: any) => ({
              id: String(d.FactorCode || ''),
              FactorCode: d.FactorCode || '',
              FactorDescription: d.FactorDescription || '',
              InWhichDimension: d.InWhichDimension || dimension
            }))
          }
        } else {
          // For other dimensions or when no row is selected
          const res = await apiService.get<any>('/master-data/distribution-rules', {
            dimension,
            companyid: selectedCompanyId
          })
          let data = (res as any)?.data || res || []

          // Apply dim1 allowed set filtering if applicable
          if (inlinePickerType === 'dim1' && allowedDim1Set) {
            data = data.filter((d: any) => allowedDim1Set.has(String(d.FactorCode || '').trim().toLowerCase()))
          }
          if (inlinePickerType === 'dim2' && allowedDim2Set) {
            data = data.filter((d: any) => allowedDim2Set.has(String(d.FactorCode || '').trim().toLowerCase()))
          }

          const filtered = query ? data.filter((d: any) => String(d.FactorCode || '').toLowerCase().includes(query.toLowerCase()) || String(d.FactorDescription || '').toLowerCase().includes(query.toLowerCase())) : data
          nextRows = filtered.map((d: any) => ({
            id: String(d.FactorCode || ''),
            FactorCode: d.FactorCode || '',
            FactorDescription: d.FactorDescription || '',
            InWhichDimension: d.InWhichDimension || dimension
          }))
        }
      }
      const sig = JSON.stringify(nextRows.map((r: any) => r.id))
      if (inlineRowsSigRef.current !== sig) {
        inlineRowsSigRef.current = sig
        setInlineRows(nextRows)

        // Auto-select if only 1 result
        if (nextRows.length === 1 && inlinePickerType) {
          const single = nextRows[0]
          if (inlinePickerType === 'project') {
            const code = String(single.Code || '')
            setAllocationCode(code)
            setInlinePickerOpen(false)
          } else if (inlinePickerType === 'dim1') {
            const code = String(single.FactorCode || '')
            setAllocationCode(code)
            setInlinePickerOpen(false)
          }
        }
      }
    } catch {
      setInlineRows([])
      inlineRowsSigRef.current = '[]'
    } finally {
      setInlineLoading(false)
    }
  }, [inlinePickerType, isAdminUser, requisitionType, applyToRowId, lines])

  useEffect(() => {
    if (!inlinePickerOpen || inlineOpenedOnceRef.current) return
    inlineOpenedOnceRef.current = true
    setInlineSelection([])
    void loadInlineList('')
  }, [inlinePickerOpen, inlinePickerType, loadInlineList])

  useEffect(() => {
    if (!inlinePickerOpen) return
    if (inlineDebounceRef.current) { try { clearTimeout(inlineDebounceRef.current) } catch {} }
    inlineDebounceRef.current = setTimeout(() => { void loadInlineList(inlineQuery) }, 300) as any
    return () => { if (inlineDebounceRef.current) { try { clearTimeout(inlineDebounceRef.current) } catch {} } }
  }, [inlineQuery, inlinePickerOpen, inlinePickerType, loadInlineList])

  const projectMappingRequest = useRef<Record<number, number>>({})
  const applyLineProject = React.useCallback(async (rowIndex: number, rawCode: string) => {
    const projectCode = String(rawCode || '').trim()
    const request = (projectMappingRequest.current[rowIndex] || 0) + 1
    projectMappingRequest.current[rowIndex] = request
    setLines(ls => ls.map((line, index) => index === rowIndex
      ? withItemAuxiliary(lineWithProjectDefaults(line, projectCode, {}, '',
          !projectCode && allocationType === 'CostCenter' ? allocationCode : ''), knownItemAuxiliary) : line))
    // The line's item keeps its own Auxiliary (asked now when not known yet).
    const lineItem = String(linesRef.current[rowIndex]?.itemcode || '').trim()
    if (lineItem) void fillItemAuxiliaries([{ rowIdx: rowIndex, itemCode: lineItem }])
    if (!projectCode) return
    try {
      const companyid = Number(localStorage.getItem('selectedCompanyId') || '') || undefined
      const response = await apiService.get<any>('/master-data', {
        companyid, endpointkey: 'sap-projects', keycode: projectCode,
      })
      const project = ((response.data || [])[0] || {}).jsondata || {}
      let auxiliary = String(project.U_SCOPE ?? '').trim()
      if (!auxiliary) {
        try {
          const response = await apiService.get<any>('/master-data/auxiliary-by-project', {
            projectcode: projectCode, companyid,
          })
          const choices = (response as any)?.data || response || []
          if (choices.length === 1) {
            const rule = choices[0].data || choices[0]
            auxiliary = String(rule.FactorCode || rule.Code || rule.DistributionRule || '').trim()
          }
        } catch (error) { console.error('Unable to map project auxiliary', projectCode, error) }
      }
      if (projectMappingRequest.current[rowIndex] !== request) return
      setLines(ls => ls.map((line, index) => index === rowIndex && line.projectcode === projectCode
        ? withItemAuxiliary(lineWithProjectDefaults(line, projectCode, project, auxiliary,
            allocationType === 'CostCenter' ? allocationCode : ''), knownItemAuxiliary) : line))
    } catch (error) {
      console.error('Unable to map PR line project', projectCode, error)
    }
  }, [allocationType, allocationCode, knownItemAuxiliary, fillItemAuxiliaries])

  const resolveOnEdit = React.useCallback(async (field: string, value: string, params: any) => {
    const v = String(value || '').trim()
    if (!v) return
    if (field === 'itemcode') {
      try {
        const res = await apiService.listLocalItemMaster()
        const data = (res as any)?.data || res || []
        const filtered = data.filter((it: any) => String(it.data?.ItemCode || it.ItemCode || '').toLowerCase().includes(v.toLowerCase()))
        if (filtered.length === 1) {
          const it = filtered[0]
          const code = String(it.data?.ItemCode ?? it.ItemCode ?? '')
          const name = String(it.data?.ItemName ?? it.ItemName ?? '')
          const uom = String(it.data?.PurchaseUnit ?? (it as any).PurchaseUnit ?? it.data?.InventoryUOM ?? it.InventoryUOM ?? '')
          await params.api.setEditCellValue({ id: params.id, field: 'itemcode', value: code })
          await params.api.setEditCellValue({ id: params.id, field: 'itemdescription', value: name })
          await params.api.setEditCellValue({ id: params.id, field: 'uomcode', value: uom })
          const row: any = typeof params.api.getRow === 'function' ? params.api.getRow(params.id) : undefined
          const curr = String(row?.deliverydate || '')
          if (!curr) await params.api.setEditCellValue({ id: params.id, field: 'deliverydate', value: requiredDate || '' })
          const igc = String(((it as any)?.data?.ItemsGroupCode ?? (it as any)?.ItemsGroupCode ?? '') || '')
          if ((requisitionType === 'Inventory' || requisitionType === 'Service' || requisitionType === 'FixedAsset') && igc) {
            try {
              const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined
              const gRes = await apiService.getItemGroups({ companyId: selectedCompanyId, code: igc })
              const arr = (gRes as any)?.data || gRes || []
              const grp = arr[0]
              const uCost = String((grp?.Data?.U_MCSR_CODE ?? grp?.U_MCSR_CODE ?? '') || '').trim()
              if (uCost) {
                try {
                  const drRes = await apiService.get<any>('/master-data/distribution-rules', { dimension: 3, companyid: selectedCompanyId })
                  const drList = (drRes as any)?.data || drRes || []
                  const exists = drList.some((d: any) => String(d.FactorCode || d.Code || d.DistributionRule || '').trim() === uCost.trim())
                  if (exists) await params.api.setEditCellValue({ id: params.id, field: 'dim3', value: uCost })
                  else setWarnInfo(`Cost Code mapping for Item Group ${igc} not found. Please select WBS manually.`)
                } catch {}
              }
            } catch {}
          }
        } else if (filtered.length > 1) {
          setApplyToRowId(Number(params.id))
          setInlinePickerType('item')
          setInlineQuery(v)
          setInlineSelection([])
          inlineOpenedOnceRef.current = false
          setInlinePickerOpen(true)
        }
      } catch {}
    } else if (field === 'vendorcode') {
      try {
        const res = await apiService.getVendors()
        const data = (res as any)?.data || res || []
        const filtered = data.filter((vnd: any) => String(vnd.CardCode || vnd.cardcode || '').toLowerCase().includes(v.toLowerCase()))
        if (filtered.length === 1) {
          const first = filtered[0]
          const code = String(first.CardCode ?? first.cardcode ?? '')
          await params.api.setEditCellValue({ id: params.id, field: 'vendorcode', value: code })
        } else if (filtered.length > 1) {
          setApplyToRowId(Number(params.id))
          setInlinePickerType('vendor')
          setInlineQuery(v)
          setInlineSelection([])
          inlineOpenedOnceRef.current = false
          setInlinePickerOpen(true)
        }
      } catch {}
    } else if (field === 'warehouse') {
      try {
        const res = await apiService.getWarehouses()
        const data = (res as any)?.data || res || []
        const filtered = data.filter((w: any) => String(w.WarehouseCode || w.WhsCode || '').toLowerCase().includes(v.toLowerCase()))
        if (filtered.length === 1) {
          const first = filtered[0]
          const code = String(first.WarehouseCode ?? first.WhsCode ?? '')
          await params.api.setEditCellValue({ id: params.id, field: 'warehouse', value: code })
        } else if (filtered.length > 1) {
          setApplyToRowId(Number(params.id))
          setInlinePickerType('warehouse')
          setInlineQuery(v)
          setInlineSelection([])
          inlineOpenedOnceRef.current = false
          setInlinePickerOpen(true)
        }
      } catch {}
    } else if (field === 'projectcode') {
      try {
        const res = await apiService.getProjects()
        let data = (res as any)?.data || res || []
        let filtered = data
        const allowed = allowedCodes('projects')
        if (allowed) filtered = filtered.filter((p: any) => allowed.has(String(p.Code || p.ProjCode || p.Project || '').trim().toLowerCase()))
        filtered = filtered.filter((p: any) => String(p.Code || p.ProjCode || p.Project || '').toLowerCase().includes(v.toLowerCase()))
        if (filtered.length === 1) {
          const first = filtered[0]
          const projCode = String(first.Code || first.ProjCode || first.Project || '')
          await params.api.setEditCellValue({ id: params.id, field: 'projectcode', value: projCode })
          await applyLineProject(Number(params.id), projCode)
        } else if (filtered.length > 1) {
          setApplyToRowId(Number(params.id))
          setInlinePickerType('project')
          setInlineQuery(v)
          setInlineSelection([])
          inlineOpenedOnceRef.current = false
          setInlinePickerOpen(true)
        }
      } catch {}
    } else if (field === 'dim1' || field === 'dim2' || field === 'dim3' || field === 'dim4' || field === 'dim5') {
      try {
        const level = field === 'dim1' ? 1 : field === 'dim2' ? 2 : field === 'dim3' ? 3 : field === 'dim4' ? 4 : 5
        const res = await apiService.get<any>('/sap/distribution-rules', { level })
        const data = (res as any)?.data || res || []
        let base = data
        if (field === 'dim1') {
          const allowedDivisions = allowedCodes('divisions')
          if (allowedDivisions) base = base.filter((d: any) => allowedDivisions.has(String(d.FactorCode || d.Code || d.DistributionRule || '').trim().toLowerCase()))
        }
        const filtered = base.filter((d: any) => String(d.FactorCode || d.Code || d.DistributionRule || '').toLowerCase().includes(v.toLowerCase()))
        if (filtered.length === 1) {
          const first = filtered[0]
          const code = String(first.FactorCode || first.Code || first.DistributionRule || '')
          await params.api.setEditCellValue({ id: params.id, field, value: code })
        } else if (filtered.length > 1) {
          setApplyToRowId(Number(params.id))
          setInlinePickerType(field as any)
          setInlineQuery(v)
          setInlineSelection([])
          inlineOpenedOnceRef.current = false
          setInlinePickerOpen(true)
        }
      } catch {}
    }
  }, [requisitionType, requiredDate, applyLineProject])

  const createEmptyLine = React.useCallback((): Line => ({
    ...newLineAllocation,
    itemtype: requisitionType,
    quantity: 1,
    unitprice: 1,
    accountcode: '',
    vendorcode: '',
    deliverydate: '',
  }), [requisitionType, newLineAllocation])

  const addLine = React.useCallback(() => setLines((ls) => ([...ls, createEmptyLine()])), [createEmptyLine])

  const updateLine = React.useCallback((idx: number, patch: Partial<Line>) => {
    console.log('DEBUG updateLine called: idx=', idx, 'patch=', patch, 'requiredDate=', requiredDate)
    setLines((ls) => ls.map((l, i) => {
      if (i !== idx) return l
      const updated = { ...l, ...patch }
      if (patch.deliverydate !== undefined) updated.deliverydate = fixTwoDigitYear(patch.deliverydate)
      // Auto-fill delivery date when item is added and delivery date is empty
      const hasItem = updated.itemcode || updated.itemdescription
      const hasNoDate = !isDocumentDate(updated.deliverydate)
      if (hasItem && hasNoDate && isDocumentDate(requiredDate)) {
        console.log('DEBUG updateLine: Auto-filling deliverydate with', requiredDate)
        updated.deliverydate = requiredDate
      }
      return updated
    }))
  }, [requiredDate])
  const removeLine = React.useCallback((idx: number) => setLines((ls) => ls.filter((_, i) => i !== idx)), [])

  const handleCopyRow = React.useCallback((idx: number) => {
    const row = lines[idx]
    if (row) {
      setClipboardRow({ ...row })
      setClipboardMode('copy')
      dispatch(showSnackbar({ message: 'Row copied', severity: 'success' }))
    }
  }, [lines, dispatch])

  const handleCutRow = React.useCallback((idx: number) => {
    const row = lines[idx]
    if (row) {
      setClipboardRow({ ...row })
      setClipboardMode('cut')
      removeLine(idx)
      dispatch(showSnackbar({ message: 'Row cut', severity: 'success' }))
    }
  }, [lines, removeLine, dispatch])

  const handlePasteRow = React.useCallback((idx: number) => {
    if (!clipboardRow) {
      dispatch(showSnackbar({ message: 'No row to paste', severity: 'warning' }))
      return
    }

    const newRow = { ...clipboardRow }
    setLines((ls) => {
      const updated = [...ls]
      updated[idx] = newRow
      return updated
    })

    if (clipboardMode === 'cut') {
      setClipboardRow(null)
      setClipboardMode(null)
    }

    dispatch(showSnackbar({ message: 'Row pasted', severity: 'success' }))
  }, [clipboardRow, clipboardMode, dispatch])

  const handleCopyTable = React.useCallback(() => {
    if (!gridRef.current?.api) {
      dispatch(showSnackbar({ message: 'Grid not ready', severity: 'warning' }))
      return
    }

    try {
      const api = gridRef.current.api

      // Get all columns except index and checkbox
      const columnKeys: string[] = []
      const headers: string[] = []

      api.getColumns()?.forEach((col: any) => {
        const colId = col.getColId()
        // Skip internal columns
        if (colId !== 'index' && !col.getColDef()?.checkboxSelection) {
          columnKeys.push(colId)
          const colDef = col.getColDef()
          headers.push(colDef.headerName || colDef.field || '')
        }
      })

      // Get all rows
      const rowData: any[] = []
      api.forEachNode((node: any) => {
        rowData.push(node.data)
      })

      if (rowData.length === 0) {
        dispatch(showSnackbar({ message: 'No data to copy', severity: 'warning' }))
        return
      }

      // Build tab-separated content with headers
      let clipboardContent = headers.join('\t') + '\n'

      rowData.forEach((row: any) => {
        const values = columnKeys.map((key) => {
          const val = row[key]
          // Handle different data types
          if (val === null || val === undefined) return ''
          if (typeof val === 'number') return String(val)
          return String(val)
        })
        clipboardContent += values.join('\t') + '\n'
      })

      // Create a textarea element to copy to clipboard (more reliable than navigator.clipboard)
      const textarea = document.createElement('textarea')
      textarea.value = clipboardContent
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()

      try {
        const successful = document.execCommand('copy')
        document.body.removeChild(textarea)

        if (successful) {
          const rowCount = rowData.length
          dispatch(showSnackbar({
            message: `Table copied (${rowCount} rows with headers) - Ready to paste in Excel`,
            severity: 'success'
          }))
        } else {
          dispatch(showSnackbar({ message: 'Failed to copy table', severity: 'error' }))
        }
      } catch (err) {
        document.body.removeChild(textarea)
        // Fallback to navigator.clipboard
        navigator.clipboard.writeText(clipboardContent).then(() => {
          const rowCount = rowData.length
          dispatch(showSnackbar({
            message: `Table copied (${rowCount} rows with headers)`,
            severity: 'success'
          }))
        }).catch(() => {
          dispatch(showSnackbar({ message: 'Failed to copy table', severity: 'error' }))
        })
      }
    } catch (error) {
      dispatch(showSnackbar({ message: 'Error copying table', severity: 'error' }))
    }
  }, [dispatch])

  const handleRefreshPage = React.useCallback(() => {
    window.location.reload()
  }, [])

  const autoFillItemDetails = React.useCallback(async (rowIdx: number, itemCode: string, cachedItems?: any[]) => {
    try {
      const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined

      // Use cached items if provided, otherwise fetch from local master data
      let data: any[] = cachedItems || []
      if (!cachedItems) {
        const res = await apiService.listLocalItemMaster(undefined, undefined, requisitionType)
        data = (res as any)?.data || res || []
      }

      const item = data.find((it: any) =>
        String(it.data?.ItemCode ?? it.ItemCode ?? '').toUpperCase() === itemCode.toUpperCase()
      )

      if (!item) {
        console.warn('Item not found:', itemCode)
        return false
      }

      const itemName = String(item.data?.ItemName ?? item.ItemName ?? '')
      const uom = String(item.data?.PurchaseUnit ?? (item as any).PurchaseUnit ?? item.data?.InventoryUOM ?? item.InventoryUOM ?? '')
      const itemsGroupCode = String(item.data?.ItemsGroupCode ?? item.ItemsGroupCode ?? '')

      // Update line with basic item info
      updateLine(rowIdx, {
        itemcode: itemCode,
        itemdescription: itemName,
        uomcode: uom
      })

      // Auto-fill Auxiliary (dim4: the item's own, SAP U_AUX_Code) and Unit Price from item master data
      try {
        const sapItemCode = String(item.data?.ItemCode ?? item.ItemCode ?? itemCode).trim()
        const [itemRes, auxiliaries] = await Promise.all([
          apiService.get<any>('/master-data', {
            companyid: selectedCompanyId,
            endpointkey: 'sap-items',
            keycode: itemCode
          }),
          loadItemAuxiliaries([sapItemCode]),
        ])
        const itemData = ((itemRes.data || [])[0] || {}).jsondata || {}
        const auxCode = String(auxiliaries.get(sapItemCode) || '').trim()

        // Extract unit price from item master data
        const unitPrice = Number(
          itemData.ItemPrices?.[0]?.Price ||
          itemData.LastPurchasePrice ||
          itemData.AvgStdPrice ||
          itemData.PurchasePrice ||
          0
        )

        const updates: any = {}
        if (auxCode) {
          updates.dim4 = auxCode
          console.log('Auto-filled Auxiliary from item paste:', auxCode)
        }
        if (requisitionType === 'Service') {
          updates.unitprice = 1
        } else if (unitPrice > 0) {
          updates.unitprice = unitPrice
          console.log('Auto-filled Unit Price from item paste:', unitPrice)
        }

        if (Object.keys(updates).length > 0) {
          updateLine(rowIdx, updates)
        }
      } catch (error) {
        console.error('Error fetching item details:', error)
      }

      // Auto-fill Cost Code/WBS (dim3) from item group
      if ((requisitionType === 'Inventory' || requisitionType === 'Service' || requisitionType === 'FixedAsset') && itemsGroupCode) {
        try {
          const gRes = await apiService.getItemGroups({ companyId: selectedCompanyId, code: itemsGroupCode })
          const arr = (gRes as any)?.data || gRes || []
          const grp = arr[0]
          const uCost = String((grp?.Data?.U_MCSR_CODE ?? grp?.U_MCSR_CODE ?? '') || '').trim()
          if (uCost) {
            updateLine(rowIdx, { dim3: uCost })
            console.log('Auto-filled Cost Code from item paste:', uCost)
          }
        } catch (error) {
          console.error('Error fetching item group:', error)
        }
      }

      return true
    } catch (error) {
      console.error('Error auto-filling item details:', error)
      return false
    }
  }, [requisitionType, updateLine, loadItemAuxiliaries])

  /**
   * Lines from the Import dialog (Excel template or pasted cells, already checked). They are
   * added after the existing lines or replace them, then filled in like typed lines: item
   * details from SAP, the project's dimensions. Values in the file win over what is filled in.
   */
  const handleImportLines = React.useCallback(async (imported: ImportedLine[], mode: ImportMode, cachedItems: any[]) => {
    setImportOpen(false)
    const kept = mode === 'append' ? lines.filter(isFilledLine) : []
    const startIndex = kept.length
    const base = createEmptyLine()
    setLines([...kept, ...imported.map((imp) => ({ ...base, ...(imp as Partial<Line>) }) as Line)])
    dispatch(showSnackbar({ message: `Importing ${imported.length} line${imported.length === 1 ? '' : 's'}: filling item details from SAP…`, severity: 'info' }))
    void loadItemAuxiliaries(imported.map((imp) => String(imp.itemcode || '')))
    const batchSize = 5
    for (let i = 0; i < imported.length; i += batchSize) {
      await Promise.all(imported.slice(i, i + batchSize).map(async (imp, j) => {
        const rowIdx = startIndex + i + j
        try {
          if (imp.itemcode) await autoFillItemDetails(rowIdx, String(imp.itemcode), cachedItems.length ? cachedItems : undefined)
          if (imp.projectcode) await applyLineProject(rowIdx, String(imp.projectcode))
        } catch (error) {
          console.error('Import: could not fill line', rowIdx + 1, error)
        }
        const own: Partial<Line> = {}
        for (const [field, value] of Object.entries(imp)) if (value !== undefined && value !== '') (own as any)[field] = value
        updateLine(rowIdx, own)
      }))
    }
    dispatch(showSnackbar({ message: `${imported.length} line${imported.length === 1 ? '' : 's'} imported`, severity: 'success' }))
  }, [lines, createEmptyLine, dispatch, autoFillItemDetails, applyLineProject, updateLine, loadItemAuxiliaries])

  const handleExcelQuickPaste = React.useCallback(async (pastedText: string, startRowIdx: number, startColId?: string) => {
    const activeColId = startColId === 'quantity' ? 'quantity' : startColId === 'itemcode' ? 'itemcode' : ''
    if (!activeColId || viewMode !== 'create') return false

    const parsedRows = pastedText
      .split(/\r?\n/)
      .map((row) => row.split('\t').map((cell) => String(cell || '').trim()))
      .filter((cells) => cells.some((cell) => cell.length > 0))

    if (parsedRows.length === 0) return true

    const maxColumnCount = Math.max(...parsedRows.map((cells) => cells.length))
    if (maxColumnCount > 2) return false

    const headerAliases: Record<string, 'itemcode' | 'quantity' | ''> = {
      item: 'itemcode',
      itemcode: 'itemcode',
      'item code': 'itemcode',
      'item no': 'itemcode',
      'item no.': 'itemcode',
      code: 'itemcode',
      qty: 'quantity',
      quantity: 'quantity',
      'req qty': 'quantity',
      'req. qty': 'quantity',
      'required qty': 'quantity'
    }
    const firstRowHeaders = parsedRows[0].map((cell) => headerAliases[cell.toLowerCase()] || '')
    const hasSupportedHeader = firstRowHeaders.some(Boolean)
    const dataRows = hasSupportedHeader ? parsedRows.slice(1) : parsedRows
    if (dataRows.length === 0) return true

    const parsePositiveNumber = (value: string): number | null => {
      const normalized = String(value || '').replace(/[, ]/g, '').trim()
      if (!normalized) return null
      const parsed = Number(normalized)
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null
    }

    let fields: Array<'itemcode' | 'quantity'>
    if (hasSupportedHeader) {
      fields = firstRowHeaders.filter(Boolean) as Array<'itemcode' | 'quantity'>
    } else if (maxColumnCount === 1) {
      fields = [activeColId as 'itemcode' | 'quantity']
    } else {
      fields = ['itemcode', 'quantity']
    }

    if (!fields.length) return false

    dispatch(showSnackbar({ message: 'Validating pasted PR lines...', severity: 'info' }))

    let itemMap = new Map<string, any>()
    let cachedLocalItems: any[] = []
    if (fields.includes('itemcode')) {
      const res = await apiService.listLocalItemMaster(undefined, undefined, requisitionType)
      cachedLocalItems = (res as any)?.data || res || []
      cachedLocalItems.forEach((item: any) => {
        const code = String(item.data?.ItemCode ?? item.ItemCode ?? '').trim().toUpperCase()
        if (code) itemMap.set(code, item)
      })
    }

    const validPatches: Array<{ rowOffset: number; itemCode?: string; patch: Partial<Line> }> = []
    const rejected: string[] = []

    dataRows.forEach((cells, dataIdx) => {
      const patch: Partial<Line> = {}
      let itemCode = ''
      let invalidReason = ''

      fields.forEach((field, colIdx) => {
        const value = String(cells[colIdx] || '').trim()
        if (!value) return

        if (field === 'itemcode') {
          const matchedItem = itemMap.get(value.toUpperCase())
          if (!matchedItem) {
            invalidReason = `invalid item ${value}`
            return
          }
          itemCode = String(matchedItem.data?.ItemCode ?? matchedItem.ItemCode ?? value).trim()
          patch.itemcode = itemCode
          patch.itemdescription = String(matchedItem.data?.ItemName ?? matchedItem.ItemName ?? '')
          patch.uomcode = String(matchedItem.data?.PurchaseUnit ?? matchedItem.PurchaseUnit ?? matchedItem.data?.InventoryUOM ?? matchedItem.InventoryUOM ?? '')
        }

        if (field === 'quantity') {
          const quantity = parsePositiveNumber(value)
          if (quantity === null) {
            invalidReason = `invalid quantity ${value || '(blank)'}`
            return
          }
          patch.quantity = quantity
        }
      })

      if (invalidReason) {
        rejected.push(`row ${dataIdx + 1}: ${invalidReason}`)
        return
      }

      if (Object.keys(patch).length > 0) {
        validPatches.push({ rowOffset: dataIdx, itemCode, patch })
      }
    })

    if (validPatches.length === 0) {
      dispatch(showSnackbar({
        message: rejected.length ? `Paste rejected: ${rejected.slice(0, 2).join('; ')}` : 'No valid values found to paste',
        severity: 'error'
      }))
      return true
    }

    const requiredRows = startRowIdx + Math.max(...validPatches.map((row) => row.rowOffset)) + 1
    setLines((prevLines) => {
      const updated = [...prevLines]
      while (updated.length < requiredRows) {
        updated.push(createEmptyLine())
      }
      validPatches.forEach(({ rowOffset, patch }) => {
        const targetIndex = startRowIdx + rowOffset
        updated[targetIndex] = {
          ...updated[targetIndex],
          itemtype: requisitionType,
          deliverydate: updated[targetIndex]?.deliverydate || requiredDate || '',
          ...patch
        }
      })
      return updated
    })

    const itemRowsToEnrich = validPatches
      .filter((row) => !!row.itemCode)
      .map((row) => ({ rowIdx: startRowIdx + row.rowOffset, itemCode: String(row.itemCode) }))

    if (itemRowsToEnrich.length > 0) {
      void loadItemAuxiliaries(itemRowsToEnrich.map((row) => row.itemCode))
      setTimeout(() => {
        void (async () => {
          const batchSize = 8
          for (let i = 0; i < itemRowsToEnrich.length; i += batchSize) {
            const batch = itemRowsToEnrich.slice(i, i + batchSize)
            await Promise.all(batch.map((row) => autoFillItemDetails(row.rowIdx, row.itemCode, cachedLocalItems)))
          }
        })()
      }, 100)
    }

    const rejectNote = rejected.length ? ` Skipped ${rejected.length}: ${rejected.slice(0, 2).join('; ')}` : ''
    dispatch(showSnackbar({
      message: `Pasted ${validPatches.length} row(s).${rejectNote}`,
      severity: rejected.length ? 'warning' : 'success'
    }))

    return true
  }, [autoFillItemDetails, createEmptyLine, dispatch, requisitionType, requiredDate, viewMode, loadItemAuxiliaries])

  const handlePasteTable = React.useCallback(() => {
    console.log('Paste button clicked, focusedCellPosition:', focusedCellPosition, 'lastClickedRef:', lastClickedCellRef.current)

    if (viewMode !== 'create') {
      dispatch(showSnackbar({ message: 'Paste only available in Create mode', severity: 'warning' }))
      return
    }

    if (!gridRef.current?.api) {
      dispatch(showSnackbar({ message: 'Grid not ready', severity: 'warning' }))
      return
    }

    // Create a temporary textarea to capture clipboard paste
    const textarea = document.createElement('textarea')
    textarea.style.position = 'fixed'
    textarea.style.top = '0'
    textarea.style.left = '0'
    textarea.style.width = '1px'
    textarea.style.height = '1px'
    textarea.style.padding = '0'
    textarea.style.border = 'none'
    textarea.style.outline = 'none'
    textarea.style.boxShadow = 'none'
    textarea.style.background = 'transparent'
    textarea.value = ''
    document.body.appendChild(textarea)
    textarea.focus()

    // Add one-time paste event listener
    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault()
      const clipboardText = e.clipboardData?.getData('text') || ''
      document.body.removeChild(textarea)
      document.removeEventListener('paste', handlePaste)

      if (!clipboardText.trim()) {
        dispatch(showSnackbar({ message: 'Clipboard is empty. Please copy data first.', severity: 'warning' }))
        return
      }

      processClipboardData(clipboardText)
    }

    document.addEventListener('paste', handlePaste, { once: true })

    // Show instructions to user
    dispatch(showSnackbar({
      message: 'Press Ctrl+V now to paste data from clipboard',
      severity: 'info'
    }))

    // Auto-focus and trigger paste if possible
    setTimeout(() => {
      textarea.select()
    }, 100)

    // Cleanup if user doesn't paste within 10 seconds
    setTimeout(() => {
      if (document.body.contains(textarea)) {
        document.body.removeChild(textarea)
        document.removeEventListener('paste', handlePaste)
      }
    }, 10000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, dispatch])

  // Separate function to process clipboard data
  const processClipboardData = React.useCallback((clipboardText: string) => {
    if (!gridRef.current?.api) return

    try {
      const api = gridRef.current.api
      const focusedCell = api.getFocusedCell?.()
      const activeColId = String(focusedCell?.column?.getColId?.() || focusedCellPosition?.colId || '')
      const quickPasteColumnCount = Math.max(
        ...clipboardText
          .split(/\r?\n/)
          .filter((line: string) => line.trim())
          .map((line: string) => line.split('\t').length),
        0
      )
      if ((activeColId === 'itemcode' || activeColId === 'quantity') && quickPasteColumnCount <= 2) {
        const quickStartIndex = Number(
          focusedCell?.rowIndex ??
          lastClickedCellRef.current ??
          focusedCellPosition?.rowIndex ??
          0
        )
        void handleExcelQuickPaste(clipboardText, Math.max(0, quickStartIndex), activeColId)
          .then((handled) => {
            if (!handled) {
              dispatch(showSnackbar({
                message: 'For quick paste, copy only Item Code, Quantity, or Item Code + Quantity columns.',
                severity: 'warning'
              }))
            }
          })
        return
      }

      const selectedCells = api.getCellRanges()
      const selectedRows = api.getSelectedRows()

      // Parse clipboard data
      const lines = clipboardText.split(/\r?\n/).filter((line: string) => line.trim())

      console.log('Processing clipboard data:', { linesCount: lines.length, firstLine: lines[0] })

      if (lines.length === 0) {
        dispatch(showSnackbar({ message: 'No data found in clipboard', severity: 'warning' }))
        return
      }

      // Column names (the template's, the grid's and common variants): utils/prLineImport.
      const hasHeaders = lines.length > 0 && isHeaderRow(lines[0].split('\t'))

      console.log('Has headers:', hasHeaders)

      let startIndex = 0
      let columnFields: string[] = []

      // Get actual row count from current lines state
      const apiRowCount = api.getDisplayedRowCount() || 0

      // SIMPLIFIED LOGIC: Default to REPLACE mode when grid has rows
      // Only use APPEND mode when grid is completely empty
      let insertMode: 'replace' | 'append' = apiRowCount > 0 ? 'replace' : 'append'

      console.log('=== PASTE DECISION ===')
      console.log('Clipboard row count:', lines.length)
      console.log('Current grid row count:', apiRowCount)
      console.log('Initial mode:', insertMode)
      console.log('lastClickedCellRef:', lastClickedCellRef.current)
      console.log('focusedCellPosition:', focusedCellPosition)

      // Scenario 1: Cell range selected (highest priority)
      if (selectedCells && selectedCells.length > 0) {
        const range = selectedCells[0]
        const startRowNode = range?.rowRangePositions?.[0]?.rowIndex ?? 0
        startIndex = Math.min(startRowNode, apiRowCount > 0 ? apiRowCount - 1 : 0)
        insertMode = 'replace'
        console.log('Case 1: Cell range selected, replace at:', startIndex)

        if (range?.columns) {
          // Filter OUT the index column from paste operations
          columnFields = range.columns
            .map((col: any) => col.getColId())
            .filter((field: string) => field !== 'index' && field !== '')
        }
      }
      // Scenario 2: Rows selected (checkbox selection)
      else if (selectedRows && selectedRows.length > 0) {
        // Get the first selected row using the row node's ID instead of itemcode
        const firstSelectedRowId = selectedRows[0].id ?? selectedRows[0].itemcode
        const firstSelectedNode = api.getRowNode(String(firstSelectedRowId || ''))
        const nodeIndex = firstSelectedNode ? (firstSelectedNode.rowIndex ?? 0) : 0
        startIndex = Math.min(nodeIndex, apiRowCount > 0 ? apiRowCount - 1 : 0)
        insertMode = 'replace'
        console.log('Case 2: Rows selected, replace at:', startIndex, 'Row ID:', firstSelectedRowId)
      }
      // Scenario 3: Use last clicked cell position (most common case)
      else if (lastClickedCellRef.current !== null && lastClickedCellRef.current >= 0) {
        startIndex = Math.min(lastClickedCellRef.current, apiRowCount > 0 ? apiRowCount - 1 : 0)
        // ALWAYS use replace mode when we have rows and clicked on a valid position
        insertMode = 'replace'
        console.log('Case 3: Last clicked cell at index', lastClickedCellRef.current, '→ REPLACE at', startIndex)
      }
      // Scenario 4: Fallback - try to get currently focused cell from state
      else if (focusedCellPosition && focusedCellPosition.rowIndex !== null) {
        startIndex = Math.min(focusedCellPosition.rowIndex, apiRowCount > 0 ? apiRowCount - 1 : 0)
        insertMode = 'replace'
        console.log('Case 4: Using focusedCellPosition from state', focusedCellPosition.rowIndex, '→ REPLACE at', startIndex)
      }
      // Scenario 5: Fallback - try to get focused cell from API
      else {
        const focusedCell = api.getFocusedCell()
        if (focusedCell && focusedCell.rowIndex !== null && focusedCell.rowIndex >= 0) {
          startIndex = Math.min(focusedCell.rowIndex, apiRowCount > 0 ? apiRowCount - 1 : 0)
          insertMode = 'replace'
          console.log('Case 5: API focused cell', focusedCell.rowIndex, '→ REPLACE at', startIndex)
        } else {
          // No focus or click detected - start from beginning if grid has rows
          if (apiRowCount > 0) {
            startIndex = 0
            insertMode = 'replace'
            console.log('Case 6: No focus detected, grid has', apiRowCount, 'rows → REPLACE from row 0')
          } else {
            startIndex = 0
            insertMode = 'append'
            console.log('Case 6: Grid is empty → APPEND')
          }
        }
      }

      console.log('Final decision: Mode =', insertMode, ', StartIndex =', startIndex)
      console.log('==================')

      console.log('Selection:', { hasCellRange: !!selectedCells?.length, hasRowSelection: selectedRows?.length, focusedCell: focusedCellPosition, lastClicked: lastClickedCellRef.current, startIndex, insertMode, columnFields })

      // FORCE REPLACE MODE if we have rows and no cell selection
      // This prevents appending when user just pastes without clicking
      if (!selectedCells?.length && !selectedRows?.length && apiRowCount > 0) {
        insertMode = 'replace'
        startIndex = 0
        console.log('OVERRODE to REPLACE mode: Grid has rows but no selection, pasting from row 0')
      }

      // Parse data
      let dataToPaste: any[] = []

      if (hasHeaders) {
        const headers = lines[0].split('\t').map((h: string) => h.trim().toLowerCase())
        columnFields = headers.map((h: string) => fieldForHeader(h) || '').filter((f: string) => f)

        console.log('Headers found:', headers)
        console.log('Mapped fields:', columnFields)

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split('\t')
          const rowData: any = {}

          headers.forEach((header: string, idx: number) => {
            const fieldName = fieldForHeader(header)
            let value = values[idx]?.trim() || ''

            if (!value || !fieldName) return

            if (fieldName === 'quantity' || fieldName === 'unitprice') {
              const numValue = parseFloat(value.replace(/[,$]/g, ''))
              if (!isNaN(numValue)) {
                rowData[fieldName] = numValue
              }
            } else {
              rowData[fieldName] = value
            }
          })

          if (Object.keys(rowData).length > 0) {
            dataToPaste.push(rowData)
          }
        }
      } else {
        const numCols = lines[0].split('\t').length

        if (columnFields.length === 0) {
          columnFields = ['itemcode', 'itemdescription', 'quantity', 'uomcode', 'unitprice', 'projectcode', 'dim1', 'dim2', 'dim3', 'dim4', 'dim5', 'vendorcode', 'warehouse', 'deliverydate', 'u_item_details']
        }

        const fieldsToUse = columnFields.slice(0, numCols)

        console.log('No headers, using default fields:', fieldsToUse)

        for (let i = 0; i < lines.length; i++) {
          const values = lines[i].split('\t')
          const rowData: any = {}

          fieldsToUse.forEach((field: string, idx: number) => {
            let value = values[idx]?.trim() || ''
            if (!value) return

            if (field === 'quantity' || field === 'unitprice') {
              const numValue = parseFloat(value.replace(/[,$]/g, ''))
              if (!isNaN(numValue)) {
                rowData[field] = numValue
              }
            } else {
              rowData[field] = value
            }
          })

          if (Object.keys(rowData).length > 0) {
            dataToPaste.push(rowData)
          }
        }
      }

      // Delivery dates as this PC shows them (30-11-26, 11/30/2026, 30-Nov-2026…) → YYYY-MM-DD, the
      // column's day/month order first. An unreadable one is not pasted.
      let dateNote = ''
      const pastedDates = dataToPaste.map((r: any) => r.deliverydate).filter((d: unknown) => d !== undefined)
      if (pastedDates.length) {
        const columnOrder = inferDateOrder(pastedDates)
        const dateOrder = columnOrder || localeDateOrder()
        const unread: string[] = []
        dataToPaste.forEach((r: any) => {
          if (r.deliverydate === undefined) return
          const iso = parseDate(r.deliverydate, { order: dateOrder })
          if (iso && iso !== 'invalid') r.deliverydate = iso
          else { unread.push(String(r.deliverydate)); delete r.deliverydate }
        })
        const example = columnOrder ? undefined : pastedDates.find((d: unknown) => hasAmbiguousDates([d]))
        if (unread.length) dateNote = `. ${unread.length} delivery date${unread.length === 1 ? '' : 's'} could not be read (e.g. "${unread[0]}") and ${unread.length === 1 ? 'was' : 'were'} not pasted`
        else if (example) dateNote = `. Dates read as ${dateOrder === 'MDY' ? 'month/day/year' : 'day/month/year'}: ${example} = ${displayDate(parseDate(example, { order: dateOrder }))}`
      }

      console.log('Data to paste:', dataToPaste)

      // Auto-show columns that have data
      const fieldsWithData = new Set<string>()
      dataToPaste.forEach((row: any) => {
        Object.keys(row).forEach((field) => {
          const value = row[field]
          if (value !== null && value !== undefined && String(value).trim() !== '') {
            fieldsWithData.add(field)
          }
        })
      })

      // Update column visibility for fields with data
      const fieldsToShow = ['vendorcode', 'warehouse', 'deliverydate', 'projectcode', 'dim1', 'dim2', 'dim3', 'dim4', 'dim5', 'u_item_details']

      setColumnVisibilityModel((prevModel: Record<string, boolean>) => {
        let visibilityChanged = false
        const newVisibilityModel = { ...prevModel }

        fieldsToShow.forEach((field) => {
          if (fieldsWithData.has(field) && prevModel[field] === false) {
            newVisibilityModel[field] = true
            visibilityChanged = true
          }
        })

        if (visibilityChanged) {
          console.log('Auto-showing columns:', Object.keys(newVisibilityModel).filter(k => newVisibilityModel[k] && !prevModel[k]))
          try {
            localStorage.setItem('pr_grid_view', JSON.stringify(newVisibilityModel))
          } catch (e) {
            console.log('Failed to save column visibility:', e)
          }
          // Force grid to refresh column definitions
          setTimeout(() => {
            if (gridRef.current?.api) {
              gridRef.current.api.refreshClientSideRowModel?.('filter')
            }
          }, 100)
          return newVisibilityModel
        }

        return prevModel
      })

      if (dataToPaste.length === 0) {
        dispatch(showSnackbar({ message: 'No valid data found. Ensure first row has headers like "Item Code", "Description", etc.', severity: 'warning' }))
        return
      }

      // Clear sort model BEFORE paste to prevent auto-reordering during update
      try {
        api.setGridOption('sortModel', [])
        api.setGridOption('enableSort', false)
        console.log('Disabled sorting before paste operation')
      } catch (e) {
        console.log('Failed to disable sorting:', e)
      }

      // Apply paste
      let updatedCount = 0

      if (selectedCells && selectedCells.length > 0 && columnFields.length > 0) {
        console.log('Pasting into cell range...')
        setLines((prevLines: any[]) => {
          const updated = [...prevLines]
          const maxRows = Math.min(dataToPaste.length, updated.length - startIndex)

          for (let i = 0; i < maxRows; i++) {
            const rowIndex = startIndex + i
            if (rowIndex < updated.length) {
              columnFields.forEach((field: string) => {
                if (dataToPaste[i][field] !== undefined) {
                  updated[rowIndex] = { ...updated[rowIndex], [field]: dataToPaste[i][field] }
                }
              })
              updatedCount++
            }
          }

          console.log('Updated lines in cell range:', updatedCount)
          return updated
        })

        dispatch(showSnackbar({
          message: `Pasted into ${updatedCount} rows × ${columnFields.length} columns${dateNote}`,
          severity: 'success'
        }))
      } else if (selectedRows && selectedRows.length > 0) {
        console.log('Replacing selected rows...')
        setLines((prevLines: any[]) => {
          const updated = [...prevLines]
          const maxRows = Math.min(dataToPaste.length, selectedRows.length)

          for (let i = 0; i < maxRows; i++) {
            // Use the row ID (not itemcode) to find the row node
            const selectedRowId = selectedRows[i].id ?? selectedRows[i].itemcode
            const selectedNode = api.getRowNode(String(selectedRowId || ''))
            if (selectedNode) {
              const rowIndex = selectedNode.rowIndex ?? 0
              updated[rowIndex] = { ...updated[rowIndex], ...dataToPaste[i] }
              updatedCount++
            }
          }

          console.log('Replaced rows:', updatedCount)
          return updated
        })

        dispatch(showSnackbar({
          message: `Replaced ${updatedCount} rows${dateNote}`,
          severity: 'success'
        }))
      } else if (insertMode === 'replace') {
        // Replace existing rows starting from focused cell position
        console.log('***** EXECUTING REPLACE MODE *****')
        console.log('Replacing rows starting from position', startIndex, 'with', dataToPaste.length, 'rows')

        setLines((prevLines: any[]) => {
          const updated = [...prevLines]

          // Ensure startIndex is valid
          const validStartIndex = Math.max(0, Math.min(startIndex, updated.length - 1))

          // Calculate how many rows we can replace
          const maxRows = Math.min(dataToPaste.length, updated.length - validStartIndex)

          console.log('Replace details:', {
            totalRows: updated.length,
            originalStartIndex: startIndex,
            validStartIndex,
            dataToPaste: dataToPaste.length,
            maxRows,
            willUpdate: Array.from({ length: maxRows }, (_, i) => validStartIndex + i)
          })

          for (let i = 0; i < maxRows; i++) {
            const rowIndex = validStartIndex + i
            if (rowIndex < updated.length) {
              updated[rowIndex] = { ...updated[rowIndex], ...dataToPaste[i] }
              updatedCount++
            }
          }

          console.log('Replaced rows starting from position', validStartIndex, ':', updatedCount)
          return updated
        })

        dispatch(showSnackbar({
          message: `Updated ${updatedCount} rows starting from row ${startIndex + 1}${dateNote}`,
          severity: 'success'
        }))

        // If no rows were replaced in replace mode, this is an error
        if (updatedCount === 0) {
          console.error('ERROR: Replace mode was selected but no rows were updated!')
          console.error('This indicates a bug in the paste logic.')
          dispatch(showSnackbar({
            message: 'Warning: Paste failed to replace any rows. Please try clicking on a specific cell before pasting.',
            severity: 'warning'
          }))
        }

        // Auto-fill item details and validate ALL pasted rows
        if (dataToPaste.length > 0) {
          setTimeout(async () => {
            console.log('Starting validation for', dataToPaste.length, 'rows starting from index', startIndex)
            void loadItemAuxiliaries(dataToPaste.map((row: any) => String(row?.itemcode || '')))

            // Validate and auto-fill in batches to avoid freezing
            const batchSize = 5
            for (let i = 0; i < dataToPaste.length; i += batchSize) {
              const batch = dataToPaste.slice(i, i + batchSize)

              await Promise.all(batch.map(async (row: any, index: number) => {
                const rowIndex = startIndex + i + index

                // 1. Validate quantity
                if (row.quantity !== undefined && row.quantity !== null) {
                  const qty = Number(row.quantity)
                  if (isNaN(qty) || qty <= 0) {
                    console.warn(`Row ${rowIndex + 1}: Invalid quantity ${row.quantity}, setting to 1`)
                    updateLine(rowIndex, { quantity: 1 })
                  }
                } else {
                  // Default quantity to 1 if not provided
                  updateLine(rowIndex, { quantity: 1 })
                }

                // 2. Validate unit price
                if (row.unitprice !== undefined && row.unitprice !== null) {
                  const price = Number(row.unitprice)
                  if (isNaN(price) || price < 0) {
                    console.warn(`Row ${rowIndex + 1}: Invalid unit price ${row.unitprice}, setting to 0`)
                    updateLine(rowIndex, { unitprice: 0 })
                  }
                }

                // 3. Fetch item details from database if itemcode provided
                if (row.itemcode && String(row.itemcode).trim()) {
                  console.log(`Fetching item details for row ${rowIndex + 1}, itemcode: ${row.itemcode}`)
                  await autoFillItemDetails(rowIndex, String(row.itemcode).trim())
                }
              }))

              // Small delay between batches
              await new Promise(resolve => setTimeout(resolve, 100))
            }

            console.log('Validation complete for', dataToPaste.length, 'rows')
          }, 300)
        }

        // Navigate to the page where we updated and refresh grid
        setTimeout(() => {
          if (gridRef.current?.api) {
            try {
              const paginationProxy = gridRef.current.api.paginationProxy
              if (paginationProxy) {
                const pageSize = paginationProxy.paginationPageSize
                const targetPage = Math.floor(startIndex / pageSize)
                paginationProxy.goToPage(targetPage)
                console.log('Navigated to page', targetPage)
              }
              // Force grid refresh
              gridRef.current.api.redrawRows()
            } catch (e) {
              console.log('Navigation failed:', e)
            }
          }
        }, 150)
      } else {
        // Append to end
        console.log('***** EXECUTING APPEND MODE *****')
        console.log('Adding new rows at the end. Current rows:', lines.length, 'Adding:', dataToPaste.length)

        // Create all new rows with proper structure
        const newLinesToAdd = dataToPaste.map((row: any, idx: number) => ({
          id: `${uniqueIdCounter.current}-${idx}`,
          itemtype: 'Inventory',
          quantity: row.quantity || 1,
          unitprice: row.unitprice || 0,
          accountcode: '',
          itemcode: row.itemcode || '',
          itemdescription: row.itemdescription || '',
          u_item_details: row.u_item_details || row.lineremarks || '',
          uomcode: row.uomcode || '',
          projectcode: row.projectcode || '',
          dim1: row.dim1 || '',
          dim2: row.dim2 || '',
          dim3: row.dim3 || '',
          dim4: row.dim4 || '',
          dim5: row.dim5 || '',
          vendorcode: row.vendorcode || '',
          warehouse: row.warehouse || '',
          deliverydate: row.deliverydate || ''
        }))

        // Increment counter for next batch
        uniqueIdCounter.current += dataToPaste.length

        console.log('New lines to add:', newLinesToAdd.length, 'They will be numbered starting from', lines.length + 1)

        // Capture the start index BEFORE state update
        const startRowIndex = lines.length

        // Update state in one batch - NO REFRESH NEEDED, React will handle it
        setLines((prevLines: any[]) => {
          const updated = [...prevLines, ...newLinesToAdd]
          console.log('Updated lines from', prevLines.length, 'to', updated.length)
          return updated
        })

        // Show success message
        dispatch(showSnackbar({
          message: `Added ${dataToPaste.length} new rows${dateNote}. Validating...`,
          severity: 'success'
        }))

        // Auto-fill item details and validate ALL pasted rows
        if (newLinesToAdd.length > 0) {
          setTimeout(async () => {
            console.log('Starting validation for', newLinesToAdd.length, 'appended rows starting from index', startRowIndex)
            void loadItemAuxiliaries(newLinesToAdd.map((row: any) => String(row?.itemcode || '')))

            // Validate and auto-fill in batches to avoid freezing
            const batchSize = 5
            for (let i = 0; i < newLinesToAdd.length; i += batchSize) {
              const batch = newLinesToAdd.slice(i, i + batchSize)

              await Promise.all(batch.map(async (row: any, index: number) => {
                const rowIndex = startRowIndex + i + index

                // 1. Validate quantity
                if (row.quantity !== undefined && row.quantity !== null) {
                  const qty = Number(row.quantity)
                  if (isNaN(qty) || qty <= 0) {
                    console.warn(`Row ${rowIndex + 1}: Invalid quantity ${row.quantity}, setting to 1`)
                    updateLine(rowIndex, { quantity: 1 })
                  }
                } else {
                  // Default quantity to 1 if not provided
                  updateLine(rowIndex, { quantity: 1 })
                }

                // 2. Validate unit price
                if (row.unitprice !== undefined && row.unitprice !== null) {
                  const price = Number(row.unitprice)
                  if (isNaN(price) || price < 0) {
                    console.warn(`Row ${rowIndex + 1}: Invalid unit price ${row.unitprice}, setting to 0`)
                    updateLine(rowIndex, { unitprice: 0 })
                  }
                }

                // 3. Fetch item details from database if itemcode provided
                if (row.itemcode && String(row.itemcode).trim()) {
                  console.log(`Fetching item details for row ${rowIndex + 1}, itemcode: ${row.itemcode}`)
                  await autoFillItemDetails(rowIndex, String(row.itemcode).trim())
                }
              }))

              // Small delay between batches
              await new Promise(resolve => setTimeout(resolve, 100))
            }

            console.log('Validation complete for', newLinesToAdd.length, 'appended rows')
          }, 300)
        }

        // Navigate to last page after a short delay (let React update first)
        setTimeout(() => {
          if (gridRef.current?.api) {
            try {
              const paginationProxy = gridRef.current.api.paginationProxy
              if (paginationProxy) {
                paginationProxy.goToLastPage()
                console.log('Navigated to last page')
                // Force grid refresh
                gridRef.current.api.redrawRows()
              }
            } catch (e) {
              console.log('Navigation failed:', e)
            }
          }
        }, 150)
      }

      // Clear selection
      api.clearRangeSelection()
      api.deselectAll()

      // Re-enable sorting after paste operation is complete
      try {
        api.setGridOption('sortModel', [])
        api.setGridOption('enableSort', true)
        console.log('Re-enabled sorting after paste operation')
      } catch (e) {
        console.log('Failed to re-enable sorting:', e)
      }
    } catch (error) {
      console.error('Paste error:', error)
      dispatch(showSnackbar({
        message: 'Paste failed. Please try again.',
        severity: 'error'
      }))
    }
  }, [dispatch, autoFillItemDetails, focusedCellPosition, handleExcelQuickPaste, setColumnVisibilityModel, updateLine, loadItemAuxiliaries])

  const handleItemPaste = React.useCallback(async (pastedText: string, startRowIdx: number, gridApi?: any) => {
    try {
      // Parse pasted text - could be single item or multiple rows from Excel
      const rawLines = pastedText.split(/[\r\n]+/).filter(l => l.trim().length > 0)
      const itemCodes: string[] = []

      for (const line of rawLines) {
        // Handle tab-separated values (Excel paste)
        const parts = line.split('\t')
        const itemCode = parts[0].trim()
        if (itemCode) {
          itemCodes.push(itemCode)
        }
      }

      if (itemCodes.length === 0) return

      console.log('Pasting items:', itemCodes)

      // Update the current cell with the first item code immediately
      if (itemCodes.length > 0 && gridApi) {
        try {
          await gridApi.setEditCellValue({ id: startRowIdx, field: 'itemcode', value: itemCodes[0] })
        } catch (err) {
          console.warn('Could not set edit cell value:', err)
        }
      }

      // Ensure we have enough rows
      const rowsNeeded = startRowIdx + itemCodes.length
      const currentRows = lines.length
      if (rowsNeeded > currentRows) {
        const newRows: Line[] = []
        for (let i = currentRows; i < rowsNeeded; i++) {
          newRows.push({
            ...newLineAllocation,
            itemtype: requisitionType,
            quantity: 1,
            unitprice: 0,
            accountcode: '',
            vendorcode: '',
            deliverydate: requiredDate || '',
          })
        }
        setLines(ls => [...ls, ...newRows])
        // Wait for state to update
        await new Promise(resolve => setTimeout(resolve, 100))
      }

      // Fetch local items ONCE and cache for all items to avoid repeated API calls
      const res = await apiService.listLocalItemMaster(undefined, undefined, requisitionType)
      const cachedLocalItems = (res as any)?.data || res || []
      console.log(`Fetched ${cachedLocalItems.length} items from cache for bulk paste`)

      // Process items in batches to improve performance while avoiding API overload
      const batchSize = 10
      for (let i = 0; i < itemCodes.length; i += batchSize) {
        const batch = itemCodes.slice(i, i + batchSize)
        const batchPromises = batch.map((itemCode, batchIdx) => {
          const rowIdx = startRowIdx + i + batchIdx
          return autoFillItemDetails(rowIdx, itemCode, cachedLocalItems)
        })
        // Process batch concurrently
        await Promise.all(batchPromises)
        // Small delay between batches
        if (i + batchSize < itemCodes.length) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }

      // Exit edit mode for the first row after a delay
      if (gridApi) {
        setTimeout(() => {
          try {
            gridApi.stopCellEditMode({ id: startRowIdx, field: 'itemcode' })
          } catch (err) {
            console.warn('Could not stop edit mode:', err)
          }
        }, 200)
      }

      dispatch(showSnackbar({ message: `Pasted ${itemCodes.length} item(s)`, severity: 'success' }))
    } catch (error: any) {
      console.error('Error handling item paste:', error)
      dispatch(showSnackbar({ message: error.message || 'Error pasting items', severity: 'error' }))
    }
  }, [lines, requisitionType, requiredDate, newLineAllocation, autoFillItemDetails, dispatch])
  

  // Lines (1-based, as numbered in the grid) that break a company PR rule.
  const lineRuleIssues = useMemo(() => {
    const filled = lines.map((line, i) => ({ line, lineNo: i + 1 })).filter(({ line }) => isFilledLine(line))
    return {
      unitPriceNotOne: companyRules.serviceUnitPriceMustBeOne
        ? filled.filter(({ line }) => isServiceLine(requisitionType, line) && Number(line.unitprice) !== 1).map(({ lineNo }) => lineNo)
        : [],
      missingProject: companyRules.requireLineProject && allocationType === 'CostCenter'
        ? filled.filter(({ line }) => !String(line.projectcode || '').trim()).map(({ lineNo }) => lineNo)
        : [],
    }
  }, [lines, companyRules, requisitionType, allocationType])

  const setServiceUnitPricesToOne = () => {
    setLines(ls => ls.map(l => isServiceLine(requisitionType, l) && isFilledLine(l) ? { ...l, unitprice: 1 } : l))
  }

  const canSubmit = useMemo(() => {
    if (viewMode !== 'create') return false
    const effective = lines.filter(l => !!l.itemcode || !!l.itemdescription)
    const hasAllocation = !!allocationCode || lines.some(l => !!l.projectcode || !!l.dim1)
    const hasValidApprovalFlow = !!matchedApprovalFlow && !approvalFlowError
    const companyRulesOk = lineRuleIssues.unitPriceNotOne.length === 0 && lineRuleIssues.missingProject.length === 0
    return hasAllocation && !!requiredDate && effective.length > 0 && effective.every(l => l.quantity > 0 && l.unitprice >= 0) && companyRulesOk && hasValidApprovalFlow
  }, [viewMode, requiredDate, lines, allocationCode, matchedApprovalFlow, approvalFlowError, lineRuleIssues])

  const filledLineCount = useMemo(() => lines.filter(isFilledLine).length, [lines])

  // What still stops Submit, in plain words: the checks of canSubmit and submit(). Display only, so the
  // Submit button no longer greys out without a reason; canSubmit and submit() keep their own rules.
  const submitProblems = useMemo(() => {
    if (viewMode !== 'create') return [] as string[]
    const problems: string[] = []
    const eff = lines.filter(isFilledLine)
    const hasAllocation = !!allocationCode || lines.some(l => !!l.projectcode || !!l.dim1)
    if (!hasAllocation) problems.push(allocationType === 'CostCenter' ? 'Choose a cost center.' : 'Choose a project.')
    if (!requiredDate) {
      problems.push('Enter the required date.')
    } else if (!isDocumentDate(fixTwoDigitYear(requiredDate))) {
      problems.push(`Required date ${toDDMMYYYY(requiredDate)} is not a valid date: enter the year with 4 digits.`)
    } else {
      const now = new Date()
      const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      if (fixTwoDigitYear(requiredDate) < todayISO) problems.push('Required date cannot be in the past.')
    }
    if (eff.length === 0) problems.push('Add at least one line with an item or a description.')
    eff.forEach((l) => {
      const n = lines.indexOf(l) + 1
      if (l.deliverydate && !isDocumentDate(fixTwoDigitYear(l.deliverydate))) problems.push(`Line ${n}: delivery date ${toDDMMYYYY(l.deliverydate)} is not a valid date.`)
      if (!(Number(l.quantity) > 0)) problems.push(`Line ${n}: quantity must be more than 0.`)
      if (!(Number(l.unitprice) >= 0)) problems.push(`Line ${n}: unit price cannot be negative.`)
    })
    if (lineRuleIssues.missingProject.length) problems.push(`Line ${lineRuleIssues.missingProject.join(', ')}: this company needs a project on every line of a cost center PR.`)
    if (lineRuleIssues.unitPriceNotOne.length) problems.push(`Line ${lineRuleIssues.unitPriceNotOne.join(', ')}: this company needs unit price 1 on Service lines.`)
    if (approvalFlowError) problems.push(approvalFlowError)
    else if (!matchedApprovalFlow && allocationCode) problems.push('No approval flow matches yet: check the type, facility and cycle.')
    else if (!matchedApprovalFlow && hasAllocation) problems.push(`Choose the ${allocationType === 'CostCenter' ? 'cost center' : 'project'} in Request details so the approval flow can be found.`)
    return problems
  }, [viewMode, lines, allocationCode, allocationType, requiredDate, lineRuleIssues, approvalFlowError, matchedApprovalFlow])

  // The header project's values that every line gets (see headerLineDefaults), as chips.
  const headerDefaultChips = useMemo(() => {
    const d: any = headerLineDefaults || {}
    return [
      d.dim1 ? `Division ${d.dim1}` : '',
      d.dim2 ? `Department ${d.dim2}` : '',
      d.dim4 ? `Auxiliary ${d.dim4}` : '',
      d.dim5 ? `Location ${d.dim5}` : '',
      d.warehouse ? `Warehouse ${d.warehouse}` : '',
    ].filter(Boolean)
  }, [headerLineDefaults])

  const prHeading = viewMode === 'find'
    ? 'Find a purchase requisition'
    : createdPrId
      ? (prNumber ? `Purchase requisition ${prNumber}` : 'Purchase requisition')
      : 'New purchase requisition'
  const saveStatusText = viewMode !== 'create'
    ? ''
    : createdPrId && prStatus === 'Draft'
      ? (lastSavedAt ? `Saved at ${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Changes save automatically')
      : !createdPrId ? 'Not saved yet' : ''

  const totalAmount = useMemo(() => {
    return lines
      .filter(l => l.itemcode || (l.itemdescription && l.itemdescription.trim().length > 0))
      .reduce((sum, l) => sum + prLineValues(requisitionType, l).amount, 0)
  }, [lines, requisitionType])

  const totalQuantity = useMemo(() => {
    return lines
      .filter(l => l.itemcode || (l.itemdescription && l.itemdescription.trim().length > 0))
      .reduce((sum, l) => sum + prLineValues(requisitionType, l).quantity, 0)
  }, [lines, requisitionType])

  // Service Qty / Service Unit Price show on a Service PR or when a line holds a service (62…) item.
  const hasServiceLines = useMemo(
    () => requisitionType === 'Service' || lines.some(l => isServiceLine(requisitionType, l)),
    [lines, requisitionType]
  )

  useEffect(() => {
    try { localStorage.setItem('pr_grid_view', JSON.stringify(columnVisibilityModel)) } catch {}
  }, [columnVisibilityModel])

  const rows = useMemo(() => lines.map((l, i) => ({ id: i, itemcode: String(l.itemcode || ''), itemdescription: String(l.itemdescription || ''), u_item_details: String(l.u_item_details || ''), quantity: (Number(l.quantity) > 0 ? String(Number(l.quantity)) : ''), uomcode: String(l.uomcode || ''), unitprice: (Number(l.unitprice) !== 0 ? String(Number(l.unitprice)) : ''), u_ser_qty: l.u_ser_qty ?? null, u_ser_unit_price: l.u_ser_unit_price ?? null, vendorcode: String(l.vendorcode || ''), warehouse: String(l.warehouse || ''), projectcode: String(l.projectcode || ''), dim1: String(l.dim1 || ''), dim2: String(l.dim2 || ''), dim3: String(l.dim3 || ''), dim4: String(l.dim4 || ''), dim5: String(l.dim5 || ''), deliverydate: String(l.deliverydate || '') })), [lines])

  const saveWidthsDebounceRef = useRef<number | null>(null)
  const saveWidthsDebounced = React.useCallback((next: Record<string, number>) => {
    if (saveWidthsDebounceRef.current) window.clearTimeout(saveWidthsDebounceRef.current)
    saveWidthsDebounceRef.current = window.setTimeout(() => {
      try { localStorage.setItem(WIDTHS_STORAGE_KEY, JSON.stringify({ version: WIDTHS_STORAGE_VERSION, widths: next })) } catch {}
    }, 250)
  }, [])



  const baseColumns: any[] = [
    { field: 'index', headerName: '#', width: 50, valueGetter: (p: any) => Number(p.id)+1, sortable: false, align: 'center', headerAlign: 'center' },
    { field: 'itemcode', headerName: 'Item Code', width: columnWidths.itemcode ?? 100, editable: true, align: 'center', headerAlign: 'center',
      renderEditCell: (params: any) => (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
          <input style={{ height: 28, width: '100%' }} value={String(params.value || '')} onChange={async (e) => {
            const code = (e.target as HTMLInputElement).value
            await params.api.setEditCellValue({ id: params.id, field: 'itemcode', value: code })
          }} onKeyDown={(e) => { if (e.key === 'Tab') { const v = (e.target as HTMLInputElement).value; setTimeout(() => { void resolveOnEdit('itemcode', v, params) }, 0) } }} onPaste={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const text = (e.clipboardData as any).getData('text');
            const rowIdx = Number(params.id);
            setTimeout(() => { void handleItemPaste(text, rowIdx, params.api) }, 0);
          }} />
          <IconButton size="small" onClick={() => {
            setApplyToRowId(Number(params.id));
            setInlinePickerType('item');
            setInlineQuery('');
            inlineOpenedOnceRef.current = false
            setInlinePickerOpen(true);
          }} aria-label="Pick Item" sx={{ bgcolor: '#F7D14A', width: 18, height: 18, borderRadius: '50%', '&:hover': { bgcolor: '#F9E27A' } }}>
          </IconButton>
        </Stack>
      )
    },
    { field: 'itemdescription', headerName: 'Item Description', width: columnWidths.itemdescription ?? 200, editable: true, align: 'center', headerAlign: 'center' },
    { field: 'u_item_details', headerName: 'Line Remarks', width: columnWidths.u_item_details ?? 240, editable: true, align: 'left', headerAlign: 'left' },
    { field: 'quantity', headerName: 'Req. Qty', width: columnWidths.quantity ?? 80, editable: true, type: 'number', align: 'center', headerAlign: 'center', valueFormatter: (p: any) => {
      const api: any = (p as any)?.api
      const row: any = api && typeof api.getRow === 'function' ? api.getRow(p.id) : undefined
      const hasRef = String(row?.itemcode || '').trim().length > 0 || String(row?.itemdescription || '').trim().length > 0
      const v = Number(p?.value ?? 0)
      return hasRef && v > 0 ? v : ''
    } },
    { field: 'uomcode', headerName: 'UoM', width: columnWidths.uomcode ?? 60, editable: true, align: 'center', headerAlign: 'center' },
    { field: 'unitprice', headerName: 'Unit Price', width: columnWidths.unitprice ?? 90, editable: true, type: 'number', align: 'center', headerAlign: 'center', valueFormatter: (p: any) => {
      const api: any = (p as any)?.api
      const row: any = api && typeof api.getRow === 'function' ? api.getRow(p.id) : undefined
      const hasRef = String(row?.itemcode || '').trim().length > 0 || String(row?.itemdescription || '').trim().length > 0
      const v = Number(p?.value ?? 0)
      return hasRef && v > 0 ? v : ''
    } },
    { field: 'linetotal', headerName: 'Line Total', width: 100, editable: false, align: 'center', headerAlign: 'center',
      valueGetter: (params: any) => {
        const row = params.row
        return prLineValues(requisitionType, row).amount
      },
      valueFormatter: (p: any) => {
        const v = Number(p.value || 0)
        return v > 0 ? v.toFixed(2) : ''
      },
      renderCell: (params: any) => {
        const val = Number(params.value || 0)
        return val > 0 ? (
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', width: '100%', textAlign: 'center' }}>
            {val.toFixed(2)}
          </Typography>
        ) : null
      }
    },
    { field: 'deliverydate', headerName: 'Delivery Date', width: columnWidths.deliverydate ?? 105, editable: true, align: 'center', headerAlign: 'center',
      renderCell: (params: any) => {
        const val = String(params.value || '')
        const isAuto = !!requiredDate && val === requiredDate
        return (<Typography variant="body2" sx={{ fontStyle: isAuto ? 'italic' : 'normal', color: isAuto ? 'text.secondary' : 'inherit', textAlign: 'center', width: '100%' }}>{toDDMMYYYY(val)}</Typography>)
      },
      renderEditCell: (params: any) => (
        <input
          type="date"
          value={String(params.value || '')}
          onChange={async (e) => {
            const iso = e.target.value || ''
            await params.api.setEditCellValue({ id: params.id, field: 'deliverydate', value: iso })
          }}
          style={{ height: 28, width: '100%' }}
        />
      )
    },
    { field: 'projectcode', headerName: 'Project', width: columnWidths.projectcode ?? 100, editable: true, align: 'center', headerAlign: 'center',
      renderEditCell: (params: any) => (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
          <input style={{ height: 28, width: '100%' }} value={String(params.value || '')} onChange={async (e) => {
            await params.api.setEditCellValue({ id: params.id, field: 'projectcode', value: (e.target as HTMLInputElement).value })
          }} onKeyDown={(e) => { if (e.key === 'Tab') { const v = (e.target as HTMLInputElement).value; setTimeout(() => { void resolveOnEdit('projectcode', v, params) }, 0) } }} onPaste={(e) => { const text = (e.clipboardData as any).getData('text'); setTimeout(() => { void resolveOnEdit('projectcode', text, params) }, 0) }} />
          <IconButton size="small" onClick={() => { setApplyToRowId(Number(params.id)); setInlinePickerType('project'); setInlineQuery(''); setInlineSelection([]); inlineOpenedOnceRef.current = false; setInlinePickerOpen(true); }} aria-label="Pick Project" sx={{ bgcolor: '#F7D14A', width: 18, height: 18, borderRadius: '50%', '&:hover': { bgcolor: '#F9E27A' } }}>
          </IconButton>
        </Stack>
      )
    },
    { field: 'dim1', headerName: 'Division', width: columnWidths.dim1 ?? 85, editable: true, align: 'center', headerAlign: 'center', renderEditCell: (params: any) => (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
        <input style={{ height: 28, width: '100%' }} value={String(params.value || '')} onChange={async (e) => { await params.api.setEditCellValue({ id: params.id, field: 'dim1', value: (e.target as HTMLInputElement).value }) }} onKeyDown={(e) => { if (e.key === 'Tab') { const v = (e.target as HTMLInputElement).value; setTimeout(() => { void resolveOnEdit('dim1', v, params) }, 0) } }} onPaste={(e) => { const text = (e.clipboardData as any).getData('text'); setTimeout(() => { void resolveOnEdit('dim1', text, params) }, 0) }} />
        <IconButton size="small" onClick={() => { setApplyToRowId(Number(params.id)); setInlinePickerType('dim1'); setInlineQuery(''); setInlineSelection([]); inlineOpenedOnceRef.current = false; setInlinePickerOpen(true); }} aria-label="Pick Division" sx={{ bgcolor: '#F7D14A', width: 18, height: 18, borderRadius: '50%', '&:hover': { bgcolor: '#F9E27A' } }}>
        </IconButton>
      </Stack>
    ) },
    { field: 'dim2', headerName: 'Department', width: columnWidths.dim2 ?? 90, editable: true, align: 'center', headerAlign: 'center', renderEditCell: (params: any) => (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
        <input style={{ height: 28, width: '100%' }} value={String(params.value || '')} onChange={async (e) => { await params.api.setEditCellValue({ id: params.id, field: 'dim2', value: (e.target as HTMLInputElement).value }) }} onKeyDown={(e) => { if (e.key === 'Tab') { const v = (e.target as HTMLInputElement).value; setTimeout(() => { void resolveOnEdit('dim2', v, params) }, 0) } }} onPaste={(e) => { const text = (e.clipboardData as any).getData('text'); setTimeout(() => { void resolveOnEdit('dim2', text, params) }, 0) }} />
        <IconButton size="small" onClick={() => { setApplyToRowId(Number(params.id)); setInlinePickerType('dim2'); setInlineQuery(''); setInlineSelection([]); inlineOpenedOnceRef.current = false; setInlinePickerOpen(true); }} aria-label="Pick Department" sx={{ bgcolor: '#F7D14A', width: 18, height: 18, borderRadius: '50%', '&:hover': { bgcolor: '#F9E27A' } }}>
        </IconButton>
      </Stack>
    ) },
    { field: 'dim3', headerName: 'WBS', width: columnWidths.dim3 ?? 90, editable: true, align: 'center', headerAlign: 'center', renderEditCell: (params: any) => (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
        <input style={{ height: 28, width: '100%' }} value={String(params.value || '')} onChange={async (e) => { await params.api.setEditCellValue({ id: params.id, field: 'dim3', value: (e.target as HTMLInputElement).value }) }} onKeyDown={(e) => { if (e.key === 'Tab') { const v = (e.target as HTMLInputElement).value; setTimeout(() => { void resolveOnEdit('dim3', v, params) }, 0) } }} onPaste={(e) => { const text = (e.clipboardData as any).getData('text'); setTimeout(() => { void resolveOnEdit('dim3', text, params) }, 0) }} />
        <IconButton size="small" onClick={() => { setApplyToRowId(Number(params.id)); setInlinePickerType('dim3'); setInlineQuery(''); setInlineSelection([]); inlineOpenedOnceRef.current = false; setInlinePickerOpen(true); }} aria-label="Pick Cost Code (WBS)" sx={{ bgcolor: '#F7D14A', width: 18, height: 18, borderRadius: '50%', '&:hover': { bgcolor: '#F9E27A' } }}>
        </IconButton>
      </Stack>
    ) },
    { field: 'dim4', headerName: 'Auxiliary', width: columnWidths.dim4 ?? 80, editable: true, align: 'center', headerAlign: 'center', renderEditCell: (params: any) => (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
        <input style={{ height: 28, width: '100%' }} value={String(params.value || '')} onChange={async (e) => { await params.api.setEditCellValue({ id: params.id, field: 'dim4', value: (e.target as HTMLInputElement).value }) }} onKeyDown={(e) => { if (e.key === 'Tab') { const v = (e.target as HTMLInputElement).value; setTimeout(() => { void resolveOnEdit('dim4', v, params) }, 0) } }} onPaste={(e) => { const text = (e.clipboardData as any).getData('text'); setTimeout(() => { void resolveOnEdit('dim4', text, params) }, 0) }} />
        <IconButton size="small" onClick={() => { setApplyToRowId(Number(params.id)); setInlinePickerType('dim4'); setInlineQuery(''); setInlineSelection([]); inlineOpenedOnceRef.current = false; setInlinePickerOpen(true); }} aria-label="Pick Auxiliary" sx={{ bgcolor: '#F7D14A', width: 18, height: 18, borderRadius: '50%', '&:hover': { bgcolor: '#F9E27A' } }}>
        </IconButton>
      </Stack>
    ) },
    { field: 'dim5', headerName: 'LOC', width: columnWidths.dim5 ?? 75, editable: true, align: 'center', headerAlign: 'center', renderEditCell: (params: any) => (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
        <input style={{ height: 28, width: '100%' }} value={String(params.value || '')} onChange={async (e) => { await params.api.setEditCellValue({ id: params.id, field: 'dim5', value: (e.target as HTMLInputElement).value }) }} onKeyDown={(e) => { if (e.key === 'Tab') { const v = (e.target as HTMLInputElement).value; setTimeout(() => { void resolveOnEdit('dim5', v, params) }, 0) } }} onPaste={(e) => { const text = (e.clipboardData as any).getData('text'); setTimeout(() => { void resolveOnEdit('dim5', text, params) }, 0) }} />
        <IconButton size="small" onClick={() => { setApplyToRowId(Number(params.id)); setInlinePickerType('dim5'); setInlineQuery(''); setInlineSelection([]); inlineOpenedOnceRef.current = false; setInlinePickerOpen(true); }} aria-label="Pick LOC" sx={{ bgcolor: '#F7D14A', width: 18, height: 18, borderRadius: '50%', '&:hover': { bgcolor: '#F9E27A' } }}>
        </IconButton>
      </Stack>
    ) },
    { field: 'vendorcode', headerName: 'Vendor', width: columnWidths.vendorcode ?? 110, editable: true, align: 'center', headerAlign: 'center',
      renderEditCell: (params: any) => (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
          <input style={{ height: 28, width: '100%' }} value={String(params.value || '')} onChange={async (e) => {
            const code = (e.target as HTMLInputElement).value
            await params.api.setEditCellValue({ id: params.id, field: 'vendorcode', value: code })
          }} onKeyDown={(e) => { if (e.key === 'Tab') { const v = (e.target as HTMLInputElement).value; setTimeout(() => { void resolveOnEdit('vendorcode', v, params) }, 0) } }} onPaste={(e) => { const text = (e.clipboardData as any).getData('text'); setTimeout(() => { void resolveOnEdit('vendorcode', text, params) }, 0) }} />
          <IconButton size="small" onClick={() => {
            setApplyToRowId(Number(params.id));
            setInlinePickerType('vendor');
            setInlineQuery('');
            inlineOpenedOnceRef.current = false
            setInlinePickerOpen(true);
          }} aria-label="Pick Vendor" sx={{ bgcolor: '#F7D14A', width: 18, height: 18, borderRadius: '50%', '&:hover': { bgcolor: '#F9E27A' } }}>
          </IconButton>
        </Stack>
      )
    },
    { field: 'warehouse', headerName: 'Warehouse', width: columnWidths.warehouse ?? 90, editable: true, align: 'center', headerAlign: 'center',
      renderEditCell: (params: any) => (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
          <input style={{ height: 28, width: '100%' }} value={String(params.value || '')} onChange={async (e) => {
            const code = (e.target as HTMLInputElement).value
            await params.api.setEditCellValue({ id: params.id, field: 'warehouse', value: code })
          }} onKeyDown={(e) => { if (e.key === 'Tab') { const v = (e.target as HTMLInputElement).value; setTimeout(() => { void resolveOnEdit('warehouse', v, params) }, 0) } }} onPaste={(e) => { const text = (e.clipboardData as any).getData('text'); setTimeout(() => { void resolveOnEdit('warehouse', text, params) }, 0) }} />
          <IconButton size="small" onClick={() => {
            setApplyToRowId(Number(params.id));
            setInlinePickerType('warehouse');
            setInlineQuery('');
            inlineOpenedOnceRef.current = false
            setInlinePickerOpen(true);
          }} aria-label="Pick Warehouse" sx={{ bgcolor: '#F7D14A', width: 18, height: 18, borderRadius: '50%', '&:hover': { bgcolor: '#F9E27A' } }}>
          </IconButton>
        </Stack>
      )
    },

  ]

  // AG Grid Column Definitions
  const [agGridColumns, setAgGridColumns] = useState<any[]>([])

  useEffect(() => {
    const columns: any[] = [
      {
        field: 'index',
        headerName: '#',
        width: 50,
        minWidth: 40,
        editable: false,
        valueGetter: (params: any) => params.node.rowIndex + 1,
        pinned: 'left',
        lockPosition: true,
        lockPinned: true,
        suppressSizeToFit: false,
        resizable: true,
        suppressMovable: true,
        suppressCopyToClipboard: true,
        suppressPasteFromClipboard: true,
        suppressCellSelection: true,
        suppressMenu: true,
        cellStyle: { textAlign: 'center', fontWeight: '500' },
        headerClass: 'ag-header-center'
      },
      {
        field: 'itemcode',
        headerName: 'Item Code',
        width: columnWidths.itemcode ?? 120,
        editable: true,
        cellStyle: { textAlign: 'center', cursor: 'pointer' },
        headerClass: 'ag-header-center'
      },
      {
        field: 'itemdescription',
        headerName: 'Item Description',
        width: columnWidths.itemdescription ?? 200,
        editable: true,
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-center'
      },
      {
        field: 'u_item_details',
        headerName: 'Line Remarks',
        width: columnWidths.u_item_details ?? 240,
        editable: true,
        hide: columnVisibilityModel.u_item_details === false,
        cellStyle: { textAlign: 'left', whiteSpace: 'pre-wrap' },
        headerClass: 'ag-header-center'
      },
      {
        field: 'quantity',
        headerName: 'Req. Qty',
        width: columnWidths.quantity ?? 80,
        editable: true,
        type: 'numericColumn',
        valueFormatter: (params: any) => {
          const row = params.data
          const hasRef = String(row?.itemcode || '').trim().length > 0 || String(row?.itemdescription || '').trim().length > 0
          const v = Number(params?.value ?? 0)
          return hasRef && v > 0 ? v : ''
        },
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-center'
      },
      {
        field: 'uomcode',
        headerName: 'UoM',
        width: columnWidths.uomcode ?? 60,
        editable: true,
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-center'
      },
      {
        field: 'unitprice',
        headerName: 'Unit Price',
        width: columnWidths.unitprice ?? 90,
        editable: true,
        type: 'numericColumn',
        valueFormatter: (params: any) => {
          const row = params.data
          const hasRef = String(row?.itemcode || '').trim().length > 0 || String(row?.itemdescription || '').trim().length > 0
          const v = Number(params?.value ?? 0)
          return hasRef && v > 0 ? v : ''
        },
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-header-center'
      },
      {
        field: 'u_ser_qty', headerName: 'Service Qty', width: 105,
        editable: (params: any) => isServiceLine(requisitionType, params.data || {}), hide: !hasServiceLines,
        cellStyle: { textAlign: 'center' }, headerClass: 'ag-header-center'
      },
      {
        field: 'u_ser_unit_price', headerName: 'Service Unit Price', width: 140,
        editable: (params: any) => isServiceLine(requisitionType, params.data || {}), hide: !hasServiceLines,
        cellStyle: { textAlign: 'center' }, headerClass: 'ag-header-center'
      },
      {
        field: 'linetotal',
        headerName: 'Line Total',
        width: 100,
        editable: false,
        valueGetter: (params: any) => {
          const row = params.data
          return prLineValues(requisitionType, row).amount
        },
        valueFormatter: (params: any) => {
          const v = Number(params.value || 0)
          return v > 0 ? v.toFixed(2) : ''
        },
        cellStyle: (params: any) => ({
          textAlign: 'center',
          fontWeight: 600,
          color: params.value > 0 ? '#1976d2' : 'inherit'
        }),
        headerClass: 'ag-header-center'
      },
      {
        field: 'vendorcode',
        headerName: 'Vendor',
        width: columnWidths.vendorcode ?? 110,
        editable: true,
        hide: columnVisibilityModel.vendorcode === false,
        cellStyle: { textAlign: 'center', cursor: 'pointer' },
        headerClass: 'ag-header-center',
        suppressMovable: true
      },
      {
        field: 'warehouse',
        headerName: 'Warehouse',
        width: columnWidths.warehouse ?? 90,
        editable: true,
        hide: columnVisibilityModel.warehouse === false,
        cellStyle: { textAlign: 'center', cursor: 'pointer' },
        headerClass: 'ag-header-center',
        suppressMovable: true
      },
      {
        field: 'deliverydate',
        headerName: 'Delivery Date',
        width: columnWidths.deliverydate ?? 105,
        editable: true,
        hide: columnVisibilityModel.deliverydate === false,
        valueFormatter: (params: any) => toDDMMYYYY(params.value),
        cellStyle: (params: any) => {
          const val = String(params.value || '')
          const isAuto = !!requiredDate && val === requiredDate
          return { textAlign: 'center', fontStyle: isAuto ? 'italic' : 'normal', color: isAuto ? '#666' : 'inherit' }
        },
        headerClass: 'ag-header-center',
        suppressMovable: true
      },
      {
        field: 'projectcode',
        headerName: 'Project',
        width: columnWidths.projectcode ?? 100,
        editable: true,
        hide: columnVisibilityModel.projectcode === false,
        cellStyle: { textAlign: 'center', cursor: 'pointer' },
        headerClass: 'ag-header-center',
        suppressMovable: true
      },
      {
        field: 'dim1',
        headerName: 'Division',
        width: columnWidths.dim1 ?? 85,
        editable: true,
        hide: columnVisibilityModel.dim1 === false,
        cellStyle: { textAlign: 'center', cursor: 'pointer' },
        headerClass: 'ag-header-center',
        suppressMovable: true
      },
      {
        field: 'dim2',
        headerName: 'Department',
        width: columnWidths.dim2 ?? 90,
        editable: true,
        hide: columnVisibilityModel.dim2 === false,
        cellStyle: { textAlign: 'center', cursor: 'pointer' },
        headerClass: 'ag-header-center',
        suppressMovable: true
      },
      {
        field: 'dim3',
        headerName: 'WBS',
        width: columnWidths.dim3 ?? 90,
        editable: true,
        hide: columnVisibilityModel.dim3 === false,
        cellStyle: { textAlign: 'center', cursor: 'pointer' },
        headerClass: 'ag-header-center',
        suppressMovable: true
      },
      {
        field: 'dim4',
        headerName: 'Auxiliary',
        width: columnWidths.dim4 ?? 80,
        editable: true,
        hide: columnVisibilityModel.dim4 === false,
        cellStyle: { textAlign: 'center', cursor: 'pointer' },
        headerClass: 'ag-header-center',
        suppressMovable: true
      },
      {
        field: 'dim5',
        headerName: 'LOC',
        width: columnWidths.dim5 ?? 75,
        editable: true,
        hide: columnVisibilityModel.dim5 === false,
        cellStyle: { textAlign: 'center', cursor: 'pointer' },
        headerClass: 'ag-header-center',
        suppressMovable: true
      }
    ]
    setAgGridColumns(columns)
  }, [columnWidths, columnVisibilityModel, requiredDate, requisitionType, hasServiceLines])



  const autoFitColumnWidths = React.useCallback(() => {
    const estimate = (text: string) => Math.min(RESIZE_MAX, Math.max(RESIZE_MIN, Math.round((text?.length || 0) * 9) + 32))
    const fields = ['itemcode','itemdescription','u_item_details','quantity','uomcode','unitprice','vendorcode','warehouse','deliverydate','projectcode','dim1','dim2','dim3','dim4','dim5']
    const headers: Record<string, string> = {
      itemcode: 'Item Code',
      itemdescription: 'Item Description',
      u_item_details: 'Line Remarks',
      quantity: 'Required Qty.',
      uomcode: 'UoM Code',
      unitprice: 'Unit Price',
      vendorcode: 'Vendor',
      warehouse: 'Warehouse',
      deliverydate: 'Delivery Date',
      projectcode: 'Project',
      dim1: 'Division',
      dim2: 'Department',
      dim3: 'Cost Code (WBS)',
      dim4: 'Auxiliary',
      dim5: 'LOC',
    }
    const next: Record<string, number> = {}
    for (const f of fields) {
      let max = estimate(String(headers[f] || f))
      for (const r of rows.slice(0, 200)) {
        const val = r[f as keyof typeof r]
        const str = typeof val === 'number' ? String(val) : String(val || '')
        max = Math.max(max, estimate(str))
      }
      next[f] = max
    }
    try {
      const rootEl = contentsRef.current as HTMLElement | null
      const containerW = (rootEl?.clientWidth ?? window.innerWidth) - 24
      const visibleFields = fields.filter((f) => (columnVisibilityModel as any)[f] !== false)
      const total = visibleFields.reduce((acc, f) => acc + (next[f] ?? 0), 0)
      if (containerW > 0 && total > containerW) {
        const ratio = containerW / total
        for (const f of visibleFields) {
          next[f] = Math.max(RESIZE_MIN, Math.round((next[f] ?? 120) * ratio))
        }
      }
    } catch {}
    let same = false
    try { same = JSON.stringify(next) === JSON.stringify(columnWidths) } catch { same = false }
    if (!same) {
      setColumnWidths(next)
      saveWidthsDebounced(next)
    }
  }, [rows, columnWidths, columnVisibilityModel, saveWidthsDebounced])

  const autoFitHeaderWidths = React.useCallback(() => {
    const estimate = (text: string) => Math.min(360, Math.max(120, Math.round((text?.length || 0) * 9) + 32))
    const typeText = requisitionType === 'Inventory' ? 'Material' : requisitionType === 'Service' ? 'Service' : 'Asset'
    const allocationTypeText = allocationType === 'Project' ? 'Project' : 'Cost Center'
    const values: Record<string, string> = {
      requester: requesterName || 'Requester Name',
      type: typeText || 'Type',
      status: prStatus || 'Status',
      requireddate: requiredDate || 'Required Date',
      allocationtype: allocationTypeText || 'Allocation Type',
      allocationcode: allocationCode || 'Allocation Code',
    }
    const next: Record<string, number> = {}
    for (const k of Object.keys(values)) {
      next[k] = estimate(values[k])
    }
    let same = false
    try { same = JSON.stringify(next) === JSON.stringify(headerWidths) } catch { same = false }
    if (!same) {
      setHeaderWidths(next)
      try { localStorage.setItem('pr_header_widths', JSON.stringify(next)) } catch {}
    }
  }, [requisitionType, requesterName, prStatus, requiredDate, allocationType, allocationCode, headerWidths])

  

  useEffect(() => {
    const loadSearch = async () => {
      setItemSearchLoading(true)
      try {
        const fields = ['ItemCode','ItemName','PurchaseUnit','ItemsGroupCode','ForeignName']
        const filter = itemSearchQuery ? `contains(ItemName,'${itemSearchQuery}') or contains(ItemCode,'${itemSearchQuery}')` : undefined
        const res = await apiService.get<any>('/sap/items/custom', { fields: fields.join(','), filter, top: 100 })
        const data = res?.data || res || []
        setItemSearchResults(data)
      } catch {
        setItemSearchResults([])
      } finally {
        setItemSearchLoading(false)
      }
    }
    if (itemSearchOpen) loadSearch()
  }, [itemSearchQuery, itemSearchOpen])

  const autoFitColumnWidthsRef = useRef(autoFitColumnWidths)
  const autoFitHeaderWidthsRef = useRef(autoFitHeaderWidths)
  useEffect(() => { autoFitColumnWidthsRef.current = autoFitColumnWidths }, [autoFitColumnWidths])
  useEffect(() => { autoFitHeaderWidthsRef.current = autoFitHeaderWidths }, [autoFitHeaderWidths])
  useEffect(() => {
    if (autoResizeColumns) {
      autoFitColumnWidthsRef.current()
      autoFitHeaderWidthsRef.current()
    }
  }, [autoResizeColumns])

  // Auto-fit columns on initial load to show full header text
  useEffect(() => {
    // Run once on mount to auto-fit grid column widths to header text
    const timer = setTimeout(() => {
      // Auto-fit grid columns based on header text
      const estimate = (text: string) => Math.min(250, Math.max(80, Math.round((text?.length || 0) * 8) + 32))
      const gridColumns: Record<string, string> = {
        itemcode: 'Item Code',
        itemdescription: 'Item Description',
        u_item_details: 'Line Remarks',
        quantity: 'Req. Qty',
        uomcode: 'UoM',
        unitprice: 'Unit Price',
        linetotal: 'Line Total',
        vendorcode: 'Vendor',
        warehouse: 'Warehouse',
        deliverydate: 'Delivery Date',
        projectcode: 'Project',
        dim1: 'Division',
        dim2: 'Department',
        dim3: 'WBS',
        dim4: 'Auxiliary',
        dim5: 'LOC'
      }

      // Calculate auto-fit widths for all columns
      const autoFittedWidths: Record<string, number> = {}
      for (const [field, header] of Object.entries(gridColumns)) {
        autoFittedWidths[field] = estimate(header)
      }

      // Merge with existing widths, prioritizing auto-fit for better visibility
      const mergedWidths = { ...autoFittedWidths }

      setColumnWidths(mergedWidths)
      try {
        localStorage.setItem('pr_grid_widths', JSON.stringify(mergedWidths))
        console.log('Auto-fitted column widths to header text on initial load')
      } catch (e) {
        console.log('Failed to save column widths:', e)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const h = () => { if (autoResizeColumns) { autoFitColumnWidthsRef.current(); autoFitHeaderWidthsRef.current(); } }
    window.addEventListener('resize', h)
    return () => { window.removeEventListener('resize', h) }
  }, [autoResizeColumns])

  const chooseItemFromSearch = () => {
    if (itemSearchSelectedIndex === null) { setItemSearchOpen(false); return }
    const it = itemSearchResults[itemSearchSelectedIndex]
    const code = String(it?.ItemCode || '')
    const name = String(it?.ItemName || '')
    const uom = String(((it as any)?.PurchaseUnit ?? it?.InventoryUOM) || '')
    const itemGroupCode = String((it as any)?.ItemsGroupCode || '')
    if (applyToRowId !== null) {
      const existing = lines[applyToRowId!]
      const existingDate = String(existing?.deliverydate || '').trim()
      // Use existing date if valid, otherwise use header required date
      const finalDeliveryDate = (existingDate && /^\d{4}-\d{2}-\d{2}$/.test(existingDate)) ? existingDate : requiredDate
      console.log('DEBUG Item Picker: existingDate=', existingDate, 'requiredDate=', requiredDate, 'finalDeliveryDate=', finalDeliveryDate)
      updateLine(applyToRowId, { itemcode: code || undefined, itemdescription: name, uomcode: uom, deliverydate: finalDeliveryDate })

      // The item's own Auxiliary (SAP U_AUX_Code) onto the line
      if (code) void fillItemAuxiliaries([{ rowIdx: applyToRowId, itemCode: code }])

      if ((requisitionType === 'Inventory' || requisitionType === 'Service' || requisitionType === 'FixedAsset') && itemGroupCode) {
        setTimeout(async () => {
            try {
              const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined
              const gRes = await apiService.getItemGroups({ companyId: selectedCompanyId, code: itemGroupCode })
              const arr = (gRes as any)?.data || gRes || []
              const first = arr[0]
              const uCost = String((first?.Data?.U_MCSR_CODE ?? first?.U_MCSR_CODE ?? '') || '').trim()
              if (uCost) {
                try {
                  const drRes = await apiService.get<any>('/master-data/distribution-rules', { dimension: 3, companyid: selectedCompanyId })
                  const drList = (drRes as any)?.data || drRes || []
                  const exists = drList.some((d: any) => String(d.FactorCode || d.Code || d.DistributionRule || '').trim() === uCost.trim())
                  if (exists) updateLine(applyToRowId!, { dim3: uCost })
                  else setWarnInfo(`Cost Code mapping for Item Group ${itemGroupCode} not found. Please select WBS manually.`)
                } catch {}
              }
            } catch {}
        }, 0)
      }
    } else {
      console.log('DEBUG Item Picker (new line): Adding new line with requiredDate=', requiredDate)
      setLines((ls) => ([...ls, {
        ...newLineAllocation,
        itemtype: requisitionType,
        itemcode: code || undefined,
        itemdescription: name,
        quantity: 1,
        uomcode: uom,
        unitprice: 1,
        accountcode: '',
        vendorcode: '',
        deliverydate: requiredDate,
      }]))

      // The item's own Auxiliary (SAP U_AUX_Code) onto the new line
      if (code) void fillItemAuxiliaries([{ rowIdx: lines.length, itemCode: code }])

      if ((requisitionType === 'Inventory' || requisitionType === 'Service' || requisitionType === 'FixedAsset') && itemGroupCode) {
        setTimeout(async () => {
            try {
              const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined
              const gRes = await apiService.getItemGroups({ companyId: selectedCompanyId, code: itemGroupCode })
              const arr = (gRes as any)?.data || gRes || []
              const first = arr[0]
              const uCost = String((first?.Data?.U_MCSR_CODE ?? first?.U_MCSR_CODE ?? '') || '').trim()
              if (uCost) {
                try {
                  const drRes = await apiService.get<any>('/master-data/distribution-rules', { dimension: 3, companyid: selectedCompanyId })
                  const drList = (drRes as any)?.data || drRes || []
                  const exists = drList.some((d: any) => String(d.FactorCode || d.Code || d.DistributionRule || '').trim() === uCost.trim())
                  if (exists) setLines((ls) => ls.map((l, idx) => (idx === ls.length - 1 ? { ...l, dim3: uCost } : l)))
                  else setWarnInfo(`Cost Code mapping for Item Group ${itemGroupCode} not found. Please select WBS manually.`)
                } catch {}
              }
            } catch {}
        }, 0)
      }
    }
    setItemSearchOpen(false)
  }

  const handlePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
    // Prevent paste if in view mode
    if (viewMode !== 'create') {
      return
    }

    // Don't process paste if user is editing a cell
    const target = e.target as HTMLElement
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      return
    }

    const text = e.clipboardData.getData('text')
    if (!text) return

    const linesText = text.split(/\r?\n/).map((r) => r.trim()).filter(Boolean)
    if (!linesText.length) return

    e.preventDefault()
    e.stopPropagation()

    // Show loading state
    dispatch(showSnackbar({ message: 'Validating paste data...', severity: 'info' }))

    try {
      // Fetch master data for validation
      const [itemsRes, projectsRes, vendorsRes] = await Promise.all([
        apiService.listLocalItemMaster(undefined, undefined, requisitionType),
        apiService.listProjects(),
        apiService.getVendors()
      ])

      const items = (itemsRes as any)?.data || itemsRes || []
      const projects = (projectsRes as any)?.data || projectsRes || []
      const vendors = (vendorsRes as any)?.data || vendorsRes || []

      // Build validation sets
      const validItemCodes = new Set(
        items.map((it: any) => String(it.data?.ItemCode || it.ItemCode || '').toUpperCase())
      )
      const validProjectCodes = new Set(
        projects.map((p: any) => String(p.data?.Code || p.Code || p.ProjectCode || '').toUpperCase())
      )
      const validVendorCodes = new Set(
        vendors.map((v: any) => String(v.data?.CardCode || v.CardCode || '').toUpperCase())
      )

      // Validate and parse each row
      const newLines: Line[] = []
      let firstInvalidRow = -1
      let invalidReason = ''

      for (let rowIdx = 0; rowIdx < linesText.length; rowIdx++) {
        const row = linesText[rowIdx]

        // Split by tabs (Excel format)
        if (!row.includes('\t')) {
          firstInvalidRow = rowIdx + 1
          invalidReason = 'Row must be tab-separated (copied from Excel with columns)'
          break
        }

        const cells = row.split('\t').map((s) => String(s || '').trim())

        // Expected minimal column order:
        // 0: Item Code, 1: Description, 2: Qty, 3: UoM, 4: Unit Price
        // Full grid copy may also include 5: (empty/Linetotal), 6: Vendor, 7: Warehouse, 8: Delivery Date,
        // 9: Project Code, 10: Dim1, 11: Dim2, 12: Dim3, 13: Dim4, 14: Dim5, 15: Remarks

        if (cells.length < 5) {
          firstInvalidRow = rowIdx + 1
          invalidReason = `Row must have at least 5 columns (Item Code, Description, Qty, UoM, Price). Found: ${cells.length}`
          break
        }

        const itemcode = (cells[0] || '').trim()
        const itemdescription = (cells[1] || '').trim()

        // Validate: For Material/Asset, Item Code must exist in system
        if ((requisitionType === 'Inventory' || requisitionType === 'FixedAsset') && itemcode) {
          if (!validItemCodes.has(itemcode.toUpperCase())) {
            firstInvalidRow = rowIdx + 1
            invalidReason = `Invalid Item Code "${itemcode}" - not found in system`
            break
          }
        }

        // Validate: Must have either item code or description
        if (!itemcode && !itemdescription) {
          firstInvalidRow = rowIdx + 1
          invalidReason = 'Item Code or Description is required'
          break
        }

        // Validate: Quantity must be a valid number
        const quantity = Number(cells[2]) || 0
        if (quantity <= 0) {
          firstInvalidRow = rowIdx + 1
          invalidReason = `Quantity must be greater than 0. Found: ${cells[2]}`
          break
        }

        // Validate: Unit price must be a valid number
        const unitprice = Number(cells[4]) || 0
        if (unitprice < 0) {
          firstInvalidRow = rowIdx + 1
          invalidReason = `Unit Price cannot be negative. Found: ${cells[4]}`
          break
        }

        // Validate: Vendor code if provided
        const vendorcode = (cells[6] || '').trim()
        if (vendorcode && !validVendorCodes.has(vendorcode.toUpperCase())) {
          firstInvalidRow = rowIdx + 1
          invalidReason = `Invalid Vendor Code "${vendorcode}" - not found in system`
          break
        }

        // Validate: Project code if provided
        const projectcode = (cells[9] || '').trim()
        if (projectcode && !validProjectCodes.has(projectcode.toUpperCase())) {
          firstInvalidRow = rowIdx + 1
          invalidReason = `Invalid Project Code "${projectcode}" - not found in system`
          break
        }

        // Validate: Delivery date format if provided
        const deliverydate = (cells[8] || '').trim()
        if (deliverydate) {
          const dateRegex = /^(\d{2})-(\d{2})-(\d{4})$|^(\d{4})-(\d{2})-(\d{2})$/
          if (!dateRegex.test(deliverydate)) {
            firstInvalidRow = rowIdx + 1
            invalidReason = `Delivery Date must be in DD-MM-YYYY or YYYY-MM-DD format. Found: ${deliverydate}`
            break
          }
        }

        // Map columns (accounting for the empty Line Total column at index 5)
        const uomcode = cells[3] || ''
        const warehouse = cells[7] || ''
        const dim1 = cells[10] || ''
        const dim2 = cells[11] || ''
        const dim3 = cells[12] || ''
        const dim4 = cells[13] || ''
        const dim5 = cells[14] || ''
        const u_item_details = cells[15] || ''

        newLines.push({
          itemtype: requisitionType,
          itemcode,
          itemdescription,
          quantity,
          uomcode,
          unitprice,
          accountcode: '',
          vendorcode,
          warehouse,
          deliverydate: deliverydate || requiredDate || '',
          projectcode,
          dim1,
          dim2,
          dim3,
          dim4,
          dim5,
          u_item_details
        })
      }

      // If validation failed, show error and stop
      if (firstInvalidRow > 0) {
        dispatch(showSnackbar({
          message: `Paste stopped at row ${firstInvalidRow}: ${invalidReason}`,
          severity: 'error'
        }))
        return
      }

      // All rows validated successfully - proceed with paste
      setLines((ls) => {
        const existingEmptyRows = ls.filter(l => !l.itemcode && !l.itemdescription)

        if (existingEmptyRows.length > 0 && newLines.length > 0) {
          const updated = [...ls]
          let replaceIndex = 0
          for (let i = 0; i < updated.length && replaceIndex < newLines.length; i++) {
            if (!updated[i].itemcode && !updated[i].itemdescription) {
              updated[i] = { ...updated[i], ...newLines[replaceIndex] }
              replaceIndex++
            }
          }
          dispatch(showSnackbar({ message: `Pasted ${replaceIndex} row(s) - replaced existing empty rows`, severity: 'success' }))
          return updated
        } else {
          const updatedLines = [...ls, ...newLines]
          dispatch(showSnackbar({ message: `Pasted ${newLines.length} row(s) from Excel`, severity: 'success' }))
          return updatedLines
        }
      })
    } catch (error: any) {
      console.error('Error during paste validation:', error)
      dispatch(showSnackbar({
        message: `Paste validation failed: ${error.message || 'Unknown error'}`,
        severity: 'error'
      }))
    }
  }

  const submit = async () => {
    setSubmitting(true)
    setSubmitError('')

    const eff = lines.filter(l => !!l.itemcode || !!l.itemdescription)
    let headerAllocationType = allocationType
    let headerAllocationCode = allocationCode
    if (!headerAllocationCode) {
      const projLine = lines.find(l => !!l.projectcode)
      const ccLine = lines.find(l => !!l.dim2)
      if (projLine) { headerAllocationType = 'Project'; headerAllocationCode = String(projLine.projectcode || '') }
      else if (ccLine) { headerAllocationType = 'CostCenter'; headerAllocationCode = String(ccLine.dim2 || '') }
    }
    if (!String(headerAllocationCode || '').trim().length) { openCenterPopup('Validation Error', 'Allocation Code is required.', 'error'); setSubmitting(false); return }
    if (!requiredDate) { openCenterPopup('Validation Error', 'Required Date is mandatory.', 'error'); setSubmitting(false); return }
    if (!isDocumentDate(fixTwoDigitYear(requiredDate))) { openCenterPopup('Validation Error', `Required Date ${toDDMMYYYY(requiredDate)} is not a valid date: enter the year with 4 digits.`, 'error'); setSubmitting(false); return }
    // Get today's date in local timezone without conversion
    const now = new Date()
    const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    if (fixTwoDigitYear(requiredDate) < todayISO) { openCenterPopup('Validation Error', 'Required Date cannot be in the past.', 'error'); setSubmitting(false); return }
    if (eff.length === 0) { openCenterPopup('Validation Error', 'Add at least one line.', 'error'); setSubmitting(false); return }
    const badDateLine = eff.find(l => !!l.deliverydate && !isDocumentDate(fixTwoDigitYear(l.deliverydate)))
    if (badDateLine) { openCenterPopup('Validation Error', `Line ${lines.indexOf(badDateLine) + 1}: delivery date ${toDDMMYYYY(badDateLine.deliverydate)} is not a valid date: enter the year with 4 digits.`, 'error'); setSubmitting(false); return }
    if (companyRules.requireLineProject && headerAllocationType === 'CostCenter' && eff.some(l => !String(l.projectcode || '').trim())) { openCenterPopup('Validation Error', 'This company requires a Project code on every line of a cost center PR.', 'error'); setSubmitting(false); return }
    if (companyRules.serviceUnitPriceMustBeOne && eff.some(l => isServiceLine(requisitionType, l) && Number(l.unitprice) !== 1)) { openCenterPopup('Validation Error', 'This company requires unit price 1 on Service lines; enter the amount as the quantity.', 'error'); setSubmitting(false); return }
    if (!eff.every(l => Number(l.quantity) > 0 && Number(l.unitprice) >= 0)) { openCenterPopup('Validation Error', 'Each line must have quantity > 0 and unit price >= 0.', 'error'); setSubmitting(false); return }
    const missingDesc = eff.some(l => !String(l.itemcode || '').trim().length && !String(l.itemdescription || '').trim().length)
    if (missingDesc) { openCenterPopup('Validation Error', 'When no SAP Item Code, provide Description.', 'error'); setSubmitting(false); return }
    let previewFlowName: string | null = null
    if (!createdPrId) {
      try {
        const preview = await apiService.get<any>('/pr/approval-route', {
          companyid: Number(localStorage.getItem('selectedCompanyId')),
          requisitiontype: requisitionType, allocationtype: headerAllocationType,
          allocationcode: headerAllocationCode, facilitytype: facilityType, cycle
        })
        previewFlowName = preview?.data?.flowName ?? null
      } catch (error: any) {
        openCenterPopup('Approval Route', String(error?.response?.data?.message || error?.message || 'Unable to resolve approval flow.'), 'error')
        setSubmitting(false)
        return
      }
    }
    const payload = {
      requireddate: requiredDate,
      requisitiontype: requisitionType,
      allocationtype: headerAllocationType,
      allocationcode: headerAllocationCode,
      facilitytype: facilityType,
      cycle: cycle,
      approvalflowname: previewFlowName,
      remarks,
      lines: lines.filter(l => !!l.itemcode || !!l.itemdescription).map(l => ({ itemcode: l.itemcode || null, itemdescription: l.itemdescription || '', u_item_details: l.u_item_details || '', quantity: l.quantity, uomcode: l.uomcode || undefined, unitprice: l.unitprice, accountcode: l.accountcode, vendorcode: l.vendorcode || undefined, warehouse: l.warehouse || undefined, projectcode: l.projectcode || undefined, dim1: l.dim1 || undefined, dim2: l.dim2 || undefined, dim3: l.dim3 || undefined, dim4: l.dim4 || undefined, dim5: l.dim5 || undefined, deliverydate: l.deliverydate ? l.deliverydate : undefined, u_ser_qty: l.u_ser_qty ?? null, u_ser_unit_price: l.u_ser_unit_price ?? null, itemtype: l.itemtype }))
    }
    try {
      if (createdPrId && prStatus === 'Draft') {
        await apiService.updatePRDraft(createdPrId, payload)
        const resSubmit = await apiService.submitPR(createdPrId)
        if ((resSubmit as any)?.success) {
          setPrStatus('Submitted')
          const prId = createdPrId
          try {
            const atRes = await apiService.getPRAttachments(prId)
            const data = (atRes as any)?.data || atRes || []
            setAttachments(data)
          } catch {}
          try { await apiService.getWorkflowStatus(prId) } catch {}
          const detailsParts: string[] = [`Type: ${requisitionType}`, `Allocation: ${headerAllocationType} (${headerAllocationCode})`, `Needed By: ${requiredDate}`]
          if (String(remarks || '').trim().length > 0) detailsParts.push(`Remarks: ${remarks}`)
          openCenterPopup(
            'PR Submitted',
            'Your purchase requisition has been submitted for approval.',
            'success',
            detailsParts,
            (prId ? `/pr/${prId}` : '')
          )
          setResultInfo('')
          return
        } else {
          const msg = String((resSubmit as any)?.message || 'Failed to submit PR')
          openCenterPopup('Submission Failed', msg, 'error')
          setSubmitting(false)
          return
        }
      }
      const res = await apiService.createPR(payload)
      if ((res as any)?.success) {
        const prId = (res as any)?.data?.prid ?? null
        const prNum = String(((res as any)?.data?.prnumber) || '')
        const prDt = String(((res as any)?.data?.prdate) || '')
        if (prId) {
          setCreatedPrId(prId)
          setPrNumber(prNum)
          setPrDate(prDt)
          setPrStatus('Submitted')
          
          try {
            const atRes = await apiService.getPRAttachments(prId)
            const data = (atRes as any)?.data || atRes || []
            setAttachments(data)
          } catch {}
          try {
            await apiService.getWorkflowStatus(prId)
          } catch {}
          if (selectedFiles.length > 0) {
            setUploading(true)
            setUploadProgress(0)
            try {
              for (let i = 0; i < selectedFiles.length; i++) {
                const f = selectedFiles[i]
                // upload sequentially to ensure ordering
                await apiService.uploadSingleAttachment(prId, f, (e: any) => {
                  if (e && e.total) {
                    const pct = Math.round((e.loaded / e.total) * 100)
                    setRowProgress((arr) => arr.map((v, idx) => (idx === i ? pct : v)))
                  }
                })
              }
              const atRes2 = await apiService.getPRAttachments(prId)
              const data2 = (atRes2 as any)?.data || atRes2 || []
              setAttachments(data2)
              setSelectedFiles([])
              setRowProgress([])
            } catch {
            } finally {
              setUploading(false)
              setUploadProgress(0)
            }
          }
        }
        const prNumber = String(((res as any)?.data?.prnumber) || '')
        const detailsParts: string[] = [`Type: ${requisitionType}`, `Allocation: ${headerAllocationType} (${headerAllocationCode})`, `Needed By: ${requiredDate}`]
        if (String(remarks || '').trim().length > 0) detailsParts.push(`Remarks: ${remarks}`)
        openCenterPopup(
          prNumber ? `PR ${prNumber} Created` : 'Purchase Requisition Created',
          'Your purchase requisition has been created successfully.',
          'success',
          detailsParts,
          (prId ? `/pr/${prId}` : '')
        )
        setResultInfo('')
        setCreatedPrId(null)
        setPrNumber('')
        setPrDate('')
        setPrStatus('Draft')
        setLines([])
        setRemarks('')
      } else {
        const msg = String((res as any)?.message || 'Failed to create PR')
        openCenterPopup('Submission Failed', msg, 'error')
        
      }
    } catch (err: any) {
      const msg = String(err?.response?.data?.message || err?.message || 'Request failed')
      const budget = err?.response?.data?.budgetStatus
      if (Array.isArray(budget)) {
        const over = budget.filter((b: any) => b?.isExceeded)
        if (over.length) {
          const summary = over.map((b: any) => `${b.accountcode || b.accountCode}: requested ${b.requested}, available ${b.available}`).join('; ')
          openCenterPopup('Budget Validation Failed', `Budget exceeded: ${summary}`, 'error')
          
          return
        }
      }
      openCenterPopup('Submission Failed', msg, 'error')
      const hint = /535\s+5\.7\.139/i.test(msg) ? 'Email notification failed due to invalid SMTP credentials. PR creation may have completed. Please check Email Settings and credentials.' : ''
      setResultInfo(hint ? 'PR submitted, but email failed.' : '')
    }
    finally {
      setSubmitting(false)
    }
  }

  const saveDraft = async () => {
    setSubmitting(true)
    setSubmitError('')
    let headerAllocationType = allocationType
    let headerAllocationCode = allocationCode
    if (!headerAllocationCode) {
      const projLine = lines.find(l => !!l.projectcode)
      const ccLine = lines.find(l => !!l.dim2)
      if (projLine) { headerAllocationType = 'Project'; headerAllocationCode = String(projLine.projectcode || '') }
      else if (ccLine) { headerAllocationType = 'CostCenter'; headerAllocationCode = String(ccLine.dim2 || '') }
    }
    const eff = lines.filter(l => !!l.itemcode || !!l.itemdescription)
    const payload = {
      requireddate: requiredDate || null,
      requisitiontype: requisitionType,
      allocationtype: headerAllocationType,
      allocationcode: headerAllocationCode || null,
      facilitytype: facilityType,
      cycle: cycle,
      remarks,
      lines: eff.map(l => ({ itemcode: l.itemcode || null, itemdescription: l.itemdescription || '', u_item_details: l.u_item_details || '', quantity: l.quantity || 0, uomcode: l.uomcode || undefined, unitprice: l.unitprice || 0, accountcode: l.accountcode, vendorcode: l.vendorcode || undefined, warehouse: l.warehouse || undefined, projectcode: l.projectcode || undefined, dim1: l.dim1 || undefined, dim2: l.dim2 || undefined, dim3: l.dim3 || undefined, dim4: l.dim4 || undefined, dim5: l.dim5 || undefined, deliverydate: l.deliverydate ? l.deliverydate : undefined, u_ser_qty: l.u_ser_qty ?? null, u_ser_unit_price: l.u_ser_unit_price ?? null, itemtype: l.itemtype }) )
    }
    try {
      if (createdPrId && prStatus === 'Draft') {
        const res = await apiService.updatePRDraft(createdPrId, payload)
        if ((res as any)?.success) {
          // Save as Draft sent its own body: the next autosave sends whatever it has.
          autosaveBaseline.current = null
          const prId = createdPrId
          const detailsParts: string[] = [`Type: ${requisitionType}`, `Allocation: ${headerAllocationType}${headerAllocationCode ? ` (${headerAllocationCode})` : ''}`]
          openCenterPopup('Draft Updated', 'Your draft has been updated.', 'success', detailsParts, (prId ? `/pr/${prId}` : ''))
          setResultInfo('')
          setLastSavedAt(new Date())
        } else {
          const msg = String((res as any)?.message || 'Failed to update draft')
          openCenterPopup('Draft Save Failed', msg, 'error')
        }
      } else {
        const res = await apiService.createPRDraft(payload)
        if ((res as any)?.success) {
          const prId = (res as any)?.data?.prid ?? null
          const prNumber = String(((res as any)?.data?.prnumber) || '')
          if (prId) {
            setCreatedPrId(prId)
            setPrStatus('Draft')
            setLastSavedAt(new Date())
            if (selectedFiles.length > 0) {
              setUploading(true)
              setUploadProgress(0)
              try {
                for (let i = 0; i < selectedFiles.length; i++) {
                  const f = selectedFiles[i]
                  await apiService.uploadSingleAttachment(prId, f, (e: any) => {
                    if (e && e.total) {
                      const pct = Math.round((e.loaded / e.total) * 100)
                      setRowProgress((arr) => arr.map((v, idx) => (idx === i ? pct : v)))
                    }
                  })
                }
                const atRes2 = await apiService.getPRAttachments(prId)
                const data2 = (atRes2 as any)?.data || atRes2 || []
                setAttachments(data2)
                setSelectedFiles([])
                setRowProgress([])
              } catch {} finally {
                setUploading(false)
                setUploadProgress(0)
              }
            }
          }
          const detailsParts: string[] = [`Type: ${requisitionType}`, `Allocation: ${headerAllocationType}${headerAllocationCode ? ` (${headerAllocationCode})` : ''}`]
          openCenterPopup(
            prNumber ? `PR ${prNumber} Saved as Draft` : 'Draft Saved',
            'Your draft has been saved.',
            'success',
            detailsParts,
            (prId ? `/pr/${prId}` : '')
          )
          setResultInfo('')
        } else {
          const msg = String((res as any)?.message || 'Failed to save draft')
          openCenterPopup('Draft Save Failed', msg, 'error')
        }
      }
    } catch (err: any) {
      const msg = String(err?.response?.data?.message || err?.message || 'Request failed')
      openCenterPopup('Draft Save Failed', msg, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDiscardDraft = async () => {
    if (!createdPrId) {
      setDiscardDialogOpen(false)
      return
    }

    setDiscarding(true)
    try {
      const res = await apiService.discardPRDraft(createdPrId)
      if ((res as any)?.success) {
        pendingAutosave.current = null // the draft is gone: nothing of it to save
        dispatch(showSnackbar({ message: 'Draft discarded successfully', severity: 'success' }))
        setCreatedPrId(null)
        setPrNumber('')
        setPrDate('')
        setPrStatus('Draft')
        setLines([])
        setRemarks('')
        setRequiredDate('')
        setResultInfo('')
        navigate('/dashboard')
      } else {
        const msg = String((res as any)?.message || 'Failed to discard draft')
        dispatch(showSnackbar({ message: msg, severity: 'error' }))
      }
    } catch (err: any) {
      const msg = String(err?.response?.data?.message || err?.message || 'Failed to discard draft')
      dispatch(showSnackbar({ message: msg, severity: 'error' }))
    } finally {
      setDiscarding(false)
      setDiscardDialogOpen(false)
    }
  }

  useEffect(() => {
    // Only the user's own editable draft is autosaved: not a read-only PR, not the Find filters.
    const editable = !!createdPrId && prStatus === 'Draft' && viewModeRef.current === 'create'
    // A change still waiting from before: kept for the same draft, else sent now or dropped (waitingAutosaveAction).
    const waiting = pendingAutosave.current
    if (waiting) {
      const action = waitingAutosaveAction(waiting.prId, { prId: createdPrId, status: prStatus, editable })
      if (action !== 'keep') pendingAutosave.current = null
      if (action === 'send') void sendAutosave(waiting)
    }
    if (!editable || !createdPrId) return
    // The draft as it is now, sent 1.5 s after the last change; nothing when it is what the server has.
    const payload = draftAutosavePayload({ requiredDate, requisitionType, allocationType, allocationCode, facilityType, cycle, remarks }, lines)
    const body = JSON.stringify(payload)
    const baseline = autosaveBaseline.current
    if (baseline && baseline.prId === createdPrId && baseline.body === body) {
      pendingAutosave.current = null
      return
    }
    const pending: WaitingAutosave = { prId: createdPrId, payload, body }
    pendingAutosave.current = pending
    const timer = setTimeout(() => {
      if (pendingAutosave.current !== pending) return
      pendingAutosave.current = null
      void sendAutosave(pending)
    }, 1500)
    return () => { try { clearTimeout(timer) } catch {} }
  }, [createdPrId, prStatus, requiredDate, requisitionType, allocationType, allocationCode, facilityType, cycle, remarks, lines, sendAutosave])

  const onSelectFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : []
    setSelectedFiles(files)
    setRowProgress(files.map(() => 0))
  }

  const onDropFiles = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const files = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : []
    if (files.length) {
      setSelectedFiles((prev) => [...prev, ...files])
      setRowProgress((prev) => [...prev, ...files.map(() => 0)])
    }
  }
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const uploadFiles = async () => {
    if (!createdPrId || selectedFiles.length === 0) return
    setUploading(true)
    setUploadProgress(0)
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const f = selectedFiles[i]
        await apiService.uploadSingleAttachment(createdPrId, f, (e: any) => {
          if (e && e.total) {
            const pct = Math.round((e.loaded / e.total) * 100)
            setRowProgress((arr) => arr.map((v, idx) => (idx === i ? pct : v)))
          }
        })
      }
      setSelectedFiles([])
      setRowProgress([])
      setUploadProgress(100)
      const atRes = await apiService.getPRAttachments(createdPrId)
      const data = (atRes as any)?.data || atRes || []
      setAttachments(data)
    } catch {
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const refreshAttachments = async () => {
    if (!createdPrId) return
    try {
      const atRes = await apiService.getPRAttachments(createdPrId)
      const data = (atRes as any)?.data || atRes || []
      setAttachments(data)
    } catch {}
  }

  const deleteAttachment = async (attachmentId: number) => {
    try {
      await apiService.deleteAttachment(attachmentId)
      await refreshAttachments()
    } catch {}
  }

  return (
    <Paper
      component="section"
      aria-labelledby="create-pr-heading"
      elevation={0}
      sx={{ p: { xs: 1, sm: 1.5, md: 2 }, bgcolor: '#f7f7f7', border: 0, borderRadius: 0, height: 'calc(100vh - 64px)', maxHeight: 'calc(100vh - 64px)', overflow: 'auto' }}
    >
      {/* Page header: what this PR is, moving between your PRs, finding and copying one */}
      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, bgcolor: '#ffffff' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
          <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
            <Typography
              component="h1"
              id="create-pr-heading"
              sx={{ m: 0, fontSize: { xs: 18, sm: 22 }, fontWeight: 600, lineHeight: 1.25, color: EDITOR_COLORS.text }}
            >
              {prHeading}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.75, fontSize: 12, color: EDITOR_COLORS.muted }}>
              <StatusPill status={viewMode === 'find' ? 'Find mode' : (prStatus || 'Draft')} />
              {prNumber ? <span>{prNumber}</span> : null}
              {prDate ? <span>{toDDMMYYYY(prDate)}</span> : null}
              <span>Requested by {requesterName || '—'}</span>
              {saveStatusText ? (
                <Box component="span" sx={{ color: lastSavedAt ? EDITOR_COLORS.green : EDITOR_COLORS.muted, fontWeight: lastSavedAt ? 600 : 400 }}>
                  {saveStatusText}
                </Box>
              ) : null}
              {currentIndex !== null && activeList[currentIndex] ? (
                <GoldenArrow prId={Number(activeList[currentIndex].prid)} title="Open PR" />
              ) : null}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <Box role="group" aria-label="Move between your PRs" sx={{ display: 'inline-flex', border: `1px solid ${EDITOR_COLORS.border}`, borderRadius: '3px', bgcolor: '#ffffff' }}>
              <IconButton size="small" onClick={goFirst} disabled={!activeList.length} aria-label="First PR" title="First PR"><FirstPageIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={goPrev} disabled={!canNavigate} aria-label="Previous PR" title="Previous PR"><ChevronLeftIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={goNext} disabled={!canNavigate} aria-label="Next PR" title="Next PR"><ChevronRightIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={goLast} disabled={!activeList.length} aria-label="Last PR" title="Last PR"><LastPageIcon fontSize="small" /></IconButton>
            </Box>
            <Button variant="outlined" startIcon={<SearchIcon />} onClick={() => setGlobalPickerOpen(true)} sx={secondaryButtonSx}>Find a PR</Button>
            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => setCopyDialogOpen(true)} sx={secondaryButtonSx}>Copy PR</Button>
            {viewMode === 'find' && (
              <>
                <Button variant="outlined" onClick={() => { setViewMode('read'); setFilteredPrList([]); setCurrentIndex(null) }} sx={secondaryButtonSx}>Exit</Button>
                <Button variant="contained" onClick={executeFind}>Find</Button>
              </>
            )}
            <IconButton onClick={() => navigate('/dashboard')} aria-label="Close" title="Close"><CloseIcon /></IconButton>
          </Box>
        </Box>
      </Paper>
      <MuiMenu
        id="grid-settings-menu"
        anchorEl={gridMenuAnchor}
        open={gridMenuOpen}
        onClose={() => setGridMenuAnchor(null)}
        TransitionComponent={Fade}
      >
        <MuiMenuItem onClick={() => { setGridMenuAnchor(null); setColumnsOpen(true); }} aria-label="Manage Columns">
          <ListItemText>Columns</ListItemText>
        </MuiMenuItem>
        <MuiMenuItem onClick={() => { setGridMenuAnchor(null); setFiltersOpen(true); }} aria-label="Filters">
          <ListItemText>Filters</ListItemText>
        </MuiMenuItem>
        <MuiMenuItem onClick={() => { setGridMenuAnchor(null); setDensityOpen(true); }} aria-label="Density">
          <ListItemText>Density</ListItemText>
        </MuiMenuItem>
        <MuiMenuItem onClick={() => {
          try {
            const fields = baseColumns.filter((c) => c.field !== 'index').map((c) => String(c.headerName || c.field))
            const dataRows = rows.map((r: any) => baseColumns.filter((c) => c.field !== 'index').map((c) => {
              const v = (r as any)[String(c.field)]
              const s = typeof v === 'number' ? String(v) : String(v ?? '')
              const escaped = s.replace(/"/g, '""')
              return `"${escaped}"`
            }).join(',')).join('\n')
            const csv = `${fields.map(h => `"${String(h).replace(/"/g, '""')}"`).join(',')}\n${dataRows}`
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'PR_Export.csv'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
          } catch {}
          setGridMenuAnchor(null)
        }} aria-label="Export to Excel">
          <ListItemIcon><InsertDriveFileIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Export to Excel (CSV)</ListItemText>
        </MuiMenuItem>
      </MuiMenu>

      {columnsOpen && (
        <Popover
          open={columnsOpen}
          onClose={() => setColumnsOpen(false)}
          anchorEl={gridMenuAnchor}
          TransitionComponent={Fade}
        >
          <Box sx={{ p: 1.5, minWidth: 240 }}>
            {['itemcode','itemdescription','u_item_details','quantity','uomcode','unitprice','deliverydate','projectcode','dim1','dim2','dim3','dim4','dim5','vendorcode','warehouse'].map((f) => (
              <FormControlLabel
                key={f}
                control={<Checkbox size="small" checked={(columnVisibilityModel as any)[f] !== false} onChange={(e) => {
                  const checked = e.target.checked
                  setColumnVisibilityModel((prev) => ({ ...(prev as any), [f]: checked }))
                  try { localStorage.setItem('pr_grid_view', JSON.stringify({ ...(columnVisibilityModel as any), [f]: checked })) } catch {}
                }} />}
                label={String(f)}
              />
            ))}
          </Box>
        </Popover>
      )}

      {densityOpen && (
        <Popover
          open={densityOpen}
          onClose={() => setDensityOpen(false)}
          anchorEl={gridMenuAnchor}
          TransitionComponent={Fade}
        >
          <Box sx={{ p: 1.5 }}>
            <RadioGroup row value={gridDensity} onChange={(e) => setGridDensity(e.target.value as any)} aria-label="Grid Density">
              <FormControlLabel value="compact" control={<Radio size="small" />} label="Compact" />
              <FormControlLabel value="standard" control={<Radio size="small" />} label="Standard" />
              <FormControlLabel value="comfortable" control={<Radio size="small" />} label="Comfortable" />
            </RadioGroup>
          </Box>
        </Popover>
      )}

      {filtersOpen && (
        <Popover
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          anchorEl={gridMenuAnchor}
          TransitionComponent={Fade}
        >
          <Box sx={{ p: 1.5, minWidth: 260 }}>
            <TextField
              size="small"
              fullWidth
              label="Quick Filter"
              value={quickFilterText}
              inputProps={{ 'aria-label': 'Quick Filter' }}
              onChange={(e) => {
                const v = e.target.value
                setQuickFilterText(v)
                try { gridRef.current?.api.setQuickFilter(v) } catch {}
              }}
            />
          </Box>
        </Popover>
      )}

      <EditorLayout
        main={
          <>
            {tab === 'contents' && headerReady && (!!submitError || !!warnInfo || !!resultInfo || (viewMode === 'create' && (lineRuleIssues.unitPriceNotOne.length > 0 || lineRuleIssues.missingProject.length > 0))) ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {!!submitError && <Alert severity="error">{submitError}</Alert>}
                {!!warnInfo && <Alert severity="warning">{warnInfo}</Alert>}
                {!!resultInfo && <Alert severity="success">{resultInfo}</Alert>}
                {viewMode === 'create' && lineRuleIssues.unitPriceNotOne.length > 0 && (
                  <Alert severity="warning" action={<Button color="inherit" size="small" onClick={setServiceUnitPricesToOne}>Set unit price to 1</Button>}>
                    This company requires unit price 1 on Service lines; enter the amount as the quantity. Line{lineRuleIssues.unitPriceNotOne.length > 1 ? 's' : ''} {lineRuleIssues.unitPriceNotOne.join(', ')} {lineRuleIssues.unitPriceNotOne.length > 1 ? 'have' : 'has'} a different price.
                  </Alert>
                )}
                {viewMode === 'create' && lineRuleIssues.missingProject.length > 0 && (
                  <Alert severity="warning">
                    This company requires a Project on every line of a cost center PR. Line{lineRuleIssues.missingProject.length > 1 ? 's' : ''} {lineRuleIssues.missingProject.join(', ')} {lineRuleIssues.missingProject.length > 1 ? 'have' : 'has'} none.
                  </Alert>
                )}
              </Box>
            ) : null}

            <EditorSection id="pr-sec-details" number={1} title="Request details">
              <Box component="fieldset" disabled={viewMode === 'read'} sx={{ border: 0, m: 0, p: 0, minWidth: 0, mb: 2 }}>
                <Box component="legend" sx={{ p: 0, mb: 1, fontSize: 12, fontWeight: 600, color: EDITOR_COLORS.text }}>What are you requesting?</Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 1.5 }}>
                  {/* Here and in the lists below, the PR's own value shows even when the user's approval flows
                      do not offer it: an opened PR shows what it was saved with. */}
                  {PR_TYPE_CHOICES.filter((choice) => canShowRequisitionType(choice.value) || choice.value === requisitionType).map((choice) => {
                    const selected = requisitionType === choice.value
                    return (
                      <Box
                        component="label"
                        key={choice.value}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.25,
                          p: selected ? '9px 11px' : '10px 12px',
                          border: selected ? `2px solid ${EDITOR_COLORS.blue}` : '1px solid #89919a',
                          borderRadius: '6px',
                          bgcolor: selected ? '#f2f8fe' : '#ffffff',
                          cursor: viewMode === 'read' ? 'default' : 'pointer',
                          '&:focus-within': { outline: `2px solid ${EDITOR_COLORS.blue}`, outlineOffset: '2px' },
                        }}
                      >
                        <input
                          type="radio"
                          name="pr-requisition-type"
                          value={choice.value}
                          checked={selected}
                          onChange={() => setRequisitionType(choice.value)}
                          disabled={viewMode === 'read'}
                          style={{ width: 16, height: 16, margin: '2px 0 0', accentColor: EDITOR_COLORS.blue }}
                        />
                        <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Box component="span" sx={{ fontSize: 14, fontWeight: 600, color: EDITOR_COLORS.text }}>{choice.label}</Box>
                          <Box component="span" sx={{ fontSize: 12, color: EDITOR_COLORS.muted }}>{choice.hint}</Box>
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
                <EditorField label="Charge to" htmlFor="pr-allocation-type">
                  <select id="pr-allocation-type" value={allocationType} onChange={(e) => { setAllocationType(e.target.value as any); setAllocationCode(''); setSelectedFlowId('') }} disabled={viewMode==='read'} style={editorControlStyle}>
                    {(!availableFlowOptions || availableFlowOptions.allocationType.includes('Project') || allocationType === 'Project') && <option value="Project">Project</option>}
                    {(!availableFlowOptions || availableFlowOptions.allocationType.includes('CostCenter') || allocationType === 'CostCenter') && <option value="CostCenter">Cost center</option>}
                  </select>
                </EditorField>
                <EditorField label={allocationType === 'Project' ? 'Project' : 'Cost center'} htmlFor="pr-allocation-code">
                  <Box sx={{ position: 'relative' }}>
                    <input
                      id="pr-allocation-code"
                      value={allocationCode}
                      onChange={(e) => { setAllocationCode(e.target.value); setSelectedFlowId('') }}
                      disabled={viewMode==='read'}
                      placeholder={allocationType === 'Project' ? 'Choose a project' : 'Choose a cost center'}
                      onClick={() => { if (viewMode === 'create') { setApplyToRowId(null); setInlinePickerType(allocationType === 'Project' ? 'project' : 'dim2'); setInlineQuery(''); setInlineSelection([]); inlineOpenedOnceRef.current = false; setInlinePickerOpen(true); } }}
                      style={{ ...editorControlStyle, paddingRight: 32, cursor: viewMode === 'create' ? 'pointer' : 'default' }}
                    />
                    {viewMode === 'create' && (
                      <SearchIcon aria-hidden="true" sx={{ position: 'absolute', right: 9, top: 9, fontSize: 18, color: EDITOR_COLORS.muted, pointerEvents: 'none' }} />
                    )}
                  </Box>
                </EditorField>
                <EditorField label="Required by" htmlFor="pr-required-date">
                  <input id="pr-required-date" type="date" value={requiredDate} onChange={(e) => setRequiredDate(e.target.value)} onBlur={(e) => setRequiredDate(fixTwoDigitYear(e.target.value))} disabled={viewMode==='read'} style={editorControlStyle} />
                </EditorField>
                <EditorField label="Facility" htmlFor="pr-facility">
                  <select id="pr-facility" value={facilityType} onChange={(e) => setFacilityType(e.target.value)} disabled={viewMode==='read'} style={editorControlStyle}>
                    {(!availableFlowOptions || availableFlowOptions.facilityType.length === 0 || availableFlowOptions.facilityType.includes('NA') || facilityType === 'NA') && <option value="NA">NA</option>}
                    {(!availableFlowOptions || availableFlowOptions.facilityType.length === 0 || availableFlowOptions.facilityType.includes('Commercial') || facilityType === 'Commercial') && <option value="Commercial">Commercial</option>}
                    {(!availableFlowOptions || availableFlowOptions.facilityType.length === 0 || availableFlowOptions.facilityType.includes('Residential') || facilityType === 'Residential') && <option value="Residential">Residential</option>}
                  </select>
                </EditorField>
                <EditorField label="Cycle" htmlFor="pr-cycle">
                  <select id="pr-cycle" value={cycle} onChange={(e) => setCycle(e.target.value)} disabled={viewMode==='read'} style={editorControlStyle}>
                    {(!availableFlowOptions || availableFlowOptions.cycle.length === 0 || availableFlowOptions.cycle.includes('Project') || cycle === 'Project') && <option value="Project">Project</option>}
                    {(!availableFlowOptions || availableFlowOptions.cycle.length === 0 || availableFlowOptions.cycle.includes('Department') || cycle === 'Department') && <option value="Department">Department</option>}
                    {(!availableFlowOptions || availableFlowOptions.cycle.length === 0 || availableFlowOptions.cycle.includes('HR') || cycle === 'HR') && <option value="HR">HR</option>}
                    {(!availableFlowOptions || availableFlowOptions.cycle.length === 0 || availableFlowOptions.cycle.includes('Admin') || cycle === 'Admin') && <option value="Admin">Admin</option>}
                  </select>
                </EditorField>
              </Box>

              {allocationType === 'Project' && !!allocationCode && headerDefaultChips.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2, px: 1.5, py: 1.25, borderRadius: '6px', bgcolor: EDITOR_COLORS.subtle, fontSize: 13, color: EDITOR_COLORS.text }}>
                  <InfoOutlinedIcon aria-hidden="true" sx={{ fontSize: 18, color: EDITOR_COLORS.blueText }} />
                  <span>Every line gets this project&apos;s</span>
                  {headerDefaultChips.map((chip) => (
                    <Box component="span" key={chip} sx={chipSx}>{chip}</Box>
                  ))}
                  <Box component="span" sx={{ fontSize: 12, color: EDITOR_COLORS.muted }}>Change a line in the grid when it differs.</Box>
                </Box>
              )}

              {viewMode === 'create' && allAvailableFlows.length > 0 && allocationType && allocationCode && (
                <Box sx={{ mb: 2, p: 1.5, borderRadius: '6px', border: `1px solid ${EDITOR_COLORS.border}`, bgcolor: '#fbfcfc' }}>
                  <Box component="label" htmlFor="pr-flow-select" sx={{ display: 'block', mb: 0.75, fontSize: 12, fontWeight: 600, color: EDITOR_COLORS.text }}>
                    Fill in from an approval flow <Box component="span" sx={{ fontWeight: 400, color: EDITOR_COLORS.muted }}>(optional)</Box>
                  </Box>
                  <select
                    id="pr-flow-select"
                    value={selectedFlowId}
                    onChange={(e) => handleFlowSelection(e.target.value)}
                    style={{ ...editorControlStyle, maxWidth: 640 }}
                  >
                    <option value="">-- Select a Flow to Auto-Fill --</option>
                    {sortApprovalFlows(allAvailableFlows
                      .filter(flow => {
                        const typeMatch = String(flow.allocationType || '').toUpperCase() === String(allocationType || '').toUpperCase()
                        const codeMatch = allocationCodeMatchesFlow(flow.allocationCode, allocationCode)
                        return typeMatch && codeMatch
                      }))
                      .map((flow) => {
                        const originalIdx = allAvailableFlows.indexOf(flow)
                        const label = `${flow.requisitionType || '?'} | ${flow.facilityType || 'Any'} | ${flow.cycle || 'Any'} | ${flow.flowName || 'Flow'}`
                        return <option key={originalIdx} value={`${originalIdx}`}>{label}</option>
                      })}
                  </select>
                  <Box sx={{ mt: 0.5, fontSize: 12, color: EDITOR_COLORS.muted }}>
                    {allAvailableFlows.filter(flow => {
                      const typeMatch = String(flow.allocationType || '').toUpperCase() === String(allocationType || '').toUpperCase()
                      const codeMatch = allocationCodeMatchesFlow(flow.allocationCode, allocationCode)
                      return typeMatch && codeMatch
                    }).length} flows available for this {allocationType === 'Project' ? 'project' : 'cost center'}. Choosing one fills in the type, facility and cycle.
                  </Box>
                </Box>
              )}

              <EditorField label="Purpose" htmlFor="pr-purpose" hint="Initiator remarks, visible to all approvers">
                <TextField
                  id="pr-purpose"
                  placeholder="What is this for? Approvers read it first."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  disabled={viewMode !== 'create'}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </EditorField>
            </EditorSection>

            {tab === 'contents' && headerReady && (
              <Box ref={contentsRef} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <EditorSection
                  id="pr-sec-items"
                  number={2}
                  title="Items"
                  note={`· ${filledLineCount} filled of ${rows.length} rows`}
                  disableBodyPadding
                  actions={
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                      <Button size="small" variant="outlined" startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />} onClick={addLine} disabled={viewMode !== 'create'} sx={addButtonSx}>Add Line</Button>
                      <Button size="small" variant="outlined" startIcon={<SearchIcon sx={{ fontSize: 16 }} />} onClick={() => { setApplyToRowId(null); setItemSearchOpen(true); setItemSearchSelectedIndex(null); setItemSearchQuery(''); }} disabled={viewMode !== 'create'} sx={secondaryButtonSx}>Item Search</Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setImportOpen(true)}
                        disabled={viewMode !== 'create'}
                        sx={secondaryButtonSx}
                        title="Add lines from an Excel file or from cells copied in Excel (template available)"
                      >
                        Import from Excel
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<TableChartIcon sx={{ fontSize: 16 }} />}
                        onClick={handleCopyTable}
                        sx={secondaryButtonSx}
                        title="Copy all rows with headers to clipboard (tab-separated)"
                      >
                        Copy Table
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                        onClick={() => {
                          if (window.confirm('Are you sure you want to clear all cell content? Rows will be kept.')) {
                            setLines((prevLines: any[]) => {
                              return prevLines.map((line: any) => ({
                                ...line,
                                itemcode: '',
                                itemdescription: '',
                                u_item_details: '',
                                quantity: 0,
                                uomcode: '',
                                unitprice: 0,
                                vendorcode: '',
                                warehouse: '',
                                deliverydate: '',
                                projectcode: '',
                                dim1: '',
                                dim2: '',
                                dim3: '',
                                dim4: '',
                                dim5: ''
                              }))
                            })
                            dispatch(showSnackbar({ message: 'All cell content cleared', severity: 'success' }))
                          }
                        }}
                        disabled={viewMode !== 'create'}
                        sx={{ color: EDITOR_COLORS.red, borderColor: '#d9a3a3', bgcolor: '#ffffff', '&:hover': { borderColor: EDITOR_COLORS.red, bgcolor: '#fff5f5' } }}
                        title="Clear all cell content (keep rows)"
                      >
                        Clear All
                      </Button>
                      <IconButton size="small" onClick={(e) => setGridMenuAnchor(e.currentTarget)} aria-label="Grid Settings" title="Grid settings">
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  {/* Grid - fixed height with its own scrollbar; paste and right-click work on this box */}
                  <Box
                    onPaste={handlePaste}
                    tabIndex={0}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      const target = e.target as HTMLElement;
                      const rowEl = target.closest('[data-id]') as HTMLElement | null;
                      const idAttr = rowEl?.getAttribute('data-id') || null;
                      const rowId = idAttr ? Number(idAttr) : null;
                      setContextMenu({ mouseX: e.clientY, mouseY: e.clientX, rowId });
                    }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      outline: 'none',
                      '&:focus-within': {
                        outline: '2px solid #1976d2',
                        outlineOffset: '-2px'
                      }
                    }}
                  >
            <div className="ag-theme-alpine" style={{ height: 600, width: '100%', borderTop: '1px solid #e5e9eb', overflow: 'hidden' }}>
              <AgGridReact
                ref={gridRef}
                rowData={rows as any[]}
                columnDefs={agGridColumns}
                domLayout="normal"
                rowHeight={26}
                headerHeight={28}
                defaultColDef={{
                  editable: viewMode === 'create',
                  resizable: true,
                  sortable: true,
                  filter: false,
                  flex: 0,
                  minWidth: 50
                }}
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 20, 50, 100]}
                rowSelection={{
                  mode: 'multiRow',
                  checkboxes: true,
                  headerCheckbox: true,
                  enableClickSelection: true
                }}
                theme="legacy"
                getRowId={(params: any) => String(params.data.id)}
                onCellValueChanged={(params: any) => {
                  if (viewMode === 'create' && params.oldValue !== params.newValue) {
                    const fieldMap: Record<string, string> = {
                      itemcode: 'itemcode',
                      itemdescription: 'itemdescription',
                      u_item_details: 'u_item_details',
                      quantity: 'quantity',
                      uomcode: 'uomcode',
                      unitprice: 'unitprice',
                      u_ser_qty: 'u_ser_qty',
                      u_ser_unit_price: 'u_ser_unit_price',
                      deliverydate: 'deliverydate',
                      projectcode: 'projectcode',
                      dim1: 'dim1',
                      dim2: 'dim2',
                      dim3: 'dim3',
                      dim4: 'dim4',
                      dim5: 'dim5',
                      vendorcode: 'vendorcode',
                      warehouse: 'warehouse'
                    }
                    const field = fieldMap[params.column.getColId()]
                    if (field) {
                      const rowIndex = Number(params.node.id)
                      const serviceField = field === 'u_ser_qty' || field === 'u_ser_unit_price'
                      const raw = params.newValue
                      const numericValue = raw === '' || raw == null ? null : Number(raw)
                      if (serviceField && numericValue != null && (!Number.isFinite(numericValue) || numericValue < 0)) {
                        openCenterPopup('Invalid service value', 'Enter a non-negative number or leave the field blank.', 'error')
                        params.node.setDataValue(field, params.oldValue)
                        return
                      }
                      const line = { [field]: serviceField ? numericValue : raw }
                      if (field === 'projectcode') void applyLineProject(rowIndex, String(raw || ''))
                      else updateLine(rowIndex, line)
                    }
                  }
                }}
                onCellFocused={(params: any) => {
                  if (params.rowIndex !== null) {
                    const colId = String(params.column?.getColId?.() || '')
                    console.log('Cell focused:', params.rowIndex, colId)
                    setFocusedCellPosition({ rowIndex: params.rowIndex, colId })
                  }
                }}
                onCellClicked={(params: any) => {
                  console.log('Cell clicked:', params.rowIndex, params.colId)
                  if (params.rowIndex !== null) {
                    const colId = String(params.column?.getColId?.() || params.colId || '')
                    setFocusedCellPosition({ rowIndex: params.rowIndex, colId })
                    lastClickedCellRef.current = params.rowIndex
                  }

                  if (viewMode !== 'create') return
                  const colId = params.column.getColId()
                  const pickerFields: Record<string, 'warehouse' | 'dim1' | 'dim2' | 'dim3' | 'dim4' | 'dim5' | 'item' | 'vendor' | 'project'> = {
                    'itemcode': 'item',
                    'projectcode': 'project',
                    'dim1': 'dim1',
                    'dim2': 'dim2',
                    'dim3': 'dim3',
                    'dim4': 'dim4',
                    'dim5': 'dim5',
                    'vendorcode': 'vendor',
                    'warehouse': 'warehouse'
                  }
                  if (pickerFields[colId] && params.event) {
                    setApplyToRowId(Number(params.node.id))
                    setInlinePickerType(pickerFields[colId])
                    setInlineQuery('')
                    setInlineSelection([])
                    inlineOpenedOnceRef.current = false
                    setInlinePickerOpen(true)
                  }
                }}
                onCellContextMenu={(params: any) => {
                  params.event.preventDefault()
                  setContextMenu({ mouseX: params.event.clientY, mouseY: params.event.clientX, rowId: Number(params.node.id) })
                }}
                onComponentStateChanged={(params: any) => {
                  if (params.name === 'rowData' && gridRef.current?.api) {
                    // Grid is ready, add keyboard listener
                    const gridElement = gridRef.current?.api.getGridCore()?.eGridDiv
                    if (gridElement && !gridElement.hasAttribute('data-paste-listener')) {
                      gridElement.setAttribute('data-paste-listener', 'true')
                      gridElement.addEventListener('keydown', (e: KeyboardEvent) => {
                        // Check for Ctrl+V
                        if ((e.ctrlKey || e.metaKey) && e.key === 'v' && viewMode === 'create') {
                          e.preventDefault()
                          handlePasteTable()
                        }
                        // Check for Ctrl+C
                        if ((e.ctrlKey || e.metaKey) && e.key === 'c' && !e.shiftKey) {
                          e.preventDefault()
                          handleCopyTable()
                        }
                      })
                    }
                  }
                }}
                getRowStyle={(params: any) => ({
                  backgroundColor: (params.node.rowIndex ?? 0) % 2 === 0 ? '#FFFFFF' : '#F8F8F8'
                })}
              />
            </div>
            <MuiMenu
              open={!!contextMenu}
              onClose={() => setContextMenu(null)}
              anchorReference="anchorPosition"
              anchorPosition={contextMenu ? { top: contextMenu.mouseX, left: contextMenu.mouseY } : undefined}
            >
              <MuiMenuItem onClick={() => { const id = contextMenu?.rowId; if (id !== null && id !== undefined) { handleCopyRow(Number(id)); } setContextMenu(null); }} disabled={viewMode !== 'create'}>
                <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Copy Row</ListItemText>
              </MuiMenuItem>
              <MuiMenuItem onClick={() => { const id = contextMenu?.rowId; if (id !== null && id !== undefined) { handleCutRow(Number(id)); } setContextMenu(null); }} disabled={viewMode !== 'create'}>
                <ListItemIcon><ContentCutIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Cut Row</ListItemText>
              </MuiMenuItem>
              <MuiMenuItem onClick={() => { const id = contextMenu?.rowId; if (id !== null && id !== undefined) { handlePasteRow(Number(id)); } setContextMenu(null); }} disabled={!clipboardRow || viewMode !== 'create'}>
                <ListItemIcon><ContentPasteIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Paste Row</ListItemText>
              </MuiMenuItem>
              <MuiMenuItem onClick={() => { addLine(); setContextMenu(null); }} disabled={viewMode !== 'create'}>
                <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Add Row</ListItemText>
              </MuiMenuItem>
              <MuiMenuItem onClick={() => { const id = contextMenu?.rowId; if (id !== null && id !== undefined) { removeLine(Number(id)); } setContextMenu(null); }} disabled={viewMode !== 'create'}>
                <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Remove Row</ListItemText>
              </MuiMenuItem>
              <MuiMenuItem onClick={() => { handleRefreshPage(); setContextMenu(null); }}>
                <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Refresh</ListItemText>
              </MuiMenuItem>
            </MuiMenu>
                  </Box>
                  {/* End of grid */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '8px 24px', px: 2, py: 1.25, borderTop: `1px solid ${EDITOR_COLORS.divider}`, bgcolor: '#f9fafb' }}>
                    <Typography sx={{ fontSize: 12, color: EDITOR_COLORS.muted }}>
                      Click an item, project, vendor, warehouse or dimension cell to choose from the list.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 24px' }}>
                      <Typography component="span" sx={{ fontSize: 13 }}>
                        <Box component="span" sx={{ color: EDITOR_COLORS.muted }}>Total quantity </Box>
                        <strong>{totalQuantity.toFixed(2)}</strong>
                      </Typography>
                      <Typography component="span" sx={{ fontSize: 13 }}>
                        <Box component="span" sx={{ color: EDITOR_COLORS.muted }}>Estimated total </Box>
                        <Box component="strong" sx={{ fontSize: 16 }}>{totalAmount.toFixed(2)}</Box>
                      </Typography>
                    </Box>
                  </Box>
                </EditorSection>

          <Dialog
            open={inlinePickerOpen}
            onClose={() => { inlineOpenedOnceRef.current = false; setInlinePickerOpen(false); }}
            PaperProps={{ sx: { bgcolor: '#ADD8E6', border: '2px solid #b2b2b2', width: 560, maxWidth: '80vw', maxHeight: '70vh', resize: 'both', overflow: 'auto', cursor: 'default' } }}
          >
            <DialogTitle id="inline-picker-title" sx={{ py: 0.5, bgcolor: '#ADD8E6' }}>Choose From List</DialogTitle>
            <DialogContent dividers sx={{ p: 0.75 }}>
              <Stack direction="row" spacing={0.5} mb={0.75}>
                <input style={{ height: 28, flex: 1 }} placeholder="Search" value={inlineQuery} onChange={(e) => setInlineQuery((e.target as HTMLInputElement).value)} onKeyDown={(e) => { if (e.key === 'Enter') { void loadInlineList(inlineQuery) } }} onPaste={(e) => { e.preventDefault(); const t = (e.clipboardData as any).getData('text'); setInlineQuery(t); }} />
                <Button variant="outlined" size="small" onClick={async () => { await loadInlineList(inlineQuery) }}>Search</Button>
              </Stack>
              <Stack direction="row" spacing={1} mb={0.75}>
                
              </Stack>
              <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
                <AgGridReact
                  rowData={inlineRows}
                  columnDefs={inlineColumns}
                  getRowId={(params: any) =>
                    inlinePickerType === 'warehouse' ? String(params.data.WarehouseCode || params.data.WhsCode || params.data.id) :
                    inlinePickerType === 'item' ? String(params.data.ItemCode || params.data.id) :
                    inlinePickerType === 'vendor' ? String(params.data.CardCode || params.data.id) :
                    inlinePickerType === 'project' ? String(params.data.Code || params.data.ProjCode || params.data.Project || params.data.id) :
                    String(params.data.FactorCode || params.data.Code || params.data.DistributionRule || params.data.id)
                  }
                  loading={inlineLoading}
                  rowSelection="multiple"
                  suppressRowClickSelection={false}
                  rowMultiSelectWithClick
                  onSelectionChanged={(params) => {
                    const selectedIds = params.api.getSelectedRows().map((row: any) => {
                      if (inlinePickerType === 'warehouse') return String(row.WarehouseCode || row.WhsCode || row.id)
                      if (inlinePickerType === 'item') return String(row.ItemCode || row.id)
                      if (inlinePickerType === 'vendor') return String(row.CardCode || row.id)
                      if (inlinePickerType === 'project') return String(row.Code || row.ProjCode || row.Project || row.id)
                      return String(row.FactorCode || row.Code || row.DistributionRule || row.id)
                    })
                    const s = JSON.stringify(selectedIds)
                    if (inlineSelectionSigRef.current !== s) {
                      inlineSelectionSigRef.current = s
                      setInlineSelection(selectedIds)
                    }
                  }}
                  onRowClicked={(params: any) => {
                  const first: any = params.data;
                  if (applyToRowId !== null) {
                    if (inlinePickerType === 'warehouse') updateLine(applyToRowId, { warehouse: String(first.WarehouseCode) });
                    else if (inlinePickerType === 'item') {
                      const itemCode = String(first.ItemCode)
                      updateLine(applyToRowId, { itemcode: itemCode, itemdescription: String(first.ItemName), uomcode: String(((first as any).PurchaseUnit ?? first.InventoryUOM) || '') });

                      // The item's own Auxiliary (SAP U_AUX_Code) onto the line
                      if (itemCode) void fillItemAuxiliaries([{ rowIdx: applyToRowId, itemCode }])

                      const igc = String((first as any).ItemsGroupCode || '')
                      if ((requisitionType === 'Inventory' || requisitionType === 'Service' || requisitionType === 'FixedAsset') && igc) {
                        setTimeout(async () => {
                          try {
                            const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined
                            const gRes = await apiService.getItemGroups({ companyId: selectedCompanyId, code: igc })
                            const arr = (gRes as any)?.data || gRes || []
                            const grp = arr[0]
                            const uCost = String((grp?.Data?.U_MCSR_CODE ?? grp?.U_MCSR_CODE ?? '') || '').trim()
                            if (uCost) updateLine(applyToRowId!, { dim3: uCost })
                          } catch {}
                        }, 0)
                      }
                    }
                    else if (inlinePickerType === 'vendor') updateLine(applyToRowId, { vendorcode: String(first.CardCode) });
                    else if (inlinePickerType === 'project') {
                      const projCode = String(first.Code || first.Project || first.ProjCode || '')
                      void applyLineProject(applyToRowId, projCode)
                    }
                    else if (inlinePickerType === 'dim1') updateLine(applyToRowId, { dim1: String(first.FactorCode || first.Code || first.DistributionRule || '') });
                    else if (inlinePickerType === 'dim2') updateLine(applyToRowId, { dim2: String(first.FactorCode || first.Code || first.DistributionRule || '') });
                    else if (inlinePickerType === 'dim3') updateLine(applyToRowId, { dim3: String(first.FactorCode || first.Code || first.DistributionRule || '') });
                    else if (inlinePickerType === 'dim4') updateLine(applyToRowId, { dim4: String(first.FactorCode || first.Code || first.DistributionRule || '') });
                    else if (inlinePickerType === 'dim5') updateLine(applyToRowId, { dim5: String(first.FactorCode || first.Code || first.DistributionRule || '') });
                  } else {
                    if (inlinePickerType === 'project') {
                      const projCode = String(first.Code || first.Project || first.ProjCode || '')
                      setAllocationType('Project'); setAllocationCode(projCode); setSelectedFlowId('');
                      if (projCode) {
                        setTimeout(async () => {
                          try {
                            const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined
                            console.log('Fetching project data for:', projCode, 'companyId:', selectedCompanyId)
                            const r = await apiService.get<any>('/master-data', {
                              companyid: selectedCompanyId,
                              endpointkey: 'sap-projects',
                              keycode: projCode
                            })
                            console.log('Master data response:', r)
                            const obj = ((r.data || [])[0] || {}).jsondata || {}
                            console.log('Parsed project data:', obj)
                            const division = String(obj.U_Division ?? '').trim()
                            const department = String(obj.U_Department ?? '').trim()
                            const scope = String(obj.U_SCOPE ?? '').trim()
                            const loc = String(obj.U_LOC ?? '').trim()
                            console.log('Extracted values - Division:', division, 'Department:', department, 'Scope:', scope, 'LOC:', loc)
                            setLines((ls) => ls.map((ln) => ({
                              ...ln,
                              dim1: ln.dim1 || division || ln.dim1,
                              dim2: ln.dim2 || department || ln.dim2,
                              dim4: ln.dim4 || scope || ln.dim4,
                              dim5: ln.dim5 || loc || ln.dim5,
                            })))
                          } catch (error) {
                            console.error('Error fetching/applying project data to all lines:', error)
                          }
                        }, 0)
                      }
                    }
                    else if (inlinePickerType === 'dim2') { setAllocationType('CostCenter'); setAllocationCode(String(first.FactorCode || first.Code || first.DistributionRule || '')); setSelectedFlowId(''); }
                  }
                  setInlinePickerOpen(false);
                  setInlineQuery('');
                }}
                  pagination={true}
                  paginationPageSize={10}
                  paginationPageSizeSelector={[10, 20, 50]}
                  domLayout="normal"
                  defaultColDef={{
                    resizable: true,
                    sortable: true,
                    flex: 1,
                    minWidth: 100
                  }}
                />
              </div>
            </DialogContent>
            <DialogActions sx={{ px: 1, py: 0.75 }}>
              <Button size="small" onClick={() => { setInlinePickerOpen(false); }}>Cancel</Button>
                <Button size="small" variant="contained" onClick={async () => {
                  const selected = (inlineSelection || []).map((id) => inlineRows.find((r) => r.id === id)).filter(Boolean);
                  if (!selected.length) { setInlinePickerOpen(false); return; }
                  if (applyToRowId !== null) {
                    const first = selected[0] as any;
                    if (inlinePickerType === 'warehouse') {
                      updateLine(applyToRowId, { warehouse: String(first.WarehouseCode) });
                    } else if (inlinePickerType === 'item') {
                      const itemCode = String(first.ItemCode)
                      const existing = lines[applyToRowId!]
                      const existingDate = String(existing?.deliverydate || '').trim()
                      // Use existing date if valid, otherwise use header required date
                      const finalDeliveryDate = (existingDate && /^\d{4}-\d{2}-\d{2}$/.test(existingDate)) ? existingDate : requiredDate
                      console.log('DEBUG Inline Picker: existingDate=', existingDate, 'requiredDate=', requiredDate, 'finalDeliveryDate=', finalDeliveryDate)
                      updateLine(applyToRowId, { itemcode: itemCode, itemdescription: String(first.ItemName), uomcode: String(((first as any).PurchaseUnit ?? first.InventoryUOM) || ''), deliverydate: finalDeliveryDate });

                      // The item's own Auxiliary (SAP U_AUX_Code) onto the line
                      if (itemCode) void fillItemAuxiliaries([{ rowIdx: applyToRowId, itemCode }])

                      const igc = String((first as any).ItemsGroupCode || '')
                      if ((requisitionType === 'Inventory' || requisitionType === 'Service' || requisitionType === 'FixedAsset') && igc) {
                        setTimeout(async () => {
                          try {
                            const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined
                            const gRes = await apiService.getItemGroups({ companyId: selectedCompanyId, code: igc })
                            const arr = (gRes as any)?.data || gRes || []
                            const grp = arr[0]
                            const uCost = String((grp?.Data?.U_MCSR_CODE ?? grp?.U_MCSR_CODE ?? '') || '').trim()
                            if (uCost) updateLine(applyToRowId!, { dim3: uCost })
                          } catch {}
                        }, 0)
                      }
                    } else if (inlinePickerType === 'vendor') {
                      updateLine(applyToRowId, { vendorcode: String(first.CardCode) });
                    } else if (inlinePickerType === 'project') {
                      const projCode = String(first.Code || first.Project || first.ProjCode || '')
                      void applyLineProject(applyToRowId, projCode)
                    } else if (inlinePickerType === 'dim1') {
                      updateLine(applyToRowId, { dim1: String(first.FactorCode || first.Code || first.DistributionRule || '') });
                    } else if (inlinePickerType === 'dim2') {
                      updateLine(applyToRowId, { dim2: String(first.FactorCode || first.Code || first.DistributionRule || '') });
                    } else if (inlinePickerType === 'dim3') {
                      updateLine(applyToRowId, { dim3: String(first.FactorCode || first.Code || first.DistributionRule || '') });
                    } else if (inlinePickerType === 'dim4') {
                      updateLine(applyToRowId, { dim4: String(first.FactorCode || first.Code || first.DistributionRule || '') });
                    } else if (inlinePickerType === 'dim5') {
                      updateLine(applyToRowId, { dim5: String(first.FactorCode || first.Code || first.DistributionRule || '') });
                    }
                  } else {
                    if (inlinePickerType === 'warehouse') {
                      setLines((ls) => ([...ls, ...selected.map((w: any) => ({
                        ...newLineAllocation,
                        itemtype: requisitionType,
                        quantity: 1,
                        unitprice: 1,
                        accountcode: '',
                        vendorcode: '',
                        warehouse: String(w.WarehouseCode),
                        deliverydate: '',
                      }))]));
                    } else if (inlinePickerType === 'item') {
                      const selectedCount = selected.length
                      setLines((ls) => ([...ls, ...selected.map((it: any) => ({
                        ...newLineAllocation,
                        itemtype: requisitionType,
                        itemcode: String(it.ItemCode),
                        itemdescription: String(it.ItemName),
                        quantity: 1,
                        uomcode: String(((it as any).PurchaseUnit ?? it.InventoryUOM) || ''),
                        unitprice: 1,
                        accountcode: '',
                        vendorcode: '',
                        deliverydate: requiredDate,
                      }))]));

                      // Each item's own Auxiliary (SAP U_AUX_Code) onto its new line
                      void fillItemAuxiliaries(selected.map((it: any, itemIdx: number) => ({ rowIdx: lines.length + itemIdx, itemCode: String(it.ItemCode) })))

                      const firstSel = selected[0] as any
                      const igc = String((firstSel as any).ItemsGroupCode || '')
                      if ((requisitionType === 'Inventory' || requisitionType === 'Service' || requisitionType === 'FixedAsset') && igc) {
                        setTimeout(async () => {
                          try {
                            const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined
                            const gRes = await apiService.getItemGroups({ companyId: selectedCompanyId, code: igc })
                            const arr = (gRes as any)?.data || gRes || []
                            const grp = arr[0]
                            const uCost = String((grp?.Data?.U_MCSR_CODE ?? grp?.U_MCSR_CODE ?? '') || '').trim()
                            if (uCost) {
                              try {
                                const drRes = await apiService.get<any>('/master-data/distribution-rules', { dimension: 3, companyid: selectedCompanyId })
                                const drList = (drRes as any)?.data || drRes || []
                                const exists = drList.some((d: any) => String(d.FactorCode || d.Code || d.DistributionRule || '').trim() === uCost.trim())
                                if (exists) setLines((ls) => ls.map((l, idx) => (idx >= ls.length - selectedCount ? { ...l, dim3: uCost } : l)))
                                else setWarnInfo(`Cost Code mapping for Item Group ${igc} not found. Please select WBS manually.`)
                              } catch {}
                            }
                          } catch {}
                        }, 0)
                      }
                    } else if (inlinePickerType === 'vendor') {
                      setLines((ls) => ([...ls, ...selected.map((v: any) => ({
                        ...newLineAllocation,
                        itemtype: requisitionType,
                        quantity: 1,
                        unitprice: 1,
                        accountcode: '',
                        vendorcode: String(v.CardCode),
                        deliverydate: '',
                      }))]));
                    } else if (inlinePickerType === 'project') {
                      const first = selected[0] as any;
                      const projCode = String(first.Code || first.Project || first.ProjCode || '')
                      setAllocationType('Project');
                      setAllocationCode(projCode);
                      setSelectedFlowId('');
                      if (projCode) {
                        setTimeout(async () => {
                          try {
                            const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined
                            console.log('Fetching project data for:', projCode, 'companyId:', selectedCompanyId)
                            const r = await apiService.get<any>('/master-data', {
                              companyid: selectedCompanyId,
                              endpointkey: 'sap-projects',
                              keycode: projCode
                            })
                            console.log('Master data response:', r)
                            const obj = ((r.data || [])[0] || {}).jsondata || {}
                            console.log('Parsed project data:', obj)
                            const division = String(obj.U_Division ?? '').trim()
                            const department = String(obj.U_Department ?? '').trim()
                            const scope = String(obj.U_SCOPE ?? '').trim()
                            const loc = String(obj.U_LOC ?? '').trim()
                            console.log('Extracted values - Division:', division, 'Department:', department, 'Scope:', scope, 'LOC:', loc)
                            setLines((ls) => ls.map((ln) => ({
                              ...ln,
                              dim1: ln.dim1 || division || ln.dim1,
                              dim2: ln.dim2 || department || ln.dim2,
                              dim4: ln.dim4 || scope || ln.dim4,
                              dim5: ln.dim5 || loc || ln.dim5,
                            })))
                          } catch (error) {
                            console.error('Error fetching/applying project data to all lines:', error)
                          }
                        }, 0)
                      }
                    } else if (inlinePickerType === 'dim2') {
                      setLines((ls) => ([...ls, ...selected.map((d: any) => ({
                        ...newLineAllocation,
                        itemtype: requisitionType,
                        quantity: 1,
                        unitprice: 1,
                        accountcode: '',
                        dim2: String(d.FactorCode || d.Code || d.DistributionRule || ''),
                        deliverydate: '',
                      }))]));
                    } else if (inlinePickerType === 'dim3') {
                      setLines((ls) => ([...ls, ...selected.map((d: any) => ({
                        ...newLineAllocation,
                        itemtype: requisitionType,
                        quantity: 1,
                        unitprice: 1,
                        accountcode: '',
                        dim3: String(d.FactorCode || d.Code || d.DistributionRule || ''),
                        deliverydate: '',
                      }))]));
                    } else if (inlinePickerType === 'dim4') {
                      setLines((ls) => ([...ls, ...selected.map((d: any) => ({
                        ...newLineAllocation,
                        itemtype: requisitionType,
                        quantity: 1,
                        unitprice: 1,
                        accountcode: '',
                        dim4: String(d.FactorCode || d.Code || d.DistributionRule || ''),
                        deliverydate: '',
                      }))]));
                    } else if (inlinePickerType === 'dim5') {
                      setLines((ls) => ([...ls, ...selected.map((d: any) => ({
                        ...newLineAllocation,
                        itemtype: requisitionType,
                        quantity: 1,
                        unitprice: 1,
                        accountcode: '',
                        dim5: String(d.FactorCode || d.Code || d.DistributionRule || ''),
                        deliverydate: '',
                      }))]));
                    }
                  }
                  setInlinePickerOpen(false);
                  setInlineQuery('');
                }}>Choose</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={itemSearchOpen} onClose={() => setItemSearchOpen(false)} fullWidth maxWidth="lg">
            <DialogTitle sx={{ py: 1.5 }}>List of Items</DialogTitle>
            <DialogContent dividers sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={1} mb={1}>
                <input style={{ height: 28, width: '100%' }} placeholder="Find" value={itemSearchQuery} onChange={(e) => setItemSearchQuery((e.target as HTMLInputElement).value)} />
              </Stack>
              <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
                <AgGridReact
                  rowData={itemSearchResults.map((it: any) => ({ id: String(it.ItemCode || ''), ItemCode: it.ItemCode, ItemName: it.ItemName, ItemsGroupCode: it.ItemsGroupCode, PurchaseUnit: ((it as any).PurchaseUnit ?? it.InventoryUOM), ForeignName: it.ForeignName || '' }))}
                  columnDefs={[
                    { field: 'ItemCode', headerName: 'Item No.', width: 160 },
                    { field: 'ItemName', headerName: 'Item Description', width: 320 },
                    { field: 'ItemsGroupCode', headerName: 'Item Group', width: 160 },
                    { field: 'PurchaseUnit', headerName: 'UoM', width: 120 },
                    { field: 'ForeignName', headerName: 'Foreign Name', width: 200 },
                  ]}
                  loading={itemSearchLoading}
                  pagination={true}
                  paginationPageSize={10}
                  paginationPageSizeSelector={[10, 20, 50]}
                  domLayout="normal"
                  suppressRowClickSelection
                  onRowDoubleClicked={(params: any) => {
                    const idx = itemSearchResults.findIndex((x: any) => (x.ItemCode || '') === String(params.data?.ItemCode || ''))
                    setItemSearchSelectedIndex(idx >= 0 ? idx : null)
                    chooseItemFromSearch()
                  }}
                  onRowClicked={(params: any) => {
                    const idx = itemSearchResults.findIndex((x: any) => (x.ItemCode || '') === String(params.data?.ItemCode || ''))
                    setItemSearchSelectedIndex(idx >= 0 ? idx : null)
                    chooseItemFromSearch()
                  }}
                  defaultColDef={{
                    resizable: true,
                    sortable: true,
                    flex: 1,
                    minWidth: 100
                  }}
                />
              </div>
            </DialogContent>
            <DialogActions sx={{ px: 1.5, py: 1 }}>
              
              <Button variant="contained" onClick={chooseItemFromSearch} disabled={itemSearchSelectedIndex === null}>Choose</Button>
              <Button onClick={() => setItemSearchOpen(false)}>Cancel</Button>
            </DialogActions>
          </Dialog>
              </Box>
            )}

            <EditorSection
              id="pr-sec-files"
              number={3}
              title="Attachments"
              note={attachments.length + selectedFiles.length > 0 ? `· ${attachments.length + selectedFiles.length}` : undefined}
            >
              <Box ref={attachmentsRef}>
                {!createdPrId && selectedFiles.length > 0 && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>These files upload when you save the draft or submit the PR.</Alert>
                )}
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Button variant="outlined" component="label" sx={secondaryButtonSx}>
              Select Files
              <input type="file" hidden multiple onChange={onSelectFiles} accept="*/*" />
            </Button>
            <Button variant="outlined" disabled={selectedFiles.length === 0 || uploading || !createdPrId} onClick={uploadFiles} sx={addButtonSx}>{uploading ? `Uploading ${uploadProgress}%` : 'Upload'}</Button>
            <Typography variant="body2">{selectedFiles.length > 0 ? `${selectedFiles.length} file(s) selected` : ''}</Typography>
          </Stack>
          <Box sx={{ border: '1px dashed #cfd8dc', p: 2, mb: 2 }} onDrop={onDropFiles} onDragOver={onDragOver}>
            <Typography variant="body2">Drag and drop files here</Typography>
          </Box>
          {selectedFiles.length > 0 && (
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Size (KB)</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedFiles.map((f, idx) => (
                  <TableRow key={`${f.name}-${idx}`}>
                    <TableCell>{f.name}</TableCell>
                    <TableCell>{(f.size / 1024).toFixed(2)}</TableCell>
                    <TableCell>{f.type || ''}</TableCell>
                    <TableCell align="right">
                      {uploading ? (
                        <Box sx={{ minWidth: 120 }}>
                          <Box sx={{ fontSize: 12, mb: 0.5 }}>{rowProgress[idx] ?? 0}%</Box>
                          <Box sx={{ height: 6, backgroundColor: '#e0e0e0' }}>
                            <Box sx={{ height: 6, width: `${rowProgress[idx] ?? 0}%`, backgroundColor: 'primary.main' }} />
                          </Box>
                        </Box>
                      ) : null}
                      <Button size="small" color="error" onClick={() => setSelectedFiles((list) => list.filter((_, i) => i !== idx))}>Remove</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {createdPrId ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Size (KB)</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Uploaded By</TableCell>
                  <TableCell>Uploaded Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attachments.map((a: any) => {
                  const id = a.attachmentid ?? a.AttachmentID
                  const name = a.filename ?? a.FileName
                  const size = a.filesize ?? a.FileSize
                  const type = a.filetype ?? a.FileType
                  const by = a.uploadedbyname ?? a.UploadedByName
                  const date = a.uploadeddate ?? a.UploadedDate
                  return (
                    <TableRow key={id}>
                      <TableCell>
                        <Button size="small" onClick={() => openPreview(Number(id), String(name || ''))}>{name}</Button>
                      </TableCell>
                      <TableCell>{(Number(size) / 1024).toFixed(2)}</TableCell>
                      <TableCell>{type}</TableCell>
                      <TableCell>{by || ''}</TableCell>
                      <TableCell>{date ? toDDMMYYYY(date) : ''}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" onClick={() => openPreview(Number(id), String(name || ''))}>Preview</Button>
                          <Button size="small" onClick={() => downloadAttachment(Number(id), String(name || ''))}>Download</Button>
                          <Button size="small" color="error" onClick={() => deleteAttachment(id)}>Delete</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                })}
                {attachments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>No attachments</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          ) : null}
              </Box>
            </EditorSection>
          </>
        }
        side={
          <>
            <SideCard id="pr-side-summary" title="Summary">
              <Box sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2, color: EDITOR_COLORS.text }}>{totalAmount.toFixed(2)}</Box>
              <Box sx={{ mb: 1, fontSize: 12, color: EDITOR_COLORS.muted }}>Estimated total</Box>
              <SideFact label="Lines">{filledLineCount}</SideFact>
              <SideFact label="Total quantity">{totalQuantity.toFixed(2)}</SideFact>
              <SideFact label="Type">{PR_TYPE_CHOICES.find((choice) => choice.value === requisitionType)?.label || requisitionType}</SideFact>
              <SideFact label={allocationType === 'CostCenter' ? 'Cost center' : 'Project'}>{allocationCode || '—'}</SideFact>
              <Divider sx={{ my: 1.5 }} />
              {viewMode === 'create' ? (
                <SubmitChecklist problems={submitProblems} />
              ) : (
                <Box sx={{ fontSize: 13, color: EDITOR_COLORS.muted }}>
                  {viewMode === 'find' ? 'Set the fields to look for, then press Find.' : 'This PR cannot be changed here.'}
                </Box>
              )}
              <Button fullWidth variant="contained" disabled={!canSubmit || submitting} onClick={submit} sx={{ mt: 2, minHeight: 40, fontSize: 14, fontWeight: 700 }}>
                {submitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Submit PR'}
              </Button>
              <Button fullWidth variant="outlined" disabled={submitting || viewMode !== 'create'} onClick={saveDraft} sx={{ ...secondaryButtonSx, mt: 1, minHeight: 36 }}>
                {submitting ? <CircularProgress size={18} /> : 'Save as Draft'}
              </Button>
              {createdPrId && prStatus === 'Draft' && viewMode === 'create' && (
                <Button
                  fullWidth
                  variant="text"
                  color="error"
                  disabled={submitting || discarding}
                  onClick={() => setDiscardDialogOpen(true)}
                  startIcon={discarding ? <CircularProgress size={18} /> : <DeleteIcon />}
                  sx={{ mt: 1 }}
                >
                  Discard Draft
                </Button>
              )}
            </SideCard>

            <SideCard id="pr-side-route" title="Who approves">
              {approvalFlowError ? (
                <Box role="alert" sx={{ fontSize: 13, fontWeight: 600, color: approvalFlowError.includes('Multiple') ? EDITOR_COLORS.amber : EDITOR_COLORS.red }}>
                  {approvalFlowError}
                </Box>
              ) : matchedApprovalFlow ? (
                <>
                  <Box sx={{ fontSize: 13, fontWeight: 600, color: EDITOR_COLORS.green }}>{matchedApprovalFlow}</Box>
                  {approvalStages.length > 0 && (
                    <Box component="ol" sx={{ listStyle: 'none', m: '12px 0 0', p: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {approvalStages.map((stage, index) => (
                        <Box component="li" key={`${index}-${stageTitle(stage, index)}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                          <Box
                            component="span"
                            aria-hidden="true"
                            sx={{
                              flex: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 22,
                              height: 22,
                              boxSizing: 'border-box',
                              borderRadius: '50%',
                              border: `2px solid ${EDITOR_COLORS.blue}`,
                              bgcolor: index === 0 ? EDITOR_COLORS.blue : '#ffffff',
                              color: index === 0 ? '#ffffff' : EDITOR_COLORS.blueText,
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ fontSize: 13, fontWeight: 600, color: EDITOR_COLORS.text }}>{stageTitle(stage, index)}</Box>
                            {stageWho(stage) ? <Box sx={{ fontSize: 12, color: EDITOR_COLORS.muted }}>{stageWho(stage)}</Box> : null}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ fontSize: 13, color: EDITOR_COLORS.muted }}>
                  Choose the type, the project or cost center, the facility and the cycle to see who approves.
                </Box>
              )}
            </SideCard>
          </>
        }
      />

      <Dialog open={previewOpen} onClose={closePreview} maxWidth="lg" fullWidth>
        <DialogTitle>Attachment Preview</DialogTitle>
        <DialogContent dividers>
          {previewType === 'image' ? (
            <Box sx={{ textAlign: 'center' }}>
              <img src={previewUrl} alt="Attachment" style={{ maxWidth: '100%', maxHeight: '80vh' }} />
            </Box>
          ) : previewType === 'pdf' ? (
            <Box sx={{ height: '80vh' }}>
              <iframe src={previewUrl} title="PDF Preview" style={{ width: '100%', height: '100%', border: 0 }} />
            </Box>
          ) : (
            <Typography variant="body2">Preview is not available for this file type. Use Download to open it.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <IconButton size="small" onClick={goPrevAttachment} disabled={!attachments.length || previewIndex === null || previewIndex <= 0}><ChevronLeftIcon /></IconButton>
          <IconButton size="small" onClick={goNextAttachment} disabled={!attachments.length || previewIndex === null || previewIndex >= attachments.length - 1}><ChevronRightIcon /></IconButton>
          <Button onClick={closePreview}>Close</Button>
          <Button onClick={() => { if (previewUrl) window.open(previewUrl, '_blank', 'noopener'); }}>Open in New Window</Button>
          <Button onClick={() => { if (previewAttachmentId) void downloadAttachment(previewAttachmentId, previewAttachmentName); }}>Download</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>PR Details</DialogTitle>
        <DialogContent dividers>
          {detail ? (
            <Stack spacing={1}>
              <Typography variant="subtitle2">PR No: {(detail as any).prnumber}</Typography>
              <Typography variant="body2">Type: {(detail as any).requisitiontype}</Typography>
              <Typography variant="body2">Allocation: {(detail as any).allocationtype} / {(detail as any).allocationcode}</Typography>
              <Typography variant="body2">Needed By: {toDDMMYYYY((detail as any).requireddate)}</Typography>
              <Typography variant="body2">Status: {(detail as any).status}</Typography>
              <Typography variant="body2">Total: {Number((detail as any).totalamount || 0).toFixed(2)} {(detail as any).currency}</Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={submitting} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <PRGlobalPicker open={globalPickerOpen} onClose={() => setGlobalPickerOpen(false)} context="all" />
      <CopyPRDialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)} onSelectPR={handleCopyPR} />
      <PRLineImportDialog
        open={importOpen}
        requisitionType={requisitionType}
        serviceUnitPriceMustBeOne={companyRules.serviceUnitPriceMustBeOne}
        existingCount={lines.filter(isFilledLine).length}
        onClose={() => setImportOpen(false)}
        onImport={(imported, mode, cachedItems) => { void handleImportLines(imported, mode, cachedItems) }}
      />

      {/* Discard Draft Confirmation Dialog */}
      <Dialog
        open={discardDialogOpen}
        onClose={() => !discarding && setDiscardDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Discard Draft?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            Are you sure you want to discard this draft? This action will permanently delete the draft and all its line items. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDiscardDialogOpen(false)}
            disabled={discarding}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDiscardDraft}
            disabled={discarding}
            variant="contained"
            color="error"
            startIcon={discarding ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <DeleteIcon />}
          >
            {discarding ? 'Discarding...' : 'Discard Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={centerPopup.open}
        onClose={() => {
          setCenterPopup((prev) => ({ ...prev, open: false }))
          if (centerPopup.nextPath) navigate(centerPopup.nextPath)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: centerPopup.severity === 'error' ? 'error.main' : 'success.main' }}>
          {centerPopup.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: centerPopup.details.length ? 1 : 2 }}>
            {centerPopup.message}
          </Typography>
          {centerPopup.details.map((d, i) => (
            <Typography key={`${d}-${i}`} variant="body2" sx={{ mb: 0.5 }}>
              {i + 1}. {d}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setCenterPopup((prev) => ({ ...prev, open: false }))
              if (centerPopup.nextPath) navigate(centerPopup.nextPath)
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CreatePR;
// Version history:
// 2025-12-06: Restored functional "Search PR" button and removed non-functional blue find icon; ensured no residual listeners or styles; verified pagination and selection behaviors remain intact. — Rajeesh Ambadi, 2025-11-12
